import type { GenericObject, OfferType } from '@makemydeal/dr-platform-types';
import { ApplicationCodes, BALLOON, CASH, FINANCE, LEASE, RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import * as types from '../types/actionTypes';
import {
    getBuyRateOverride,
    getBuyRateOverrideByOfferTypeAndTerm,
    getCalculationEndpointByOfferType,
    getFeatureToggle,
    getCurrentFlexBuyPayments,
    getCurrentOfferType,
    getDealerOptedOutOfAutoSelectProgram,
    getDownPayment,
    getEquityAllocationGridCashDown,
    getFlexBuyPaymentGridCashDowns,
    getGridIsUserProgramQuotes,
    hasDealLenderProgram,
    isExplicitLenderProgramGridActive,
    isTemplateRateGridActive,
    getFlexBuyPaymentGridPercents,
    getFlexBuyPaymentGridTerms,
    getFlexBuyPaymentGridToggle,
    getInitialDownPaymentListForGrid,
    getInitialFlexBuyTermMonthList,
    getGridColumnProgramInfo,
    getInitialPaymentDiscount,
    getInitialTermsMonthsListForGrid,
    getIsUserProgramQuotes,
    getMileageChangeResidualKeeper,
    isUpqResidualKeepOnMileageChangeEnabled,
    getNewSelectedCell,
    getPaymentGridBuild,
    isPaymentGridAwaitingVehicleSwitch,
    getPaymentGridCashDown,
    getPaymentGridCellPricedAt,
    getGridRateTypeByTerm,
    getPaymentGridSelectedColumnIndex,
    getPaymentGridDealColumnIndex,
    getPaymentGridTermOwnerColumnIndex,
    getPaymentGridSelectedRowIndex,
    getResolvedPaymentGridSelectedColumnIndex,
    getResolvedPaymentGridSelectedRowIndex,
    getOwnColumnOverride,
    getPaymentGridTermRateType,
    getPaymentGridCellRecord,
    getResolvedColumnSellRate,
    getPaymentGridColumnSources,
    getPaymentGridDecidedColumns,
    getPaymentGridFallbackRates,
    getPaymentGridPendingSellRates,
    getPaymentGridRestoredInputs,
    getPaymentGridPairRejectedTerms,
    getGridColumnPairHalves,
    getPaymentGridOriginalColumnSources,
    getPaymentGridOriginalColumnPrograms,
    getPaymentGridStuckColumnOrigin,
    getPaymentGridRefusedTerms,
    getPaymentGridTerms,
    getPaymentGridToggle,
    getPaymentGridErroredColumns,
    isTermDuplicated,
    getOfferDetails,
    getPaymentGridColumnView,
    getPaymentGridLeftTerms,
    getPaymentGridDealTermBeforeEdit,
    getPaymentGridDealTermOverridesBeforeEdit,
    getResolvedPaymentGridActiveCell,
    isPaymentGridCellLoading,
    isDealParkedOffActiveColumn,
    isPaymentGridCellServedFromStoredQuote,
    getPaymentProgramInfoByTerm,
    getResidualInfoOverrideByOfferTypeTerm,
    getRowsStatuses,
    getSelectedTerm,
    isAspPaymentGridRedesignActive,
    getTerm,
    getTermIdForOfferType,
    getSelectedTermMonths,
    getSellRateOverride,
    getSellRateOverrideByOfferTypeAndTerm,
    getTermByOfferTypeAndMonths,
    getTermId,
    getUniquePaymentGridCashDown,
    getUniquePaymentGridTerms,
    hasUserPricedGridColumn,
    hasGridColumnPricingResidual,
    hasUserSuppliedGridResidual,
    hasUserSuppliedGridSellRate,
    isAspPaymentGridRedesignEnabled,
    isFlexBuyPayment,
    isLeaseResidualMissing,
    shouldGatePaymentGridForQuote
} from '../selectors';

import {
    adoptPaymentGridCell,
    initializePaymentGridCashdown,
    initializePaymentGridTerms,
    loadPaymentGridData,
    loadPaymentGridLenderPrograms,
    loadPaymentGridTermData,
    loadPaymentGridTermLenderProgram,
    removeProgramResidualOverride,
    updateBaseResidualOverride,
    clearPaymentGridColumnFigures,
    clearPaymentGridTermPrograms,
    clearPaymentGridTermRecords,
    setPaymentGridLeftTerms,
    holdPaymentGridForVehicleSwitch,
    resetPaymentGrid,
    selectedTerm,
    setPaymentGridBuild,
    setPaymentGridDealProgram,
    setPaymentGridDealTermBeforeEdit,
    setPaymentGridCellPricedAt,
    setPaymentGridPendingSellRate,
    setPaymentGridColumnCells,
    setPaymentGridColumnOverride,
    setPaymentGridRestoredInputs,
    setPaymentGridSelectedColumn,
    setPaymentGridSelectedRow,
    setPaymentGridColumnSources,
    setPaymentGridOriginalColumnSources,
    restorePaymentGridTermOverrides,
    setPaymentGridDecidedColumns,
    setPaymentGridFallbackRates,
    setPaymentGridUserPricedTerm,
    setPaymentGridWrittenTerm,
    updateBuyRateForSelectedTermUPQ,
    updateBuyRateOverride,
    updateCurrentCalculationEndpoint,
    updateDownPaymentImmediate,
    updateFinanceSellRateForSelectedTermUPQ,
    updatePaymentGridRow,
    updateResidualForSelectedTermUPQ,
    updateSellRateForSelectedTermUPQ,
    updateSellRateOverride
} from '../actions/creators';

import {
    flexBuyTermList,
    loadingFlexBuyPaymentGrid,
    loadingUpdateFlexBuyCashDown,
    resetFlexBuyPaymentGridData,
    setFlexBuyPaymentGridPricedAt,
    updateFlexBuyPaymentGridAxisValues,
    updateFlexBuyPaymentGridData,
    updateFlexBuyTermsGrid
} from '../actions/flexBuyPaymentActionCreators';
import type {
    PaymentGridMiddleware,
    PaymentGridMiddlewareAction,
    PaymentGridMiddlewareNext,
    PaymentGridMiddlewareStore
} from '../types/commonTypes';
import type { GridPaymentGridData, HostSelectors, IPaymentConfig, PaymentGridBuild } from '../types/paymentTypes';
import { isCalculationEndpoint, parseSelectedTermMonths } from '../utils';
import {
    isConditionalRebateKeepGridOpenEnabled,
    isGridIncentiveManagementEnabled,
    isGridManualRebateMainRowFirstEnabled,
    isIncentiveChangeActionType,
    isSupersededTermPaymentResponse
} from '../utils/incentives';
import { overridesTypedInGrid, pickGridWrittenOverrides } from '../utils/gridWrittenOverrides';
import { changesAnnualMiles } from '../utils/leaseContractFieldsUtils';
import { parseTermId } from '../utils/termIdParser';
import {
    RESPONSE_TERM_IDS_KEY,
    addFlexBuyPaymentGridMeta,
    addPaymentGridMeta,
    nextGridRoundId,
    shouldProcessGridReselectAction,
    tagOfferType
} from '../utils/paymentGridUtils';
import type { AddPaymentGridMetaOptions } from '../utils/paymentGridUtils';
import { CALC_ENDPOINTS, GRID_TYPED_OVERRIDES, PAYMENT_REQUEST_OVERRIDE_NODE, RATE_TYPE } from '../constants';
import type { DropOverrides } from '../constants';
import {
    ASP_CHECKBOX_TOGGLED_INTENT,
    PAYMENT_GRID_BAGGAGE_COMPRESSION_TOGGLE,
    MULTI_SCENARIO_GRID_CONSISTENCY_TOGGLE,
    SV_RETAIN_PROGRAM_TOGGLE,
    DEAL_HYDRATION_INTENT,
    GRID_AXIS_EDIT_INTENT,
    PAYMENT_GRID_CELL_SELECTED_INTENT,
    PAYMENT_GRID_RESIDUAL_INTENT,
    PAYMENT_GRID_SELL_RATE_INTENT,
    PAYMENT_GRID_USER_INTENTS
} from '../constants/meta';
import { isNumber, isUndefined } from '../reducers/offerNodeUtils';
import { PROGRAM_QUOTES_REFETCH_META } from '../reducers/termsStateUtils';
import { isEmpty } from '../utils/common';
import { isSameLenderProgram, isUserPricedProgram } from '../utils/lenderProgramUtils';
import { isValidProgram, resolveCompleteResidualUnit, resolveResidualType } from '../utils/paymentPayloadUtils';
import {
    PaymentGridSource,
    getFallbackTerms,
    editGoesOutOnUserProgramQuotes,
    isUserProgramQuotesColumn,
    resolveCellVerdict,
    resolveColumnSources,
    resolveColumnVerdict,
    splitTermsByEngine,
    shouldSkipTermOverrideForColumn,
    hasTerm,
    validDownPayments
} from '../utils/paymentGridSourceUtils';
import type { LeaseColumnFacts } from '../utils/leaseGridColumnUtils';
import {
    isColumnAwaitingPair,
    isColumnPairIncomplete,
    resolveLeaseColumnSources,
    resolvePriceableColumnSources
} from '../utils/leaseGridColumnUtils';

// Type definitions for internal use
type State = any; // Redux state - keeping as any since it's the full Redux state tree

/** Only a host that pins its deals to program-quotes implements this, so Manager View answers false. */
const isUpqFallbackDisabled = (middlewareConfig: IPaymentConfig | undefined, state: State): boolean =>
    Boolean(middlewareConfig?.selectors?.isUserProgramQuotesFallbackDisabled?.(state));

let paymentActionsSkipList = [types.APPLIED_PROGRAM, types.SELECTED_TERM, types.UPDATE_FLEX_BUY_PAYMENT];

// Helper functions for complex conditions

/** Redesign gate for this middleware; `isAspPaymentGridRedesignActive` owns the deal-type scope. */
const isAspPgRedesignEnabled = (state: State, offerType: OfferType): boolean => isAspPaymentGridRedesignActive(state, offerType);

/** The active cell's own cash down, from the selected term id: the deal's `downPayment` can read 0 after a close and reopen. */
const getActiveCellDownPayment = (state: State, offerType: OfferType): number | undefined =>
    parseTermId(getTermIdForOfferType(state, offerType))?.termIdParams?.downpayment;

/** Whether Auto Select Program is OFF for the grid on screen. Falls back to the deal's endpoint when there is no build, since a cell selection can move that endpoint. */
const gridAspIsUserProgramQuotes = (state: State, offerType: OfferType): boolean =>
    getPaymentGridBuild(state, offerType)?.isUserProgramQuotes ?? getIsUserProgramQuotes(state, offerType);

// The DEAL's own engine - its endpoint, or the one it adopted from a selected cell. Not the column discriminator: a
// deal can be user-priced while its other columns still price within the program on the deal.
const dealPricesOnUserProgramQuotes = (state: State, offerType: OfferType): boolean =>
    isExplicitLenderProgramGridActive(state, offerType)
        ? gridAspIsUserProgramQuotes(state, offerType)
        : getIsUserProgramQuotes(state, offerType);

const isGridUserProgramQuotes = (
    state: State,
    offerType: OfferType,
    whenGateOff: (state: State, offerType: OfferType) => boolean = gridAspIsUserProgramQuotes
): boolean =>
    isExplicitLenderProgramGridActive(state, offerType)
        ? getGridIsUserProgramQuotes(state, offerType)
        : whenGateOff(state, offerType);

/**
 * Whether Auto Select Program is off for the grid, the only grid-wide fact allowed to decide the engine of a column
 * being edited or re-termed. Under the explicit-lender gate that is the user's untick alone: `isGridUserProgramQuotes`
 * also reads `hasDealLenderProgram`, which follows the selected term and so flipped every edited column to
 * user-program-quotes once the active column's term moved or its cell was user-priced (DE774309).
 */
const isGridAspOff = (state: State, offerType: OfferType): boolean =>
    isExplicitLenderProgramGridActive(state, offerType)
        ? getPaymentGridBuild(state, offerType)?.aspUnticked === true
        : gridAspIsUserProgramQuotes(state, offerType);

/**
 * Whether a figure typed into a program-backed column keeps that column on program-quotes instead of moving it to
 * user-program-quotes. Re-gated off `isExplicitLenderProgramGridActive`, which belongs to a release that also removes
 * the Auto Select checkbox and so left this rule unreachable while the checkbox still exists.
 *
 * Inert while the grid is on user-program-quotes, which the explicit-lender gate had no need to say: with Auto Select
 * OFF a column can still carry `ProgramQuotes` from an earlier Auto-Select-on pass, and `isUserProgramQuotesColumn`
 * answers on that recorded source alone. Without this, a rate typed into such a column would be sent to program-quotes
 * - the engine that cannot express an unsolicited quote - re-opening DE762937 for every Auto-Select-off deal.
 */
const programBackedEditsStay = (state: State, offerType: OfferType): boolean =>
    isAspPaymentGridRedesignActive(state, offerType) && !isGridAspOff(state, offerType);

/**
 * Whether a column's term has a program to pin, so it tries program-quotes first. Under the explicit-lender gate
 * `getGridColumnProgramInfo` answers with the deal's program when there is one and the term's cached program otherwise,
 * so a deal on no program tries program-quotes only where a program is known and never sends an unpinned
 * program-quotes call, which would let Payment Services pick a program of its own (DE774309).
 */
const bindGridProgramForTerm =
    (state: State, offerType: OfferType): ((termMonths?: number) => boolean) =>
    (termMonths) =>
        isValidProgram(getGridColumnProgramInfo(state, offerType, termMonths));

type ExplicitLockResolver = HostSelectors['getExplicitLenderLock'];

/**
 * Snapshot of the deal's program for the grid session, set on every open and restore so a stale one never survives.
 * Read off the selected term, never the program cache: a deal on no program must open with no snapshot (R2).
 */
const recordDealProgramAtOpen = (
    store: PaymentGridMiddlewareStore,
    offerType: OfferType,
    resolveExplicitLock?: ExplicitLockResolver
) => {
    const state = store.getState();
    const dealProgram = getSelectedTerm(state, offerType)?.lenderProgram;
    const isDealProgram = isValidProgram(dealProgram) && !isUserPricedProgram(dealProgram);
    const { lenderCode: _lenderCode, ...explicitLock } = (!isDealProgram && resolveExplicitLock?.(state, offerType)) || {};
    if (isDealProgram || !isValidProgram(explicitLock)) {
        store.dispatch(setPaymentGridDealProgram(offerType, isDealProgram ? dealProgram : undefined));
        return;
    }
    store.dispatch(setPaymentGridDealProgram(offerType, explicitLock, getSelectedTermMonths(state, offerType)));
};

const hasGridProgramForTerm = (state: State, offerType: OfferType, termMonths?: number): boolean =>
    bindGridProgramForTerm(state, offerType)(termMonths);

/** Lease under the redesign. The one predicate every Lease-only divergence in this file hangs off. */
const isLeaseRedesign = (state: State, offerType: OfferType): boolean =>
    offerType === LEASE && isAspPgRedesignEnabled(state, offerType);

/**
 * `adoptSelectedCellEngine`'s own eligibility - deliberately not folded into `isLeaseRedesign`, which
 * ~40 other call sites in this file hang Lease-specific mechanics off (residual seeding, MF-vs-APR row shapes,
 * grid-round bookkeeping). Widening that shared predicate would flip all of them for Finance at once; this twin
 * only widens the one gate that was actually broken for Finance. Balloon is not listed - `isAspPgRedesignEnabled`
 * already excludes it (`isAspPaymentGridRedesignActive`'s own comment: "Balloon and Cash never populate
 * columnSources"), so adding it here would be dead code, not a real inclusion.
 */
const supportsGridCellEngineAdoption = (state: State, offerType: OfferType): boolean =>
    (offerType === LEASE || offerType === FINANCE) && isAspPgRedesignEnabled(state, offerType);

/**
 * DE773956: toggle-gated so the untested Lease/Balloon call sites below can suppress a program-quotes
 * excursion for a dealer who opted out of Auto Select Program, without changing behavior while off.
 */
const dealerOptOutBlocksPqExcursion = (state: State): boolean =>
    getFeatureToggle(state, 'DE773956_Enable_Auto_Select_Guard_Extension') && getDealerOptedOutOfAutoSelectProgram(state);

/**
 * Whether a grid edit has to wait for the other half of its column's pair before anything is priced.
 *
 * On user-program-quotes Payment Services derives neither a rate nor a residual, so a column is priceable only
 * once it holds both. A Lease column this build never quoted holds only what the user has typed: the figures in
 * `offer.terms` are what an earlier program-quotes build left behind, and neither the payload nor the guard claims
 * them (see `getClaimableLenderResidualForTerm`). So an edit that supplies one half alone could only go out
 * incomplete and be rejected, quoting the column off a figure nobody chose (DE762937).
 *
 * Symmetric by design - a rate typed with no residual waits, and a residual typed with no rate waits the same way.
 * Clearing a residual waits too: the removal takes the only residual the column had. A column whose figures ever
 * produced a payment is in `quotedTerms`, so every edit to it reprices as before rather than being held here.
 *
 * Judged **per column**, not on the deal's Auto Select intent. Gated on ASP being off, this never fired for an ASP-ON
 * column that had fallen back to user-program-quotes: a rate typed into it went out with no residual, was rejected, and
 * the error wiped the entry. ASP-off columns still resolve to user-program-quotes, so the ASP-off behaviour this guard
 * shipped with is preserved exactly.
 *
 * The edit still reaches the reducers - only the payment is withheld - so the column KEEPS what was typed and the next
 * edit sends it. That retention is enforced in `getSellRatesFromTerms`, which used to discard an `Unavailable` column's
 * own rate and so erased the very value this guard is waiting for the other half of.
 */
const shouldHoldGridEditForIncompleteColumn = (
    state: State,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType
): boolean => {
    if (!isLeaseRedesign(state, offerType)) return false;

    const isRateEdit =
        action.type === types.UPDATED_SELL_RATE_OVERRIDE && action.meta?.userIntent === PAYMENT_GRID_SELL_RATE_INTENT;
    const isResidualEdit =
        (action.type === types.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE ||
            action.type === types.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE) &&
        action.meta?.userIntent === PAYMENT_GRID_RESIDUAL_INTENT;
    const isResidualRemoval =
        action.type === types.REMOVED_PROGRAM_RESIDUAL_OVERRIDE && action.meta?.userIntent === PAYMENT_GRID_RESIDUAL_INTENT;
    if (!isRateEdit && !isResidualEdit && !isResidualRemoval) return false;

    if (action.meta?.middleware?.payment === false) return false;

    const months = action.payload?.months;
    if (!isNumber(months)) return false;

    const { quotedTerms, quotedColumns, columnSources } = getPaymentGridColumnView(state, offerType);
    const columnIndex = action.meta?.gridColumnIndex;
    const isSibling = isSharedTermSibling(
        state,
        offerType,
        isNumber(columnIndex) ? (columnIndex as number) : undefined,
        months as number
    );
    const source = isSibling
        ? getColumnVerdict(state, offerType, columnIndex as number, months as number)
        : getPaymentGridStuckColumnOrigin(state, offerType, months as number) ?? columnSources[months as number];

    const goesOutOnUserProgramQuotes = editGoesOutOnUserProgramQuotes(
        source,
        isGridUserProgramQuotes(state, offerType),
        programBackedEditsStay(state, offerType)
    );

    const { hasRate, hasResidual } = getGridColumnPairHalves(
        state,
        offerType,
        isNumber(columnIndex) ? (columnIndex as number) : undefined,
        months as number
    );

    return isColumnPairIncomplete({
        source,
        isUserProgramQuotesEngine: goesOutOnUserProgramQuotes,
        hasUserSuppliedRate: isRateEdit || hasRate,
        hasUserSuppliedResidual: isResidualRemoval ? false : isResidualEdit || hasResidual,
        isQuoted: isSibling ? quotedColumns.has(columnIndex as number) : quotedTerms.has(months as number),
        isRefused: getPaymentGridPairRejectedTerms(state, offerType)[months as number] === true
    });
};

/**
 * The cash-down a discovery probe is made at. `undefined` while the redesign is off. On Lease the deal's cash down is
 * the equity-allocation anchor rather than `downPayment`; every rung coalesces on nullish so a genuine zero is kept.
 */
const getRedesignDiscoveryAnchor = (state: State, offerType: OfferType, gridMiddleRow?: number): number | undefined => {
    if (!isAspPgRedesignEnabled(state, offerType)) return undefined;

    const activeCellDownPayment = getActiveCellDownPayment(state, offerType);
    if (!isLeaseRedesign(state, offerType)) return activeCellDownPayment;

    return (
        activeCellDownPayment ??
        getEquityAllocationGridCashDown(state, offerType) ??
        getDownPayment(state, offerType) ??
        gridMiddleRow
    );
};

/** Lease pins one program per column, so a per-row grid payment must not repin it - `updateProgramInfo` merges per term and never clears. */
const withLeaseProgramPin = (state: State, offerType: OfferType, options: AddPaymentGridMetaOptions): AddPaymentGridMetaOptions =>
    isLeaseRedesign(state, offerType)
        ? {
              ...options,
              skipProgramInfoUpdate: true,
              // The engine state this call goes out under, so the verdict its response triggers is judged against it.
              // From the discriminator, never the endpoint, which a user-priced cell selection moves.
              isUserProgramQuotes: isGridUserProgramQuotes(state, offerType)
          }
        : options;

/** One outgoing row call: the columns it covers, the engine it is forced onto, and whether it drives the deal. */
type GridRowCall = { terms: number[]; isMain: boolean; forceUserProgramQuotes: boolean; isSplitPart: boolean };

type GridRowEngineSplit = {
    priceableTerms: number[];
    programQuotesTerms: number[];
    userProgramQuotesTerms: number[];
};

/**
 * What one grid row costs, and on which engine. A row spanning both engines is two calls, since a request carries one;
 * the half holding the deal's selected term is the main payment and is planned last.
 */
type ColumnScopedCall = {
    columnIndex: number;
    term: number;
    sellRate?: number;
    residualInfo?: GenericObject;
    ignoreTermOverride?: boolean;
};

const withoutNonActiveCellRecords = (
    state: State,
    offerType: OfferType,
    action: PaymentGridMiddlewareAction
): GenericObject | undefined => {
    if (!isAspPgRedesignEnabled(state, offerType)) return undefined;

    const originalAction = action.meta?.originalAction;
    const coveredColumns = originalAction?.meta?.gridColumnIndices as number[] | undefined;
    if (!coveredColumns?.length) return undefined;

    const terms = action.payload?.terms as GenericObject | undefined;
    const byId = terms?.byId as GenericObject | undefined;
    if (!byId) return undefined;

    const gridTerms = getPaymentGridTerms(state, offerType);
    const activeColumn = getResolvedPaymentGridSelectedColumnIndex(state, offerType);
    if (activeColumn != null && coveredColumns.includes(activeColumn)) return undefined;

    const withheldIds = Object.keys(byId).filter((termId) => {
        const months = (byId[termId] as { months?: number } | undefined)?.months;
        return isNumber(months) && gridTerms.filter((gridTerm) => gridTerm === months).length > 1;
    });
    if (!withheldIds.length) return undefined;

    const kept = Object.fromEntries(Object.entries(byId).filter(([termId]) => !withheldIds.includes(termId)));
    const idsKey = RESPONSE_TERM_IDS_KEY[offerType];
    const responseIds = (terms as GenericObject)[idsKey];
    return {
        ...terms,
        byId: kept,
        ...(Array.isArray(responseIds) && {
            [idsKey]: responseIds.filter((termId: string) => !withheldIds.includes(termId))
        })
    };
};

const holdActiveCellPosition = (
    store: PaymentGridMiddlewareStore,
    state: State,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType
): void => {
    if (!isAspPgRedesignEnabled(state, offerType)) return;
    const pin = (column: number | undefined, row: number | undefined) => {
        store.dispatch(setPaymentGridSelectedColumn(offerType, column));
        store.dispatch(setPaymentGridSelectedRow(offerType, row));
    };

    if (action.type === types.UPDATE_PAYMENT_GRID_TERM) {
        const { key, val } = action.payload;
        const gridTerms = getPaymentGridTerms(state, offerType);
        if (val == null || !gridTerms.some((term, index) => index !== key && term === val)) return;
        pin(
            getPaymentGridSelectedColumnIndex(state, offerType) ?? getResolvedPaymentGridSelectedColumnIndex(state, offerType),
            getPaymentGridSelectedRowIndex(state, offerType) ?? getResolvedPaymentGridSelectedRowIndex(state, offerType)
        );
        return;
    }

    const isDealMove =
        action.meta?.userIntent !== DEAL_HYDRATION_INTENT &&
        action.meta?.userIntent !== GRID_AXIS_EDIT_INTENT &&
        ((action.type === types.SELECTED_TERM && action.meta?.userIntent !== PAYMENT_GRID_CELL_SELECTED_INTENT) ||
            action.type === types.UPDATED_DOWN_PAYMENT_IMMEDIATE ||
            action.type === types.UPDATED_DOWN_PAYMENT_DEBOUNCED);
    const isPinHeld =
        getPaymentGridSelectedColumnIndex(state, offerType) != null || getPaymentGridSelectedRowIndex(state, offerType) != null;
    if (isDealMove && isPinHeld) pin(undefined, undefined);
};

const nearestIndexWhere = (length: number, key: number, isCandidate: (index: number) => boolean): number | undefined => {
    for (let index = Math.min(key, length) - 1; index >= 0; index -= 1) {
        if (isCandidate(index)) return index;
    }
    for (let index = Math.min(key, length); index < length; index += 1) {
        if (isCandidate(index)) return index;
    }
    return undefined;
};

const moveDealToGridCell = (
    store: PaymentGridMiddlewareStore,
    state: State,
    offerType: OfferType,
    columnIndex: number,
    rowIndex: number
): void => {
    const gridTerms = getPaymentGridTerms(state, offerType);
    const gridCashDown = getPaymentGridCashDown(state, offerType);
    const termMonths = gridTerms[columnIndex] as number;
    const downPayment = gridCashDown[rowIndex] as number;
    const termId = getTermId(offerType, termMonths, downPayment) as string;

    store.dispatch(updateDownPaymentImmediate(downPayment));
    store.dispatch(setPaymentGridSelectedColumn(offerType, columnIndex));
    store.dispatch(setPaymentGridSelectedRow(offerType, rowIndex));

    const isTermShared = isTermDuplicated(gridTerms, termMonths);
    const isCellShared = isTermShared || gridCashDown.filter((cashDown) => cashDown != null && cashDown === downPayment).length > 1;
    const ownRecord = isCellShared
        ? getPaymentGridCellRecord(state, offerType, columnIndex, rowIndex, termMonths, downPayment)
        : undefined;
    if (ownRecord || isTermShared) {
        const ownColumnOverride = getOwnColumnOverride(state, offerType, columnIndex, termMonths);
        store.dispatch(
            adoptPaymentGridCell(
                offerType,
                termId,
                ownRecord,
                ownColumnOverride?.residualInfo,
                isTermShared ? { sellRate: ownColumnOverride?.sellRate } : undefined,
                columnIndex
            )
        );
    }

    const isServedFromStoredQuote = isPaymentGridCellServedFromStoredQuote(state, offerType, columnIndex, rowIndex);
    store.dispatch(
        selectedTerm(termId, {
            ...(isServedFromStoredQuote && { payment: false }),
            meta: { userIntent: PAYMENT_GRID_CELL_SELECTED_INTENT }
        })
    );
};

const isPricedColumnVerdict = (state: State, offerType: OfferType, columnIndex: number, termMonths: number): boolean => {
    const verdict = resolveColumnVerdict(
        getPaymentGridDecidedColumns(state, offerType),
        getPaymentGridColumnSources(state, offerType),
        columnIndex,
        termMonths
    );
    return verdict === PaymentGridSource.ProgramQuotes || verdict === PaymentGridSource.UserProgramQuotes;
};

const isQuotedGridCell = (state: State, offerType: OfferType, columnIndex: number, rowIndex: number): boolean => {
    const termMonths = getPaymentGridTerms(state, offerType)[columnIndex];
    const downPayment = getPaymentGridCashDown(state, offerType)[rowIndex];
    return (
        hasTerm(termMonths) &&
        isNumber(downPayment) &&
        isPricedColumnVerdict(state, offerType, columnIndex, termMonths) &&
        hasCellQuote(state, offerType, termMonths, downPayment as number, columnIndex)
    );
};

const parkDealOnTermBeforeEdit = (store: PaymentGridMiddlewareStore, offerType: OfferType): void => {
    const state = store.getState();
    const termBeforeEdit = getPaymentGridDealTermBeforeEdit(state, offerType);
    if (!termBeforeEdit) return;

    const activeCell = getResolvedPaymentGridActiveCell(state, offerType);
    if (!activeCell || getSelectedTermMonths(state, offerType) !== activeCell.termMonths) return;

    const verdict = resolveColumnVerdict(
        getPaymentGridDecidedColumns(state, offerType),
        getPaymentGridColumnSources(state, offerType),
        activeCell.columnIndex,
        activeCell.termMonths
    );
    if (verdict !== PaymentGridSource.Unavailable) return;

    const heldOverrides = getPaymentGridDealTermOverridesBeforeEdit(state, offerType);
    store.dispatch(setPaymentGridDealTermBeforeEdit(offerType, undefined));
    if (getTerm(state, termBeforeEdit)?.monthlyPayment == null) return;

    if (heldOverrides) store.dispatch(restorePaymentGridTermOverrides(offerType, heldOverrides));
    store.dispatch(selectedTerm(termBeforeEdit, { meta: { userIntent: GRID_AXIS_EDIT_INTENT } }));
};

const settleActiveColumnAfterRound = (
    store: PaymentGridMiddlewareStore,
    offerType: OfferType,
    upqFallbackDisabled: boolean
): void => {
    parkDealOnTermBeforeEdit(store, offerType);

    const state = store.getState();
    const activeCell = getResolvedPaymentGridActiveCell(state, offerType);
    if (!activeCell || !isPricedColumnVerdict(state, offerType, activeCell.columnIndex, activeCell.termMonths)) return;

    const { columnIndex, rowIndex, termMonths, downPayment } = activeCell;
    if (!getPaymentGridCellRecord(state, offerType, columnIndex, rowIndex, termMonths, downPayment)) return;

    if (getPaymentGridDealTermBeforeEdit(state, offerType)) {
        store.dispatch(setPaymentGridDealTermBeforeEdit(offerType, undefined));
        if (
            !upqFallbackDisabled &&
            isExplicitLenderProgramGridActive(state, offerType) &&
            (isLeaseRedesign(state, offerType) ||
                (getFeatureToggle(state, 'DE783996_Enable_Roll_To_UPQ_On_First_Calculation') &&
                    offerType === FINANCE &&
                    supportsGridCellEngineAdoption(state, offerType)))
        ) {
            moveDealEngineToSource(
                store,
                state,
                offerType,
                resolveCellVerdict(
                    getPaymentGridTerms(state, offerType),
                    getPaymentGridDecidedColumns(state, offerType),
                    getPaymentGridColumnSources(state, offerType),
                    columnIndex,
                    termMonths
                ),
                columnIndex,
                termMonths
            );
        }
    }
    if (isDealParkedOffActiveColumn(state, offerType)) moveDealToGridCell(store, state, offerType, columnIndex, rowIndex);
};

const releaseDealTermBeforeEditOnSelection = (
    store: PaymentGridMiddlewareStore,
    state: State,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType
): void => {
    if (action.meta?.userIntent === GRID_AXIS_EDIT_INTENT) return;
    if (getPaymentGridDealTermBeforeEdit(state, offerType)) store.dispatch(setPaymentGridDealTermBeforeEdit(offerType, undefined));
};

const restampActiveCellFromDealPayment = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    state: State,
    offerType: OfferType
): void => {
    if (action.type !== types.PAYMENT_RECEIVED || action.meta?.isStalePaymentResponse) return;
    const originalAction = action.meta?.originalAction;
    if (originalAction?.type !== types.SELECTED_TERM) return;
    if (!isAspPgRedesignEnabled(state, offerType) || !getPaymentGridToggle(state, offerType)) return;
    if (isSupersededTermPaymentResponse(action, getTermIdForOfferType(state, offerType))) return;

    const activeCell = getResolvedPaymentGridActiveCell(state, offerType);
    const requested = parseTermId(originalAction.payload)?.termIdParams;
    if (!activeCell || requested?.term !== activeCell.termMonths || (requested?.downpayment ?? 0) !== activeCell.downPayment) {
        return;
    }

    const record = getSelectedTerm(state, offerType);
    if (record?.monthlyPayment == null || record.months !== activeCell.termMonths) return;

    const { columnIndex, rowIndex, termMonths, downPayment } = activeCell;
    store.dispatch(
        setPaymentGridColumnCells(offerType, { [columnIndex]: { [rowIndex]: { ...record, term: termMonths, downPayment } } })
    );

    if (getFeatureToggle(state, PAYMENT_GRID_BAGGAGE_COMPRESSION_TOGGLE as any)) {
        store.dispatch(
            setPaymentGridCellPricedAt(offerType, { [getTermId(offerType, termMonths, downPayment) as string]: Date.now() })
        );
    }
};

const columnScopedMeta = (
    state: State,
    offerType: OfferType,
    terms: number[],
    columnIndices: number[] | undefined,
    columnSellRate?: number,
    columnResidualInfo?: GenericObject,
    ignoreTermOverride?: boolean
): GenericObject => ({
    ...(columnSellRate != null && { columnSellRate }),
    ...(columnResidualInfo != null && { columnResidualInfo }),
    ...(ignoreTermOverride && { ignoreTermSellRateOverride: true }),
    ...(columnIndices?.length === 1 &&
        terms.length === 1 &&
        columnResidualInfo == null &&
        shouldIgnoreTermFigure(state, offerType, columnIndices[0], 'residualInfo') && { ignoreTermResidualOverride: true }),
    ...(columnIndices?.length === 1 &&
        terms.length === 1 &&
        isTermDuplicated(getPaymentGridTerms(state, offerType), terms[0]) && {
            columnScopedForDuplicateTerm: true
        })
});

const activeCellSellRate = (state: State, offerType: OfferType, isUserProgramQuotesCall: boolean): number | undefined =>
    offerType === LEASE || !isUserProgramQuotesCall
        ? undefined
        : getResolvedColumnSellRate(state, offerType, getResolvedPaymentGridSelectedColumnIndex(state, offerType));

/**
 * LEASE only.
 *
 * Finance is deliberately excluded: it keeps sending program quotes for an overridden rate.
 *
 * Returns `undefined` when no priceable term is duplicated, so a grid without duplicates keeps the existing plan.
 */
type LeaseColumnCall = ColumnScopedCall & { forceUserProgramQuotes: boolean };

type LeaseRowPlan = {
    programTerms: number[];
    programColumns: number[];
    upqTerms: number[];
    upqColumns: number[];
    columnCalls: LeaseColumnCall[];
};

const leaseRowPlanColumns = (plan: LeaseRowPlan): number[] =>
    [...new Set([...plan.programColumns, ...plan.upqColumns, ...plan.columnCalls.map(({ columnIndex }) => columnIndex)])].sort(
        (left, right) => left - right
    );

const isLeaseRowPlanSplit = (plan: LeaseRowPlan): boolean =>
    (Boolean(plan.programTerms.length) || plan.columnCalls.some(({ forceUserProgramQuotes }) => !forceUserProgramQuotes)) &&
    (Boolean(plan.upqTerms.length) || plan.columnCalls.some(({ forceUserProgramQuotes }) => forceUserProgramQuotes));

const leaseRowPlanMain = (
    plan: LeaseRowPlan,
    dealColumnIndex: number | undefined
): { columnCall?: LeaseColumnCall; isProgramBatch: boolean; isUpqBatch: boolean } => {
    const ownCall = plan.columnCalls.find(({ columnIndex }) => columnIndex === dealColumnIndex);
    const isUpqBatchHolder = dealColumnIndex != null && plan.upqColumns.includes(dealColumnIndex);
    const isProgramBatch = !ownCall && !isUpqBatchHolder && Boolean(plan.programTerms.length);
    const isUpqBatch = !ownCall && !isProgramBatch && Boolean(plan.upqTerms.length);
    return { columnCall: ownCall ?? (isProgramBatch || isUpqBatch ? undefined : plan.columnCalls[0]), isProgramBatch, isUpqBatch };
};

const isPricedVerdict = (verdict: PaymentGridSource | undefined) =>
    verdict === PaymentGridSource.ProgramQuotes || verdict === PaymentGridSource.UserProgramQuotes;

const withSharedTermsJudgedByHolder = (
    state: State,
    offerType: OfferType,
    sources: Record<number, PaymentGridSource>
): Record<number, PaymentGridSource> => {
    const gridTerms = getPaymentGridTerms(state, offerType);
    const decidedColumns = getPaymentGridDecidedColumns(state, offerType);
    return gridTerms.reduce<Record<number, PaymentGridSource>>(
        (judged, gridTerm, columnIndex) => {
            if (!isNumber(gridTerm) || !isTermDuplicated(gridTerms, gridTerm as number)) return judged;
            const term = gridTerm as number;
            const holderVerdict = decidedColumns[columnIndex];
            if (!isPricedVerdict(judged[term]) && isPricedVerdict(holderVerdict)) judged[term] = holderVerdict;
            return judged;
        },
        { ...sources }
    );
};

const planLeaseColumnsByOwnedOverride = (
    state: State,
    offerType: OfferType,
    priceableTerms: number[],
    targetedTerm?: number
): LeaseRowPlan | undefined => {
    const gridTerms = getPaymentGridTerms(state, offerType);
    if (!priceableTerms.some((term) => isTermDuplicated(gridTerms, term))) return undefined;

    const termSources = getPaymentGridColumnSources(state, offerType);
    const dealIsUserProgramQuotes = isGridUserProgramQuotes(state, offerType);
    const plan: LeaseRowPlan = { programTerms: [], programColumns: [], upqTerms: [], upqColumns: [], columnCalls: [] };

    gridTerms.forEach((gridTerm, columnIndex) => {
        if (!isNumber(gridTerm) || !priceableTerms.includes(gridTerm as number)) return;
        const term = gridTerm as number;
        const isShared = isTermDuplicated(gridTerms, term);
        const isSibling = isSharedTermSibling(state, offerType, columnIndex, term);
        const verdict = isShared ? getColumnVerdict(state, offerType, columnIndex, term) : termSources[term];
        if (isShared && verdict === PaymentGridSource.Unavailable && !(term === targetedTerm && !isSibling)) return;

        const ownOverride = getOwnColumnOverride(state, offerType, columnIndex, term);
        const ownsFigures = ownOverride?.sellRate != null || ownOverride?.residualInfo != null;
        const forceUserProgramQuotes =
            ownsFigures && !programBackedEditsStay(state, offerType)
                ? true
                : isUserProgramQuotesColumn(verdict, dealIsUserProgramQuotes);
        const mustIgnoreDealsSlot =
            isSibling &&
            (getSellRateOverrideByOfferTypeAndTerm(state, offerType, term) != null ||
                hasUserSuppliedGridResidual(state, offerType, term));

        if (ownsFigures || mustIgnoreDealsSlot) {
            plan.columnCalls.push({
                columnIndex,
                term,
                sellRate: ownOverride?.sellRate,
                ...(ownOverride?.residualInfo && { residualInfo: ownOverride.residualInfo as GenericObject }),
                ignoreTermOverride: ownOverride?.sellRate == null,
                forceUserProgramQuotes
            });
            return;
        }
        const [terms, columns] = forceUserProgramQuotes
            ? [plan.upqTerms, plan.upqColumns]
            : [plan.programTerms, plan.programColumns];
        columns.push(columnIndex);
        if (!terms.includes(term)) terms.push(term);
    });

    return plan.columnCalls.length || plan.upqTerms.length || plan.programTerms.length ? plan : undefined;
};

const splitTermsByColumn = (
    state: State,
    offerType: OfferType,
    terms: number[],
    isUserProgramQuotesCall: boolean | undefined
): { sharedTerms: number[]; columnCalls: ColumnScopedCall[] } => {
    const gridTerms = getPaymentGridTerms(state, offerType);

    const sharedTerms: number[] = [];
    const columnCalls: ColumnScopedCall[] = [];

    terms.forEach((term) => {
        const positions = gridTerms.reduce<number[]>((held, gridTerm, columnIndex) => {
            if (gridTerm === term) held.push(columnIndex);
            return held;
        }, []);

        if (positions.length > 1) {
            positions.forEach((columnIndex) => {
                const ownOverride = getOwnColumnOverride(state, offerType, columnIndex, term);
                const ownSellRate = ownOverride?.sellRate;
                columnCalls.push({
                    columnIndex,
                    term,
                    sellRate: ownSellRate ?? activeCellSellRate(state, offerType, Boolean(isUserProgramQuotesCall)),
                    ...(ownOverride?.residualInfo && { residualInfo: ownOverride.residualInfo as GenericObject }),
                    ignoreTermOverride: ownSellRate == null
                });
            });
        } else {
            sharedTerms.push(term);
        }
    });

    return { sharedTerms, columnCalls };
};

const shouldIgnoreTermFigure = (
    state: State,
    offerType: OfferType,
    columnIndex: number,
    figure: 'sellRate' | 'residualInfo'
): boolean => {
    const term = getPaymentGridTerms(state, offerType)[columnIndex];
    if (!isNumber(term) || !isSharedTermSibling(state, offerType, columnIndex, term as number)) return false;
    return getOwnColumnOverride(state, offerType, columnIndex, term as number)?.[figure] == null;
};

const isSharedTermSibling = (state: State, offerType: OfferType, columnIndex: number | undefined, termMonths: number): boolean =>
    columnIndex != null &&
    isTermDuplicated(getPaymentGridTerms(state, offerType), termMonths) &&
    columnIndex !== getPaymentGridTermOwnerColumnIndex(state, offerType);

const getColumnVerdict = (
    state: State,
    offerType: OfferType,
    columnIndex: number,
    termMonths: number
): PaymentGridSource | undefined => {
    const ownVerdict = getPaymentGridDecidedColumns(state, offerType)[columnIndex] as PaymentGridSource | undefined;
    return isSharedTermSibling(state, offerType, columnIndex, termMonths)
        ? ownVerdict
        : ownVerdict ?? getPaymentGridColumnSources(state, offerType)[termMonths];
};

const editWritesTermSlot = (state: State, offerType: OfferType, columnIndex: number | undefined, termMonths: number): boolean =>
    !isAspPgRedesignEnabled(state, offerType) ||
    !shouldSkipTermOverrideForColumn(
        getPaymentGridTerms(state, offerType),
        getPaymentGridSelectedColumnIndex(state, offerType),
        termMonths,
        columnIndex,
        getSelectedTermMonths(state, offerType)
    );

const gridRowsFor = (state: State, offerType: OfferType): { rowIndex: number; downPayment: number }[] =>
    getPaymentGridCashDown(state, offerType).reduce<{ rowIndex: number; downPayment: number }[]>((rows, cash, rowIndex) => {
        if (cash != null) rows.push({ rowIndex, downPayment: cash as number });
        return rows;
    }, []);

const coveredColumnIndices = (state: State, offerType: OfferType, terms: number[]): number[] =>
    getPaymentGridTerms(state, offerType).reduce<number[]>((covered, term, columnIndex) => {
        if (isNumber(term) && terms.includes(term as number)) covered.push(columnIndex);
        return covered;
    }, []);

const resolveEditedColumnIndex = (preSortTerms: (number | null)[], key: number, newTermValue: number | null): number => {
    if (newTermValue == null) return key;
    const unsorted = [...preSortTerms];
    unsorted[key] = newTermValue;
    return unsorted.slice(0, key).filter((term) => term != null).length;
};

const planGridRowCalls = (
    { priceableTerms, programQuotesTerms, userProgramQuotesTerms }: GridRowEngineSplit,
    { isRowSplit, isMainPayment, currentTermMonth }: { isRowSplit: boolean; isMainPayment: boolean; currentTermMonth?: number }
): GridRowCall[] => {
    if (!isRowSplit) {
        return [
            {
                terms: priceableTerms,
                isMain: isMainPayment,
                // Force user-program-quotes only when every column in the row resolved to it.
                forceUserProgramQuotes: Boolean(priceableTerms.length) && !programQuotesTerms.length,
                isSplitPart: false
            }
        ];
    }

    const isSelectedTermOnUserProgramQuotes = isNumber(currentTermMonth) && userProgramQuotesTerms.includes(currentTermMonth);

    return [
        {
            terms: isSelectedTermOnUserProgramQuotes ? programQuotesTerms : userProgramQuotesTerms,
            isMain: false,
            forceUserProgramQuotes: !isSelectedTermOnUserProgramQuotes,
            isSplitPart: true
        },
        {
            terms: isSelectedTermOnUserProgramQuotes ? userProgramQuotesTerms : programQuotesTerms,
            isMain: isMainPayment,
            forceUserProgramQuotes: isSelectedTermOnUserProgramQuotes,
            isSplitPart: true
        }
    ];
};

const isPaymentGridEnabled = (state: State, offerType: OfferType) => {
    const paymentGridEnabledToggle = getPaymentGridToggle(state, offerType);
    const isFlexBuyPaymentGridEnabled = getFlexBuyPaymentGridToggle(state);
    return paymentGridEnabledToggle || isFlexBuyPaymentGridEnabled;
};

const isGridKeptOpenOnReapply = (action: PaymentGridMiddlewareAction): boolean =>
    action.type === types.APPLIED_PROGRAM && action.meta?.keepGridOpen === true;

const termsPricedByReappliedProgram = (
    state: State,
    offerType: OfferType,
    action: PaymentGridMiddlewareAction,
    terms: number[],
    currentTermMonth: number
): number[] => {
    if (!isGridKeptOpenOnReapply(action)) return terms;
    const reappliedProgram = action.payload?.programData?.lenderProgram;
    const sameProgramTerms = terms.filter(
        (term) =>
            term === currentTermMonth || isSameLenderProgram(getGridColumnProgramInfo(state, offerType, term), reappliedProgram)
    );
    return sameProgramTerms.length ? sameProgramTerms : [currentTermMonth];
};

const splitTermsByProgram = (state: State, offerType: OfferType, terms: number[]): number[][] => {
    if (!terms.length) return [];
    if (!isConditionalRebateKeepGridOpenEnabled(state)) return [terms];
    const selectedProgram = getGridColumnProgramInfo(state, offerType, getSelectedTermMonths(state, offerType));
    if (!isValidProgram(selectedProgram)) return [terms];
    const isOnOtherProgram = (term: number): boolean => {
        const columnProgram = getGridColumnProgramInfo(state, offerType, term);
        return isValidProgram(columnProgram) && !isSameLenderProgram(columnProgram, selectedProgram);
    };
    return [terms.filter((term) => !isOnOtherProgram(term)), terms.filter(isOnOtherProgram)].filter((group) => group.length);
};

const shouldProcessPaymentAction = (
    state: State,
    action: PaymentGridMiddlewareAction,
    isForPaymentGrid: boolean,
    sourceApplication: string | undefined
) => {
    const isPaymentAction = action?.meta?.middleware?.payment || false;
    return (
        isPaymentAction &&
        isForPaymentGrid &&
        (!paymentActionsSkipList.includes(action.type) || isGridKeptOpenOnReapply(action)) &&
        sourceApplication !== ApplicationCodes.SP
    );
};

const isUserProgramQuotesWithPaymentGrid = (
    updatedState: State,
    state: State,
    offerType: OfferType,
    isFlexBuy: boolean,
    isFlexBuyPaymentGridEnabled: boolean
) => {
    const stateForUserQuotes = updatedState;
    const stateForGridToggle = state;
    const isUserProgramQuotes = dealPricesOnUserProgramQuotes(stateForUserQuotes, offerType);
    const paymentGridEnabledToggle = getPaymentGridToggle(stateForGridToggle, offerType);
    return isUserProgramQuotes && !((isFlexBuy && !isFlexBuyPaymentGridEnabled) || !paymentGridEnabledToggle);
};

const shouldUpdateBuyRates = (
    state: State,
    currentTermMonth: number,
    months: number,
    offerType: OfferType,
    isFlexBuy: boolean,
    isFlexBuyPaymentGridEnabled: boolean
) => {
    if (!isUserProgramQuotesWithPaymentGrid(state, state, offerType, isFlexBuy, isFlexBuyPaymentGridEnabled)) {
        return false;
    }
    return months !== currentTermMonth;
};

const isPaymentGridToggleAction = (actionType: string) => {
    return [types.UPDATED_FLEX_BUY_PAYMENT_GRID_TOGGLE, types.UPDATED_PAYMENT_GRID_TOGGLE].includes(actionType);
};

/**
 * Actions that switch the payment grid ON. `UPDATED_PAYMENT_GRID_TOGGLE` covers both the user toggle and the
 * programmatic enable from `multiScenarioActionCreators`; `INITIALIZE_PAYMENT_GRID_FROM_TEMPLATE` covers a
 * template that carries a grid. FlexBuy is Finance-only, so it is deliberately not included.
 */
const isPaymentGridEnablingAction = (action: PaymentGridMiddlewareAction): boolean =>
    (action.type === types.UPDATED_PAYMENT_GRID_TOGGLE && action.payload === true) ||
    action.type === types.INITIALIZE_PAYMENT_GRID_FROM_TEMPLATE;

/**
 * Offer type the enabling action targets. Mirrors the reducer in `paymentGridHandlers`, which defaults a
 * template-driven enable to FINANCE when the payload carries no offer type.
 */
const getPaymentGridEnablingOfferType = (action: PaymentGridMiddlewareAction, currentOfferType: OfferType): OfferType =>
    action.type === types.INITIALIZE_PAYMENT_GRID_FROM_TEMPLATE
        ? (action.payload?.offerType as OfferType) || FINANCE
        : currentOfferType;

const shouldResetPaymentGrid = (isFlexBuy: boolean, isPaymentGridEnabled: boolean, paymentGridEnabledToggle: boolean) => {
    return (isFlexBuy && !isPaymentGridEnabled) || (!isFlexBuy && (!isPaymentGridEnabled || !paymentGridEnabledToggle));
};

const GRID_ROWS_HELD_FOR_MAIN_DEAL_META = 'gridRowsHeldForMainDeal';
const GRID_ROWS_HELD_FOR_MAIN_DEAL_SCOPE_META = 'gridRowsHeldForMainDealScope';

type RowsHeldForMainDeal = {
    token: number;
    requested: boolean;
    rows: PaymentGridMiddlewareAction[];
    store: PaymentGridMiddlewareStore;
};

const rowsHeldForMainDealByStore = new WeakMap<object, Map<string, RowsHeldForMainDeal>>();
let lastMainDealToken = 0;

const realStoreOf = (store: PaymentGridMiddlewareStore): object => (store as { __realStore?: object }).__realStore ?? store;
const scopeIdOf = (store: PaymentGridMiddlewareStore): string => store.getState()?.__scopeId ?? '';

const getRowsHeldForMainDeal = (
    store: PaymentGridMiddlewareStore,
    scopeId: string = scopeIdOf(store)
): RowsHeldForMainDeal | undefined => {
    const bySlot = rowsHeldForMainDealByStore.get(realStoreOf(store));
    return bySlot?.size ? bySlot.get(scopeId) : undefined;
};

const setRowsHeldForMainDeal = (store: PaymentGridMiddlewareStore, held: RowsHeldForMainDeal): void => {
    const realStore = realStoreOf(store);
    const bySlot = rowsHeldForMainDealByStore.get(realStore) ?? new Map<string, RowsHeldForMainDeal>();
    bySlot.set(scopeIdOf(store), held);
    rowsHeldForMainDealByStore.set(realStore, bySlot);
};

const clearRowsHeldForMainDeal = (store: PaymentGridMiddlewareStore, scopeId: string = scopeIdOf(store)): void => {
    const bySlot = rowsHeldForMainDealByStore.get(realStoreOf(store));
    if (bySlot?.size) bySlot.delete(scopeId);
};

const mainDealTokenOf = (action: PaymentGridMiddlewareAction | undefined): number | undefined =>
    action?.meta?.[GRID_ROWS_HELD_FOR_MAIN_DEAL_META];

const mainDealScopeOf = (action: PaymentGridMiddlewareAction | undefined): string | undefined =>
    action?.meta?.[GRID_ROWS_HELD_FOR_MAIN_DEAL_SCOPE_META];

const observeMainDealRequest = (store: PaymentGridMiddlewareStore, action: PaymentGridMiddlewareAction): void => {
    if (action.type !== types.PAYMENT_REQUESTED) return;
    const held = getRowsHeldForMainDeal(store, mainDealScopeOf(action.meta?.originalAction));
    if (held && mainDealTokenOf(action.meta?.originalAction) === held.token) {
        held.requested = true;
    }
};

const MAIN_DEAL_OUTCOMES: ReadonlySet<string> = new Set([
    types.PAYMENT_RECEIVED,
    types.PAYMENT_FAILURE,
    types.PAYMENT_ABORTED,
    types.PAYMENT_GRID_ROW_SUPERSEDED
]);

const settleRowsHeldForMainDeal = (store: PaymentGridMiddlewareStore, action: PaymentGridMiddlewareAction): void => {
    const stampedScope = mainDealScopeOf(action.meta?.originalAction);
    const held = getRowsHeldForMainDeal(store, stampedScope);
    if (!held) return;
    if (action.type === types.RESET_PAYMENT_GRID || isPaymentGridToggleAction(action.type)) {
        clearRowsHeldForMainDeal(store, stampedScope);
        return;
    }
    if (!MAIN_DEAL_OUTCOMES.has(action.type) || mainDealTokenOf(action.meta?.originalAction) !== held.token) return;
    clearRowsHeldForMainDeal(store, stampedScope);
    const state = held.store.getState();
    const settledOnProgramQuotes =
        action.type === types.PAYMENT_RECEIVED &&
        !action.meta?.isStalePaymentResponse &&
        !dealPricesOnUserProgramQuotes(state, getCurrentOfferType(state));
    if (settledOnProgramQuotes) {
        held.rows.forEach((row) => held.store.dispatch(row));
    }
};

// Action handlers
const handlePaymentAction = (
    store: PaymentGridMiddlewareStore,
    next: PaymentGridMiddlewareNext,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType,
    invalidatesProgram?: (action: PaymentGridMiddlewareAction) => boolean
) => {
    const state = store.getState();
    const currentTermMonth = getSelectedTermMonths(state, offerType);
    const currentDownPayment = getDownPayment(state, offerType)!;
    if (!isNumber(currentTermMonth)) return false; // returning false if currentTermMonth is undefined, typescript type guard

    if (action.type === types.UPDATED_SELL_RATE_OVERRIDE) {
        const { months, sellRate } = action.payload;
        // The engine the EDIT goes out on, which is this column's rather than the deal's: a fallen-back column prices
        // on user-program-quotes while the deal still prices within its program, and `handleRegularPaymentGrid` splits
        // the row on exactly this read. Deal-wide, the reset was skipped for the one engine it exists for.
        const editIsUserProgramQuotes = programBackedEditsStay(state, offerType)
            ? isUserProgramQuotesColumn(
                  getPaymentGridStuckColumnOrigin(state, offerType, months) ??
                      getPaymentGridColumnSources(state, offerType)[months],
                  isGridUserProgramQuotes(state, offerType)
              )
            : dealPricesOnUserProgramQuotes(state, offerType);

        // reset buy rate to sell rate when we change sell rate inside payment grid for UPQ deals
        if (editIsUserProgramQuotes) {
            store.dispatch(
                updateBuyRateOverride(sellRate, months, {
                    isSelectedTerm: months === currentTermMonth,
                    withPayment: false
                })
            );
        }
    }

    const isFlexBuy = isFlexBuyPayment(state);
    const paymentGridEnabledToggle = getPaymentGridToggle(state, offerType);
    const isFlexBuyPaymentGridEnabled = getFlexBuyPaymentGridToggle(state);

    if (paymentGridEnabledToggle && !isFlexBuy) {
        // Regular payment grid: calls next() inside forEach, return to exit middleware
        handleRegularPaymentGrid(store, next, action, offerType, currentTermMonth, currentDownPayment, invalidatesProgram);
        return true; // Signal that we should return from middleware (next already called)
    } else if (isFlexBuyPaymentGridEnabled) {
        // FlexBuy: only dispatches, doesn't call next(), return false to continue to next(action)
        handleFlexBuyPaymentGrid(store, action, state);
        return false; // Signal to continue (next not yet called)
    }

    return false; // Default: continue to next(action)
};

/**
 * Re-mark the columns the user has priced, from the overrides the deal still holds.
 *
 * `userPricedTerms` is session state cleared with the grid, while the per-column rate and residual it refers to
 * persist on the deal. Without this a reopened grid read those columns as merely "having overrides" rather than
 * user-priced, routed them back to program-quotes, and that engine cannot honour what the user typed (DE762937).
 *
 * Keyed off `overridesByTerm` rather than the grid's axes because this runs before the build assigns them, and
 * judged by the same two narrow predicates the rest of the grid uses - a record holding only `adjustedResidual*`
 * from a reopened deal is not a user price.
 *
 * The selected term is excluded. For every other column a per-term record can only have come from the grid, but the
 * deal screen's Rate and Base Residual fields write the selected term's record as a matter of course, so its presence
 * says nothing about the grid. Seeding it made a deal-screen rate outrank the column's pinned program, since
 * `intendedSource` tests the marker before `hasResolvedProgram`. An edit made in the grid this session is unaffected:
 * `handleRegularPaymentGrid` marks the edited column directly.
 */
const seedUserPricedTermsFromOverrides = (store: PaymentGridMiddlewareStore, state: State, offerType: OfferType): void => {
    if (!isLeaseRedesign(state, offerType)) return;

    if (programBackedEditsStay(state, offerType)) return;

    const overridesByTerm = getOfferDetails(state, offerType)?.overridesByTerm || {};
    const selectedTermMonths = getSelectedTermMonths(state, offerType);

    Object.keys(overridesByTerm).forEach((key) => {
        // `Number` yields NaN for a non-numeric key, which `overridesByTerm` can carry - see the note on the raw-key
        // read in `paymentPayloadUtils`. A typeof check would be dead here: `Number` always returns a number.
        const months = Number(key);
        if (Number.isNaN(months)) return;
        if (months === selectedTermMonths) return;
        if (!hasUserSuppliedGridSellRate(state, offerType, months) && !hasUserSuppliedGridResidual(state, offerType, months)) {
            return;
        }

        store.dispatch(setPaymentGridUserPricedTerm(offerType, months));
    });
};

const gridWrittenHalfFor = (actionType: string): 'rate' | 'residual' =>
    actionType === types.UPDATED_SELL_RATE_OVERRIDE ? 'rate' : 'residual';

const handleRegularPaymentGrid = (
    store: PaymentGridMiddlewareStore,
    next: PaymentGridMiddlewareNext,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType,
    currentTermMonth: number,
    currentDownPayment: number,
    invalidatesProgram?: (action: PaymentGridMiddlewareAction) => boolean
) => {
    const state = store.getState();
    clearRowsHeldForMainDeal(store);

    // A row spanning two engines goes out as two calls, since a request carries only one.
    const isRedesignRow = isAspPgRedesignEnabled(state, offerType);

    // A residual override is per-term once the redesign is on, so only that column needs repricing —
    // the same narrowing sell rate already does. While the toggle is off residual is a single deal-level
    // value that affects every term, so the whole grid must still be requested.
    // Taking one back is per-term for the same reason, and narrowing it matters more: the column reverts to the
    // lender's residual, and repricing the whole grid to deliver that would re-quote columns nothing changed for.
    const isPerTermResidualOverride =
        isAspPaymentGridRedesignEnabled(state) &&
        (action.type === types.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE ||
            action.type === types.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE ||
            action.type === types.REMOVED_PROGRAM_RESIDUAL_OVERRIDE);
    const isSingleTermOverride = action.type === types.UPDATED_SELL_RATE_OVERRIDE || isPerTermResidualOverride;
    /**
     * Every payment action lands here while the grid is open and each row is stamped `gridAction`, so that flag
     * cannot mean "the user acted in the grid": the grid's own inputs and cells carry a grid user intent, and
     * nothing on the deal screen does. Shared below so the two checks can't drift apart.
     */
    const isGridCellOriginated = PAYMENT_GRID_USER_INTENTS.has(action.meta?.userIntent as string);

    const reviveTerms = !isRedesignRow
        ? []
        : isSingleTermOverride
        ? [action.payload.months as number].filter(
              (termMonths) =>
                  getPaymentGridStuckColumnOrigin(state, offerType, termMonths) != null &&
                  !isSharedTermSibling(state, offerType, action.meta?.gridColumnIndex as number | undefined, termMonths)
          )
        : isGridCellOriginated
        ? []
        : getUniquePaymentGridTerms(state, offerType);
    const revived = reviveTerms.length ? reviveStuckColumns(store, state, offerType, reviveTerms) : undefined;
    const recordedColumnSources = isRedesignRow ? { ...getPaymentGridColumnSources(state, offerType), ...revived?.reDecided } : {};

    const editedColumnTerm =
        isLeaseRedesign(state, offerType) &&
        ((action.type === types.UPDATED_SELL_RATE_OVERRIDE && action.meta?.userIntent === PAYMENT_GRID_SELL_RATE_INTENT) ||
            ((action.type === types.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE ||
                action.type === types.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE) &&
                action.meta?.userIntent === PAYMENT_GRID_RESIDUAL_INTENT)) &&
        isNumber(action.payload?.months)
            ? (action.payload.months as number)
            : undefined;

    const editedGridColumnIndex = action.meta?.gridColumnIndex as number | undefined;
    const isEditedColumnSibling =
        isNumber(editedColumnTerm) && isSharedTermSibling(state, offerType, editedGridColumnIndex, editedColumnTerm as number);
    const editedColumnVerdict = isNumber(editedColumnTerm)
        ? isEditedColumnSibling
            ? getColumnVerdict(state, offerType, editedGridColumnIndex as number, editedColumnTerm as number)
            : recordedColumnSources[editedColumnTerm as number]
        : undefined;

    const editStaysOnProgramQuotes =
        isNumber(editedColumnTerm) &&
        programBackedEditsStay(state, offerType) &&
        !isUserProgramQuotesColumn(editedColumnVerdict, isGridUserProgramQuotes(state, offerType));

    /**
     * A rate typed into a program-backed Lease column moves it to user-program-quotes, recorded here rather than in the
     * resolution pass so the payload, the engine split and the rate all follow by construction. `Unavailable` keeps its
     * verdict; an undecided column is left to the resolution pass.
     *
     * Regardless of the Auto Select checkbox (DE762937). A figure the user types IS an unsolicited quote, and
     * program-quotes cannot express one: with the column's program pinned on that engine, Payment Services prices from
     * the program's own residual table and returns it, so a typed residual was discarded and the cell repainted with
     * the program's figure. Proven on a captured trace - the request carried `baseResidualValue: 59` and the response
     * answered `baseResidualPercent: 62` off the pinned program. Only the edited column moves; every other column keeps
     * its program on program-quotes, and the row goes out as two calls (`splitTermsByEngine`).
     */
    const userPricedTerm = editStaysOnProgramQuotes ? undefined : editedColumnTerm;

    const shouldRepointToUserPriced = isNumber(userPricedTerm) && editedColumnVerdict === PaymentGridSource.ProgramQuotes;

    if (isNumber(userPricedTerm) && !isEditedColumnSibling) {
        store.dispatch(setPaymentGridUserPricedTerm(offerType, userPricedTerm as number));
    }
    if (isNumber(editedColumnTerm) && editWritesTermSlot(state, offerType, editedGridColumnIndex, editedColumnTerm as number)) {
        store.dispatch(setPaymentGridWrittenTerm(offerType, editedColumnTerm as number, gridWrittenHalfFor(action.type)));
    }
    if (shouldRepointToUserPriced) {
        store.dispatch(
            isEditedColumnSibling
                ? setPaymentGridDecidedColumns(offerType, {
                      [editedGridColumnIndex as number]: PaymentGridSource.UserProgramQuotes
                  })
                : setPaymentGridColumnSources(offerType, { [userPricedTerm as number]: PaymentGridSource.UserProgramQuotes })
        );
    }

    // Read from the merged object rather than re-reading state, so the engine split below cannot depend on
    // whether the dispatch above has been applied yet.
    const columnSources =
        shouldRepointToUserPriced && !isEditedColumnSibling
            ? { ...recordedColumnSources, [userPricedTerm as number]: PaymentGridSource.UserProgramQuotes }
            : recordedColumnSources;

    // One id for every row this edit fans out - see nextGridRoundId.
    const gridRoundId = nextGridRoundId();

    // Whether this fan-out is the grid multiplying a DEAL-SCREEN edit.
    const isDealScreenOriginated = isLeaseRedesign(state, offerType) && !isGridCellOriginated;

    const dealRowIndices = gridRowsFor(state, offerType)
        .filter(({ downPayment }) => downPayment === currentDownPayment)
        .map(({ rowIndex }) => rowIndex);
    const resolvedMainRow = getResolvedPaymentGridSelectedRowIndex(state, offerType);
    const mainRowIndex = dealRowIndices.includes(resolvedMainRow as number) ? resolvedMainRow : dealRowIndices[0];
    const parkedDealTerm =
        isRedesignRow && !isSingleTermOverride && !getUniquePaymentGridTerms(state, offerType).includes(currentTermMonth)
            ? currentTermMonth
            : undefined;

    // DE774239: the MODIFY_MANUAL_INCENTIVE reducer ignores every row but the main one, so a row priced before
    // the main row reaches next() reads the rebate total from before the edit. Hold those rows back until it has.
    const waitsForMainDeal =
        isGridIncentiveManagementEnabled(state, offerType) && !isGridCellOriginated && Boolean(invalidatesProgram?.(action));
    const holdRowsUntilMainApplied =
        (action.type === types.MODIFY_MANUAL_INCENTIVE && isGridManualRebateMainRowFirstEnabled(state)) ||
        (isGridIncentiveManagementEnabled(state, offerType) && isIncentiveChangeActionType(action.type)) ||
        isGridKeptOpenOnReapply(action) ||
        waitsForMainDeal;
    const mainDealToken = waitsForMainDeal ? ++lastMainDealToken : undefined;
    const heldRowActions: PaymentGridMiddlewareAction[] = [];
    let mainApplied = false;
    let forwardedMain: PaymentGridMiddlewareAction | undefined;
    let heldForMainDeal: RowsHeldForMainDeal | undefined;
    const dispatchRow = (rowAction: PaymentGridMiddlewareAction) => {
        if (heldForMainDeal) {
            heldForMainDeal.rows.push(rowAction);
            return;
        }
        if (holdRowsUntilMainApplied && !mainApplied) {
            heldRowActions.push(rowAction);
            return;
        }
        store.dispatch(rowAction);
    };
    const releaseHeldRows = () => {
        if (mainApplied) return;
        mainApplied = true;
        const pending = mainDealToken !== undefined ? getRowsHeldForMainDeal(store) : undefined;
        if (pending && pending.token === mainDealToken) {
            if (pending.requested && forwardedMain?.meta?.[PROGRAM_QUOTES_REFETCH_META]) {
                heldForMainDeal = pending;
            } else {
                clearRowsHeldForMainDeal(store);
            }
        }
        heldRowActions.splice(0).forEach(dispatchRow);
    };
    const forwardMain = (mainRowAction: PaymentGridMiddlewareAction) => {
        forwardedMain = mainRowAction;
        if (mainDealToken !== undefined) {
            setRowsHeldForMainDeal(store, { token: mainDealToken, requested: false, rows: [], store });
            mainRowAction.meta = {
                ...mainRowAction.meta,
                [GRID_ROWS_HELD_FOR_MAIN_DEAL_META]: mainDealToken,
                [GRID_ROWS_HELD_FOR_MAIN_DEAL_SCOPE_META]: scopeIdOf(store)
            };
        }
        const result = next(mainRowAction);
        releaseHeldRows();
        return result;
    };

    gridRowsFor(state, offerType).forEach(({ rowIndex, downPayment }) => {
        const isMainPayment = rowIndex === mainRowIndex;

        // add possibility to highlight input with error after payment grid closed
        const mainPaymentActionType = isMainPayment && isSingleTermOverride ? action.type : undefined;

        const rowTermList = isSingleTermOverride
            ? [action.payload.months]
            : termsPricedByReappliedProgram(
                  state,
                  offerType,
                  action,
                  getUniquePaymentGridTerms(state, offerType),
                  currentTermMonth
              );
        const termList = isMainPayment && parkedDealTerm != null ? [...new Set([...rowTermList, parkedDealTerm])] : rowTermList;

        // Shared with handleUpdatePaymentGridCashDown so a row edit and a cash-down edit cannot disagree on engines.
        // Resolved before `buildRowAction` so the round's own term list can be stamped on every call it fans out.
        const {
            priceableTerms: priceableTermList,
            programQuotesTerms,
            userProgramQuotesTerms,
            isRowSplit: spansBothEngines
        } = splitTermsByEngine({
            termList,
            columnSources: isRedesignRow
                ? withSharedTermsJudgedByHolder(store.getState(), offerType, columnSources)
                : columnSources,
            dealIsUserProgramQuotes: isGridUserProgramQuotes(state, offerType),
            // A typed residual is a targeted column edit as much as a typed rate: it is the retry path for a
            // column the lender never priced, whose source is `Unavailable`. Without it that term is filtered
            // out of `priceableTerms`, the row goes out with an empty `termList`, and the residual the user
            // just typed into the blank cell never prices at all (DE762937 issues 3 and 5).
            isTargetedColumnEdit: action.type === types.UPDATED_SELL_RATE_OVERRIDE || isPerTermResidualOverride
        });
        const isRowSplit = isRedesignRow && spansBothEngines;
        const leasePlan =
            isRedesignRow && isLeaseRedesign(state, offerType) && action.meta?.gridColumnIndex === undefined
                ? planLeaseColumnsByOwnedOverride(
                      store.getState(),
                      offerType,
                      priceableTermList,
                      isSingleTermOverride ? (action.payload.months as number) : undefined
                  )
                : undefined;
        const fanOutRoundColumns = leasePlan ? leaseRowPlanColumns(leasePlan) : undefined;

        const buildRowAction = (
            terms: number[],
            isMain: boolean,
            forceUserProgramQuotes: boolean,
            isSplitPart: boolean,
            columnIndices?: number[],
            columnSellRate?: number,
            ignoreTermOverride?: boolean,
            columnResidualInfo?: GenericObject
        ) => {
            const rowAction = addPaymentGridMeta(
                action,
                withLeaseProgramPin(state, offerType, {
                    isMainPayment: isMain,
                    mainPaymentActionType: isMain ? mainPaymentActionType : undefined,
                    gridRoundId,
                    // Every term this fan-out asks about, so a split row's verdict covers both halves rather than
                    // only the one that settled last - see AddPaymentGridMetaOptions.gridRoundTerms.
                    ...(isRedesignRow && { gridRoundTerms: priceableTermList }),
                    ...(fanOutRoundColumns && { gridRoundColumns: fanOutRoundColumns }),
                    dealScreenOriginated: isDealScreenOriginated,
                    [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                        [offerType]: {
                            termList: terms,
                            downPayment
                        }
                    }
                })
            );
            // A deal-screen single-value override on a redesign row: createPaymentOverridesForGenericCalcs only
            // ever consults this flag once it already knows the redesign is active (the legacy grid excludes
            // nothing from that fallback in the first place), so it's only worth stamping here too.
            // forceUseGenericCalcs below does not reliably tell a real override apart from a genuine grid column
            // (it depends on offer type and Auto Select state), whereas edit origin does - the grid's own
            // rate/residual cells are the ones that must keep deriving their own figure from the engine.
            if (isSingleTermOverride && isRedesignRow && !isGridCellOriginated) {
                rowAction.meta = {
                    ...rowAction.meta,
                    isDealScreenSingleTermOverride: true
                };
            }
            if (isRedesignRow) {
                rowAction.meta = {
                    ...rowAction.meta,
                    forceUseGenericCalcs: forceUserProgramQuotes,
                    ...(isSplitPart && { isGridRowSplitPart: true }),
                    gridColumnIndices: columnIndices ?? coveredColumnIndices(state, offerType, terms),
                    gridRowIndices: [rowIndex],
                    ...(terms.some((term) => revived?.restoredColumnPrograms[term]) && {
                        restoredColumnPrograms: terms.reduce<Record<number, GenericObject>>((programs, term) => {
                            if (revived?.restoredColumnPrograms[term]) programs[term] = revived.restoredColumnPrograms[term];
                            return programs;
                        }, {})
                    }),
                    ...columnScopedMeta(
                        state,
                        offerType,
                        terms,
                        columnIndices,
                        columnSellRate,
                        columnResidualInfo,
                        ignoreTermOverride ??
                            (columnIndices?.length === 1 && shouldIgnoreTermFigure(state, offerType, columnIndices[0], 'sellRate'))
                    )
                };
            }
            return rowAction;
        };

        const editedColumnIndex = isRedesignRow ? (action.meta?.gridColumnIndex as number | undefined) : undefined;
        if (editedColumnIndex !== undefined) {
            const editedTerm = getPaymentGridTerms(state, offerType)[editedColumnIndex];
            if (isNumber(editedTerm) && priceableTermList.includes(editedTerm as number)) {
                const editedColumnSources = isSharedTermSibling(state, offerType, editedColumnIndex, editedTerm as number)
                    ? {
                          [editedTerm as number]: shouldRepointToUserPriced
                              ? PaymentGridSource.UserProgramQuotes
                              : (getColumnVerdict(state, offerType, editedColumnIndex, editedTerm as number) as PaymentGridSource)
                      }
                    : columnSources;
                const { userProgramQuotesTerms: columnUpqTerms } = splitTermsByEngine({
                    termList: [editedTerm as number],
                    columnSources: editedColumnSources,
                    dealIsUserProgramQuotes: isGridUserProgramQuotes(state, offerType),
                    isTargetedColumnEdit: true
                });
                const columnAction = buildRowAction([editedTerm as number], isMainPayment, Boolean(columnUpqTerms.length), false, [
                    editedColumnIndex
                ]);
                const ownOverride = getOwnColumnOverride(state, offerType, editedColumnIndex, editedTerm as number);
                if (ownOverride) {
                    columnAction.meta = {
                        ...columnAction.meta,
                        ...(ownOverride.sellRate != null && { columnSellRate: ownOverride.sellRate }),
                        ...(ownOverride.residualInfo != null && { columnResidualInfo: ownOverride.residualInfo }),
                        ...(isTermDuplicated(getPaymentGridTerms(state, offerType), editedTerm as number) && {
                            columnScopedForDuplicateTerm: true
                        })
                    };
                }
                if (isMainPayment) return forwardMain(columnAction);
                dispatchRow(columnAction);
                return;
            }
            if (!isMainPayment) return;
        }

        // Nothing priceable here: only secondary rows are skipped - the main payment must still reach next(), or the deal is stranded.
        if (isRedesignRow && !priceableTermList.length && !isMainPayment) return;

        const selectedColumnIndex = isRedesignRow ? getPaymentGridDealColumnIndex(state, offerType) : undefined;
        let mainAction: PaymentGridMiddlewareAction | undefined;

        if (leasePlan) {
            const isPlanSplit = isLeaseRowPlanSplit(leasePlan);
            const isParkedMain = isMainPayment && parkedDealTerm != null;
            const planMain = isMainPayment && !isParkedMain ? leaseRowPlanMain(leasePlan, selectedColumnIndex) : undefined;
            const mainColumnCall = planMain?.columnCall;
            const isProgramMain = Boolean(planMain?.isProgramBatch);
            const isUpqBatchMain = Boolean(planMain?.isUpqBatch);
            const sendPlanned = (rowAction: PaymentGridMiddlewareAction, isMain: boolean) => {
                if (isMain) mainAction = rowAction;
                else dispatchRow(rowAction);
            };

            leasePlan.columnCalls.forEach((call) =>
                sendPlanned(
                    buildRowAction(
                        [call.term],
                        call === mainColumnCall,
                        call.forceUserProgramQuotes,
                        isPlanSplit,
                        [call.columnIndex],
                        call.sellRate,
                        call.ignoreTermOverride,
                        call.residualInfo
                    ),
                    call === mainColumnCall
                )
            );
            if (leasePlan.upqTerms.length) {
                sendPlanned(
                    buildRowAction(leasePlan.upqTerms, isUpqBatchMain, true, isPlanSplit, leasePlan.upqColumns),
                    isUpqBatchMain
                );
            }
            if (leasePlan.programTerms.length) {
                sendPlanned(
                    buildRowAction(leasePlan.programTerms, isProgramMain, false, isPlanSplit, leasePlan.programColumns),
                    isProgramMain
                );
            }
            if (isParkedMain) {
                sendPlanned(
                    buildRowAction([parkedDealTerm], true, isGridUserProgramQuotes(state, offerType), isPlanSplit, []),
                    true
                );
            }
            if (mainAction) return forwardMain(mainAction);
            return;
        }

        for (const call of planGridRowCalls(
            { priceableTerms: priceableTermList, programQuotesTerms, userProgramQuotesTerms },
            { isRowSplit, isMainPayment, currentTermMonth }
        )) {
            const { sharedTerms, columnCalls } = isRedesignRow
                ? splitTermsByColumn(state, offerType, call.terms, call.forceUserProgramQuotes)
                : { sharedTerms: call.terms, columnCalls: [] as ColumnScopedCall[] };

            const mainColumnCall = call.isMain
                ? columnCalls.find(({ columnIndex }) => columnIndex === selectedColumnIndex) ??
                  (sharedTerms.length ? undefined : columnCalls[0])
                : undefined;

            columnCalls.forEach((columnCall) => {
                if (columnCall === mainColumnCall) return;
                dispatchRow(
                    buildRowAction(
                        [columnCall.term],
                        false,
                        call.forceUserProgramQuotes,
                        call.isSplitPart,
                        [columnCall.columnIndex],
                        columnCall.sellRate,
                        columnCall.ignoreTermOverride,
                        columnCall.residualInfo
                    )
                );
            });

            if (sharedTerms.length) {
                const isSharedMain = call.isMain && !mainColumnCall;
                const sharedAction = buildRowAction(sharedTerms, isSharedMain, call.forceUserProgramQuotes, call.isSplitPart);
                if (isSharedMain) mainAction = sharedAction;
                else dispatchRow(sharedAction);
            }

            if (mainColumnCall) {
                mainAction = buildRowAction(
                    [mainColumnCall.term],
                    true,
                    call.forceUserProgramQuotes,
                    call.isSplitPart,
                    [mainColumnCall.columnIndex],
                    mainColumnCall.sellRate,
                    mainColumnCall.ignoreTermOverride,
                    mainColumnCall.residualInfo
                );
            }
        }

        if (mainAction) return forwardMain(mainAction);
    });
    // No row matched the deal's cash down, so none carries isMainPayment and the reducer would drop the edit on
    // every row. Let the action itself through once to record it, then price the held rows off the new total.
    if (holdRowsUntilMainApplied && !mainApplied) {
        forwardMain(action);
        return;
    }
    releaseHeldRows();
};

const handleFlexBuyPaymentGrid = (store: PaymentGridMiddlewareStore, action: PaymentGridMiddlewareAction, state: State): any => {
    const downPaymentList = getFlexBuyPaymentGridCashDowns(state);
    const termMonthsList = getFlexBuyPaymentGridTerms(state);
    const flexBuyPercentsList = getFlexBuyPaymentGridPercents(state);

    const termList = action.type === types.UPDATED_SELL_RATE_OVERRIDE ? [action.payload.months] : flexBuyTermList;

    const commonPayload = {
        flexBuyPercents: flexBuyPercentsList,
        flexBuyTerms: termMonthsList,
        flexBuyDownPayments: downPaymentList
    };

    const updatedAction = addFlexBuyPaymentGridMeta(action, {
        [PAYMENT_REQUEST_OVERRIDE_NODE]: {
            finance: {
                termList,
                ...commonPayload
            }
        }
    });

    // For FlexBuy, dispatch the action and return null so next() uses original action
    // This matches the expected behavior in tests where FlexBuy actions are dispatched, not passed to next()
    store.dispatch(updatedAction);
    return null;
};

/**
 * Points the deal-level residual at the newly selected term (DE763764). Every unit the record holds a figure for is
 * written, so no slot is left describing the term just left whichever unit a reader picks; each type is resolved from
 * its own fields and travels with them.
 */
const residualPayloadForTerm = (state: State, offerType: OfferType, termMonth: number) => {
    const residual = getResidualInfoOverrideByOfferTypeTerm(state, offerType, termMonth, true);
    if (isLeaseResidualMissing(residual)) return undefined;

    const unitType = resolveCompleteResidualUnit(residual);
    if (!unitType) return undefined;

    const adjustedUnitType = resolveResidualType(
        residual?.adjustedResidualType,
        residual?.adjustedResidualPercent,
        residual?.adjustedResidualAmount
    );

    // A term with no program residual of its own takes it from its own base, the way a base-residual edit does
    // (`residualHandlers`). These are the slots the Base Residual field and `programResidualValue` read, so leaving
    // them unwritten keeps the term just left on screen and in the payload.
    const programResidual = residual?.programResidual ?? residual?.baseResidualAmount;
    const programResidualPercent = residual?.programResidualPercent ?? residual?.baseResidualPercent;

    return {
        unitType,
        ...(adjustedUnitType != null && { adjustedUnitType }),
        ...(residual?.baseResidualAmount != null && { baseResidualAmount: residual.baseResidualAmount }),
        ...(residual?.baseResidualPercent != null && { baseResidualPercent: residual.baseResidualPercent }),
        ...(programResidual != null && { programResidual }),
        ...(programResidualPercent != null && { programResidualPercent }),
        ...(residual?.adjustedResidualAmount != null && { adjustedResidualAmount: residual.adjustedResidualAmount }),
        ...(residual?.adjustedResidualPercent != null && { adjustedResidualPercent: residual.adjustedResidualPercent })
    };
};

const seedResidualForIncomingTerm = (
    store: PaymentGridMiddlewareStore,
    state: State,
    offerType: OfferType,
    incomingTermMonth: number,
    currentTermMonth: number | undefined
) => {
    if (!isLeaseRedesign(state, offerType) || !getPaymentGridToggle(state, offerType)) return;
    if (!dealPricesOnUserProgramQuotes(state, offerType)) return;
    if (incomingTermMonth === currentTermMonth && !isTermDuplicated(getPaymentGridTerms(state, offerType), incomingTermMonth)) {
        return;
    }

    const residualPayload = residualPayloadForTerm(state, offerType, incomingTermMonth);
    if (!residualPayload) return;

    store.dispatch(updateResidualForSelectedTermUPQ(residualPayload, offerType));
};

const handleSelectedTermAction = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    state: State,
    offerType: OfferType
) => {
    const isFlexBuyPaymentGridEnabled = getFlexBuyPaymentGridToggle(state);
    const isFlexBuy = isFlexBuyPayment(state);
    const currentTermMonth = getSelectedTermMonths(state);
    const months = parseSelectedTermMonths(action.payload);

    if (!isNumber(currentTermMonth)) return;
    if (!isNumber(months)) return;

    // Seeded before next(), where the payload is built: otherwise an ASP-off call goes out on the previous cell's rate.
    if (isAspPgRedesignEnabled(state, offerType) && dealPricesOnUserProgramQuotes(state, offerType)) {
        const sharedTermRate = ownRateOnSharedTerm(
            state,
            offerType,
            getPaymentGridSelectedColumnIndex(state, offerType),
            months as number,
            getTerm(state, action.payload)
        );
        const incomingCellSellRate = sharedTermRate
            ? sharedTermRate.sellRate
            : getSellRateOverrideByOfferTypeAndTerm(state, offerType, months, true, true);
        // 0 means the column has no rate of its own, so it uses the active cell's (AC-OFF-3).
        if (incomingCellSellRate) {
            store.dispatch(
                updateSellRateForSelectedTermUPQ(
                    incomingCellSellRate,
                    sharedTermRate ? sharedTermRate.rateType : getPaymentGridTermRateType(state, offerType, months)
                )
            );
        }
    }

    seedResidualForIncomingTerm(store, state, offerType, months, currentTermMonth);

    if (!shouldUpdateBuyRates(state, currentTermMonth, months, offerType, isFlexBuy, isFlexBuyPaymentGridEnabled)) {
        return;
    }

    const futureTerm = getTermByOfferTypeAndMonths(state, offerType, months);
    const buyRateOverride = getBuyRateOverrideByOfferTypeAndTerm(state, offerType, months, false);

    if (buyRateOverride) {
        store.dispatch(updateBuyRateForSelectedTermUPQ(buyRateOverride, getPaymentGridTermRateType(state, offerType, months)));
    } else {
        // Typeguard condition
        if (isUndefined(futureTerm)) return;
        store.dispatch(
            updateBuyRateOverride(futureTerm.sellRate, months, {
                isSelectedTerm: true,
                withPayment: false
            })
        );
    }
};

const ownRateOnSharedTerm = (
    state: State,
    offerType: OfferType,
    columnIndex: number | undefined,
    termMonths: number | undefined,
    cellRecord: { sellRate?: number; rateType?: string } | undefined
): { sellRate?: number; rateType?: typeof RATE_TYPE.APR | typeof RATE_TYPE.MoneyFactor } | undefined => {
    const gridTerms = getPaymentGridTerms(state, offerType);
    if (
        !isAspPgRedesignEnabled(state, offerType) ||
        columnIndex == null ||
        !isNumber(termMonths) ||
        gridTerms[columnIndex] !== termMonths ||
        !isTermDuplicated(gridTerms, termMonths as number)
    ) {
        return undefined;
    }
    const ownOverride = getOwnColumnOverride(state, offerType, columnIndex, termMonths as number);
    const source = ownOverride?.sellRate != null ? ownOverride : cellRecord;
    const rateType = source?.rateType;
    return {
        sellRate: source?.sellRate,
        rateType: offerType === LEASE && (rateType === RATE_TYPE.APR || rateType === RATE_TYPE.MoneyFactor) ? rateType : undefined
    };
};

const handleSelectedTermActionAfterNext = (
    store: PaymentGridMiddlewareStore,
    state: State,
    updatedState: State,
    offerType: OfferType
) => {
    const isFlexBuy = isFlexBuyPayment(updatedState);
    const isFlexBuyPaymentGridEnabled = getFlexBuyPaymentGridToggle(state);

    if (!isUserProgramQuotesWithPaymentGrid(updatedState, state, offerType, isFlexBuy, isFlexBuyPaymentGridEnabled)) {
        return;
    }

    const currentTermMonth = getSelectedTermMonths(updatedState);
    const termData = Boolean(getSelectedTerm(updatedState));

    const sharedTermRate = ownRateOnSharedTerm(
        updatedState,
        offerType,
        getPaymentGridDealColumnIndex(updatedState, offerType),
        currentTermMonth,
        getSelectedTerm(updatedState, offerType)
    );
    const sellRateOverride = sharedTermRate
        ? sharedTermRate.sellRate
        : getSellRateOverrideByOfferTypeAndTerm(updatedState, offerType, currentTermMonth, termData, termData);

    if (sellRateOverride) {
        store.dispatch(
            updateSellRateForSelectedTermUPQ(
                sellRateOverride,
                sharedTermRate
                    ? sharedTermRate.rateType
                    : getPaymentGridTermRateType(updatedState, offerType, currentTermMonth as number)
            )
        );
    }
};

const termsTypedInGridAtClose = (state: State, offerType: OfferType): number[] => {
    const gridData = getOfferDetails(state, offerType)?.paymentGridData as GridPaymentGridData | undefined;
    const dealTermMonths = getSelectedTermMonths(state, offerType);
    const typedTerms = Object.keys(overridesTypedInGrid(gridData, dealTermMonths)).map(Number);
    const writtenTerms = Object.keys(gridData?.gridWrittenTerms ?? {})
        .map(Number)
        .filter((termMonths) => termMonths !== dealTermMonths);
    return [...new Set([...typedTerms, ...writtenTerms])];
};

const handlePaymentGridToggleAction = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    offerType: OfferType,
    upqFallbackDisabled: boolean,
    resolveExplicitLock?: ExplicitLockResolver
) => {
    const paymentGridEnabledToggle = getPaymentGridToggle(updatedState, offerType);
    const isFlexBuy = isFlexBuyPayment(updatedState);
    const currentTermMonth = getSelectedTermMonths(updatedState);
    const isPaymentGridEnabled = action.payload;
    const isUPQ = gridAspIsUserProgramQuotes(updatedState, offerType);

    if (!isNumber(currentTermMonth)) return;

    if (shouldResetPaymentGrid(isFlexBuy, isPaymentGridEnabled, paymentGridEnabledToggle)) {
        if (isFlexBuy && !isPaymentGridEnabled) {
            store.dispatch(resetFlexBuyPaymentGridData());
        } else {
            // DE770996: Lease included. A grid edit must not move the deal's engine while the grid is open - the grid
            // carries it per call - but the close is the opposite case: the column's source dies with the grid, so the
            // deal has to own the engine that priced the selected cell. On Lease a rate or residual edit is the only
            // way in, since a cell selection already adopted it (`adoptSelectedCellEngine`).
            const isSelectedCellUserPriced =
                isAspPgRedesignEnabled(updatedState, offerType) &&
                !isUPQ &&
                !upqFallbackDisabled &&
                getPaymentGridColumnSources(updatedState, offerType)[currentTermMonth] === PaymentGridSource.UserProgramQuotes;

            // A fallback column's rate was never a deal override; without writing it the next call downgrades to program-quotes.
            const selectedCellSellRate = isSelectedCellUserPriced
                ? getSellRateOverrideByOfferTypeAndTerm(updatedState, offerType, currentTermMonth, true, true)
                : undefined;
            const selectedCellRateType = isSelectedCellUserPriced
                ? getPaymentGridTermRateType(updatedState, offerType, currentTermMonth)
                : undefined;

            // Lease needs the residual as well - `isUPQFieldsAvailable` requires both, where Finance requires only the
            // rate. Read before the reset for the same reason as the rate: the close takes the grid-written halves back
            // out of `overridesByTerm` (`withoutGridWrittenOverrides`).
            const selectedCellResidual =
                isSelectedCellUserPriced && isLeaseRedesign(updatedState, offerType)
                    ? residualPayloadForTerm(updatedState, offerType, currentTermMonth)
                    : undefined;

            const dropOverrides: DropOverrides = isGridAspOff(updatedState, offerType)
                ? true
                : isAspPgRedesignEnabled(updatedState, offerType)
                ? GRID_TYPED_OVERRIDES
                : false;
            const staleQuoteTerms = isAspPgRedesignEnabled(updatedState, offerType)
                ? termsTypedInGridAtClose(updatedState, offerType)
                : [];
            store.dispatch(resetPaymentGrid(offerType, dropOverrides, staleQuoteTerms));

            if (isSelectedCellUserPriced) {
                // Rate before engine, so the deal is never on user-program-quotes without one.
                if (isNumber(selectedCellSellRate)) {
                    store.dispatch(updateSellRateForSelectedTermUPQ(selectedCellSellRate, selectedCellRateType));
                }
                // No payment and no rebuild: the selected cell is already priced.
                store.dispatch(
                    updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes, offerType, {
                        withPayment: false,
                        skipGridRebuild: true
                    })
                );
                if (selectedCellResidual) {
                    store.dispatch(updateResidualForSelectedTermUPQ(selectedCellResidual, offerType));
                }
            }
        }
        return;
    }

    /**
     * An enable for a grid that is already built is a no-op. `shouldResetPaymentGrid` is false for it, so a second
     * `UPDATED_PAYMENT_GRID_TOGGLE(true)` started a whole second build. Judged on the axes rather than the toggle,
     * which is already true here. A restore is excluded outright: it hands the grid its axes, so it looks like a repeat.
     */
    const isAlreadyBuilt =
        isPaymentGridEnabled &&
        !action.meta?.preInitializedPaymentGrid &&
        Boolean(getPaymentGridTerms(updatedState, offerType).length);
    if (isLeaseRedesign(updatedState, offerType) && isAlreadyBuilt) return;

    const isFinance = offerType === FINANCE;
    const isLease = offerType === LEASE;
    const isBalloon = offerType === BALLOON;

    // Under the redesign a Lease deal keeps whichever engine it was already using; the grid carries the engine per
    // call. `isCalculationEndpoint` is a type guard on a literal constant and so always true - Balloon depends on it.
    if (
        (isLease || isBalloon) &&
        isCalculationEndpoint(CALC_ENDPOINTS.programQuotes) &&
        !isLeaseRedesign(updatedState, offerType) &&
        !dealerOptOutBlocksPqExcursion(updatedState)
    ) {
        store.dispatch(updateCurrentCalculationEndpoint(CALC_ENDPOINTS.programQuotes, offerType));
    }

    if (isFinance) {
        if (isCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes) && isFlexBuy) {
            store.dispatch(updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes, offerType));
        }

        const sellRate = getSellRateOverride(updatedState, false);
        const isUPQ = dealPricesOnUserProgramQuotes(updatedState, offerType);
        const shouldUpdateSellRate = isUPQ || isFlexBuy;
        if (!sellRate && shouldUpdateSellRate) {
            const termData = Boolean(getSelectedTerm(updatedState));
            const sellRateOverride = getSellRateOverrideByOfferTypeAndTerm(
                updatedState,
                offerType,
                currentTermMonth,
                true,
                termData
            );
            store.dispatch(
                updateSellRateForSelectedTermUPQ(
                    sellRateOverride,
                    getPaymentGridTermRateType(updatedState, offerType, currentTermMonth)
                )
            );
        }
    }

    // Before the build, so its very first calls route a column the user priced in an earlier session correctly.
    seedUserPricedTermsFromOverrides(store, updatedState, offerType);

    initializePaymentGridData(store, action, updatedState, offerType, resolveExplicitLock);

    /**
     * US2085313: a restored or re-priced grid must not drag the deal onto a cell the dealer never picked.
     *
     * `getNewSelectedCell` substitutes when the deal's term is not in the grid — the nearest cell in the
     * deal's cash-down row, else the nearest in its column, else the first non-empty one. Adopting that here
     * moves the deal's down payment and term. A grid rebuilt from stored data is priced *against* the deal,
     * so when the deal's term is genuinely absent the honest outcome is no highlight, not a moved deal.
     *
     * Scoped to this open, and only when the grid arrived pre-initialized: the two other callers of the
     * selector — the term-edit reselect and the post-`PAYMENT_RECEIVED` reselect — still need the
     * substitution, and Shopper Platform shares them. Gating the selector itself removed their fallback too.
     */
    const isRestoredGridOpen =
        Boolean(action.meta?.preInitializedPaymentGrid) &&
        getFeatureToggle(updatedState, PAYMENT_GRID_BAGGAGE_COMPRESSION_TOGGLE as any);

    // Ensure the current term maps to a valid grid cell, always
    const item = isRestoredGridOpen ? undefined : getNewSelectedCell(updatedState);
    if (item !== null && item) {
        store.dispatch(updateDownPaymentImmediate(item.cashDownVal));
        store.dispatch(selectedTerm(item.id, { payment: false }));
    }
};

const initializePaymentGridData = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    offerType: OfferType,
    resolveExplicitLock?: ExplicitLockResolver
) => {
    const { preInitializedPaymentGrid } = action.meta ?? {};
    const isFlexBuy = isFlexBuyPayment(updatedState);

    let downPaymentList: number[];
    let termMonthsList: number[];
    let downPayment: number;
    let currentTerm: number | undefined;

    if (preInitializedPaymentGrid) {
        downPaymentList = preInitializedPaymentGrid.downPaymentList;
        termMonthsList = preInitializedPaymentGrid.termMonthsList;
        /**
         * US2085313: a restored grid may legitimately have no selected cell, which leaves the pre-initialized
         * grid carrying no current down payment or term. Both fall back to the deal's own values rather than
         * staying undefined: discovery was otherwise probed with no down payment at all, and `programTerms`
         * stopped excluding the deal's own term, so it asked about a term the grid was already pricing.
         */
        downPayment = preInitializedPaymentGrid.currentDownPayment ?? getDownPayment(updatedState) ?? 0;
        currentTerm = preInitializedPaymentGrid.currentTermMonth ?? getSelectedTermMonths(updatedState);
    } else {
        downPaymentList = getInitialDownPaymentListForGrid(updatedState);
        downPayment = getDownPayment(updatedState) || 0;
        currentTerm = getSelectedTermMonths(updatedState);
        termMonthsList = isFlexBuy ? getInitialFlexBuyTermMonthList() : getInitialTermsMonthsListForGrid(updatedState);

        if (isFlexBuy) {
            initializeFlexBuyGrid(store, downPaymentList, downPayment, termMonthsList, updatedState);
        } else {
            store.dispatch(initializePaymentGridTerms(offerType, termMonthsList));
            store.dispatch(initializePaymentGridCashdown(offerType, downPaymentList));
        }

        if (offerType === FINANCE) {
            initializeFinanceRates(store, updatedState, offerType, termMonthsList, currentTerm);
        }
    }

    const isProgramQuotes = !dealPricesOnUserProgramQuotes(store.getState(), offerType);

    /**
     * One build identity, minted before anything is dispatched for it, so a build superseded by a second enable or an
     * ASP re-tick can be told apart when its response lands.
     */
    const gridBuildId = isLeaseRedesign(store.getState(), offerType) ? nextGridRoundId() : undefined;
    if (gridBuildId) {
        store.dispatch(
            setPaymentGridBuild(offerType, {
                id: gridBuildId,
                ...(!isExplicitLenderProgramGridActive(store.getState(), offerType) && {
                    isUserProgramQuotes: getIsUserProgramQuotes(store.getState(), offerType)
                })
            })
        );
    }

    if (isExplicitLenderProgramGridActive(store.getState(), offerType)) {
        recordDealProgramAtOpen(store, offerType, resolveExplicitLock);
        store.dispatch(loadPaymentGridData(action.meta?.preInitializedPaymentGrid));
        return;
    }

    /**
     * US2118720: every column but the deal's own starts on the program resolved for its term, so the probe is needed
     * whatever the deal itself is priced on - without it `programInfoByTerm` is never written for the other terms and
     * `intendedSource` has no program to resolve them against. Before the redesign only a program-quotes deal had any
     * use for a probe, which is why the gate was the deal's own engine.
     *
     * The deal's own term is still excluded, as it always was: the probe asks about the terms the grid holds and the
     * deal is not one of them.
     */
    const needsProgramDiscovery = isAspPgRedesignEnabled(updatedState, offerType) || isProgramQuotes;

    if (needsProgramDiscovery && !preInitializedPaymentGrid?.hasLenderPrograms) {
        const programTerms = termMonthsList.filter((term) => term && term !== currentTerm);
        // If no terms exist beyond the current term, there are no additional programs to fetch;
        // proceed to load the grid directly.
        if (programTerms.length > 0) {
            // Probed at the deal's own cash down, never zero - see getRedesignDiscoveryAnchor. A restore supplies
            // its own current down payment, which stays authoritative.
            const discoveryDownPayment = !isAspPgRedesignEnabled(updatedState, offerType)
                ? 0
                : (preInitializedPaymentGrid
                      ? downPayment
                      : getRedesignDiscoveryAnchor(updatedState, offerType, downPaymentList[1])) ?? downPayment;
            // Gated, so discovery outside the redesign sends exactly what it sent before.
            const lenderPrograms = loadPaymentGridLenderPrograms(programTerms, discoveryDownPayment, offerType, {
                skipProgramOverride: isAspPgRedesignEnabled(updatedState, offerType),
                dropOverrides: isAspPgRedesignEnabled(updatedState, offerType),
                forceProgramQuotes: isAspPgRedesignEnabled(updatedState, offerType)
            });
            // DE744178: persist preInitializedPaymentGrid on the lender-programs action meta so the
            // skip-payment guard in loadRegularPaymentGridData can fire after the async PAYMENT_RECEIVED
            // boundary. Without this, SV finance PG deals always recalc per row on Summary load because
            // handleGridActions re-dispatches loadPaymentGridData(undefined, ...). sourceApplication is
            // injected by calculatePayments on the outbound payment call, so it survives on originalAction.
            // Carries the build id, so the response can be matched against the build still in progress.
            const wrappedLenderPrograms = addPaymentGridMeta(lenderPrograms, {
                isMainPayment: false,
                gridRoundId: gridBuildId,
                gridBuildId
            });
            if (preInitializedPaymentGrid) {
                wrappedLenderPrograms.meta = {
                    ...wrappedLenderPrograms.meta,
                    preInitializedPaymentGrid
                };
            }
            store.dispatch(wrappedLenderPrograms);
        } else {
            store.dispatch(loadPaymentGridData());
        }
        return;
    }

    store.dispatch(loadPaymentGridData(action.meta?.preInitializedPaymentGrid));
};

const initializeFlexBuyGrid = (
    store: PaymentGridMiddlewareStore,
    downPaymentList: number[],
    downPayment: number,
    termMonthsList: number[],
    updatedState: State
) => {
    const downPaymentIndex = downPaymentList.findIndex((dp) => dp === downPayment);
    //initialPaymentDiscount is allowed to be undefined
    const { initialPaymentDiscount } = getCurrentFlexBuyPayments(updatedState) || {};

    const basePercents = [15, initialPaymentDiscount, 18];
    const baseTerms = [...termMonthsList];

    if (downPaymentIndex !== 1) {
        [basePercents[1], basePercents[downPaymentIndex]] = [basePercents[downPaymentIndex], basePercents[1]];
        [baseTerms[1], baseTerms[downPaymentIndex]] = [baseTerms[downPaymentIndex], baseTerms[1]];
    }

    const commonPayload = {
        flexBuyPercents: basePercents,
        flexBuyTerms: baseTerms,
        flexBuyDownPayments: downPaymentList
    };

    store.dispatch(loadingFlexBuyPaymentGrid(commonPayload));
    store.dispatch(updateFlexBuyPaymentGridAxisValues(commonPayload));
};

const initializeFinanceRates = (
    store: PaymentGridMiddlewareStore,
    updatedState: State,
    offerType: OfferType,
    termMonthsList: number[],
    currentTerm: number | undefined
) => {
    const isUPQ = dealPricesOnUserProgramQuotes(store.getState(), offerType);
    if (!isUPQ) return;

    /**
     * US2118720: the deal's rate seeds the deal's OWN column and nothing else. Every other column opens on the program
     * resolved for its term (`openColumnSourcesFor`), and a seeded slot is sent as a sell-rate override ON that
     * program-quotes call - so the column came back quoted at the deal's rate instead of its program's, and the Sell
     * Rate row read the deal's rate before the response even landed. Lease seeds nothing and has always taken the
     * program's rate there; this is what makes Finance behave the same way.
     */
    const seedsDealColumnOnly = isAspPgRedesignEnabled(updatedState, offerType);

    const activeColumnIndex = getResolvedPaymentGridSelectedColumnIndex(updatedState, offerType);
    const activeColumnTerm =
        activeColumnIndex == null ? undefined : getPaymentGridTerms(updatedState, offerType)[activeColumnIndex];
    const activeColumnSellRate =
        activeColumnIndex == null || !isNumber(activeColumnTerm)
            ? undefined
            : getOwnColumnOverride(updatedState, offerType, activeColumnIndex, activeColumnTerm)?.sellRate;
    const sellRate = activeColumnSellRate ?? getSellRateOverride(updatedState, false);
    const buyRate = getBuyRateOverride(updatedState, false);

    termMonthsList.forEach((termMonth) => {
        if (termMonth === currentTerm) {
            const buyRateOverride = getBuyRateOverrideByOfferTypeAndTerm(updatedState, offerType, currentTerm, true);
            store.dispatch(
                updateBuyRateOverride(buyRate ?? buyRateOverride ?? 0, termMonth, {
                    isSelectedTerm: true,
                    withPayment: false
                })
            );
        }
        const termSlot = getSellRateOverrideByOfferTypeAndTerm(updatedState, offerType, termMonth);
        const isActiveColumnTerm = activeColumnSellRate != null && activeColumnTerm === termMonth;
        // The active column's own rate is the user's, not the deal's, so it still seeds its own term.
        if (seedsDealColumnOnly && termMonth !== currentTerm && !isActiveColumnTerm) return;
        if (termSlot == null || isActiveColumnTerm) {
            store.dispatch(
                updateSellRateOverride(isActiveColumnTerm ? activeColumnSellRate : sellRate, termMonth, {
                    isSelectedTerm: false,
                    skipPaymentCall: true
                })
            );
        }
    });
};

const handleInitializePaymentGridFromTemplate = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    resolveExplicitLock?: ExplicitLockResolver
) => {
    const offerType = action.payload?.offerType || FINANCE;
    const currentTermMonth = getSelectedTermMonths(updatedState, offerType);

    if (!isNumber(currentTermMonth)) return;

    const sellRate = getSellRateOverride(updatedState, false, offerType);
    if (!sellRate) {
        const termData = Boolean(getSelectedTerm(updatedState, offerType));
        const sellRateOverride = getSellRateOverrideByOfferTypeAndTerm(updatedState, offerType, currentTermMonth, true, termData)!;

        if (offerType === FINANCE) {
            store.dispatch(updateFinanceSellRateForSelectedTermUPQ(sellRateOverride));
        } else if (offerType === LEASE) {
            store.dispatch(
                updateSellRateForSelectedTermUPQ(
                    sellRateOverride,
                    getPaymentGridTermRateType(updatedState, offerType, currentTermMonth)
                )
            );
        }
    }

    // The template/restore path forces Lease onto program-quotes unconditionally; under the redesign the restored deal
    // keeps the engine it was saved with, since the grid carries the engine per column.
    if (offerType === LEASE && !isLeaseRedesign(updatedState, offerType) && !dealerOptOutBlocksPqExcursion(updatedState)) {
        store.dispatch(updateCurrentCalculationEndpoint(CALC_ENDPOINTS.programQuotes, offerType));
    }

    /**
     * A restore is a BUILD and has to be minted as one, or its rows carry no `buildId` and `handleGridActions` judges
     * them as an EDIT: scoped and merged rather than replacing, leaving columns undecided rather than `unavailable`.
     */
    const restoreBuildId = isLeaseRedesign(updatedState, offerType) ? nextGridRoundId() : undefined;
    if (restoreBuildId) {
        store.dispatch(
            setPaymentGridBuild(offerType, {
                id: restoreBuildId,
                ...(!isExplicitLenderProgramGridActive(updatedState, offerType) && {
                    isUserProgramQuotes: getIsUserProgramQuotes(updatedState, offerType)
                })
            })
        );
    }

    if (isExplicitLenderProgramGridActive(updatedState, offerType)) {
        recordDealProgramAtOpen(store, offerType, resolveExplicitLock);
    }

    const { paymentGrid } = action.payload;
    store.dispatch(loadPaymentGridData(paymentGrid));
};

// Grid-population actions may target an offer type other than the deal's current one (e.g. the Alt
// Deal Structure slideout populating LEASE while the deal is on Finance). Such actions carry the
// explicit offer type in meta.middleware.offerType; PAYMENT_RECEIVED carries it on the wrapped
// originalAction. Resolve it, falling back to the deal's current offer type so normal main-grid
// flows are unchanged.
const resolveGridOfferType = (action: PaymentGridMiddlewareAction, fallbackOfferType: OfferType): OfferType =>
    action?.meta?.middleware?.offerType ?? action?.meta?.originalAction?.meta?.middleware?.offerType ?? fallbackOfferType;

const handleLoadPaymentGridData = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    state: State,
    offerType: OfferType,
    upqFallbackDisabled: boolean
) => {
    const { preInitializedPaymentGrid } = action.meta ?? {};
    const isFlexBuy = isFlexBuyPayment(updatedState);
    const resolvedOfferType = resolveGridOfferType(action, offerType);

    // The grid was closed while its discovery was in flight, so there is nothing left to price. Without this the
    // row round still went out - for a grid with no axes, since `RESET_PAYMENT_GRID` cleared them.
    if (isLeaseRedesign(updatedState, resolvedOfferType) && !getPaymentGridToggle(updatedState, resolvedOfferType)) return;

    let downPaymentList: number[];
    let termMonthsList: number[];
    let flexBuyPercentsList: number[] = [];

    if (preInitializedPaymentGrid) {
        downPaymentList = preInitializedPaymentGrid.downPaymentList;
        termMonthsList = preInitializedPaymentGrid.termMonthsList;
        flexBuyPercentsList = preInitializedPaymentGrid.flexBuyPercents;
    } else {
        downPaymentList = getUniquePaymentGridCashDown(updatedState, resolvedOfferType);
        termMonthsList = getUniquePaymentGridTerms(updatedState, resolvedOfferType);

        if (isFlexBuy) {
            downPaymentList = getFlexBuyPaymentGridCashDowns(updatedState);
            termMonthsList = getFlexBuyPaymentGridTerms(updatedState);
            flexBuyPercentsList = getFlexBuyPaymentGridPercents(updatedState);
        }
    }

    if (isFlexBuy) {
        const updateRow = updateFlexBuyTermsGrid(
            {
                flexBuyDownPayments: downPaymentList,
                flexBuyTerms: termMonthsList,
                flexBuyPercents: flexBuyPercentsList
            },
            { isMainPayment: false }
        );
        store.dispatch(updateRow);
    } else {
        const sourceApplication = action.meta?.sourceApplication;
        loadRegularPaymentGridData(
            store,
            resolvedOfferType,
            downPaymentList,
            termMonthsList,
            preInitializedPaymentGrid,
            upqFallbackDisabled,
            sourceApplication
        );
    }
};

/**
 * The facts `leaseGridColumnUtils` decides a column's engine from, read off current state. `atOpen` is the grid being
 * (re)built, where no column counts as user-priced yet.
 */
const leaseColumnFacts = (
    state: State,
    offerType: OfferType,
    termMonthsList: (number | null)[],
    {
        atOpen = false,
        isUserProgramQuotes,
        columnIndex
    }: {
        atOpen?: boolean;
        isUserProgramQuotes?: boolean;
        columnIndex?: number;
    } = {}
): LeaseColumnFacts => {
    const columnSources = getPaymentGridColumnSources(state, offerType);
    const isSibling = (termMonths: number) => isSharedTermSibling(state, offerType, columnIndex, termMonths);
    const ownSellRate = (termMonths: number) => getOwnColumnOverride(state, offerType, columnIndex as number, termMonths)?.sellRate;
    const ownsFigure = (termMonths: number) => {
        const own = getOwnColumnOverride(state, offerType, columnIndex as number, termMonths);
        return own?.sellRate != null || own?.residualInfo != null;
    };

    return {
        termMonthsList,
        currentTermMonths: getSelectedTermMonths(state, offerType),
        // The build's own engine state where it recorded one, read live otherwise: unticking part-way through a build
        // used to re-judge every column under user-program-quotes rules and blank ones that had priced fine.
        isUserProgramQuotes: isUserProgramQuotes ?? isGridUserProgramQuotes(state, offerType),
        isDealOnUserProgramQuotes: isExplicitLenderProgramGridActive(state, offerType) && getIsUserProgramQuotes(state, offerType),
        isDealsOwnColumnBeingReTermed: Boolean(getPaymentGridDealTermBeforeEdit(state, offerType)),
        hasUserSuppliedRate: (termMonths) =>
            isSibling(termMonths) ? ownSellRate(termMonths) != null : hasUserSuppliedGridSellRate(state, offerType, termMonths),
        hasUserPricedColumn: (termMonths) =>
            !atOpen &&
            (isSibling(termMonths)
                ? ownsFigure(termMonths) &&
                  getColumnVerdict(state, offerType, columnIndex as number, termMonths) !== PaymentGridSource.ProgramQuotes
                : hasUserPricedGridColumn(state, offerType, termMonths)),
        hasResolvedProgram: bindGridProgramForTerm(state, offerType),
        // The verdict this column already carries, so an ASP-off pass keeps a column the grid priced against its own
        // program. Cleared with the rest of `paymentGridData` on a close.
        recordedSource: (termMonths) =>
            isSibling(termMonths)
                ? getColumnVerdict(state, offerType, columnIndex as number, termMonths)
                : columnSources[termMonths],
        ...(columnIndex != null && {
            isDealsOwnColumn: (termMonths: number) =>
                !isSibling(termMonths) && termMonths === getSelectedTermMonths(state, offerType)
        })
    };
};

/**
 * The engine each column is asked on when the grid is built. US2118720: only the deal's own column follows the deal's
 * endpoint; every other column starts on program-quotes whatever the deal is priced on. On a deal with a template that
 * has payment terms, under the template rate UPQ lock, every column starts on the deal's endpoint instead.
 *
 * Lease routes through `resolvePriceableColumnSources`, which needs a program per column because it cannot estimate:
 * a column program-quotes will not price waits for a rate of the user's own rather than being quoted off a residual it
 * was never given. Finance has no such limit - `handleGridFallbackPass` re-prices whatever program-quotes could not, in
 * one extra round - so its inactive columns are simply started on program-quotes and left to that pass. Resolving them
 * against the program cache here instead would ask for the same user-program-quotes call twice.
 *
 * The deal's own column follows the endpoint directly, with no program read. That is what "the active column is
 * unchanged" means, and on Finance the read would not be sound anyway: `getGridColumnProgramInfo`'s fallback to the
 * program on the deal's selected term is Lease-only, and the cache it would otherwise consult is written for every term
 * except the current one.
 */
const openColumnSourcesFor = (state: State, offerType: OfferType, termMonthsList: number[]): Record<number, PaymentGridSource> => {
    if (isLeaseRedesign(state, offerType)) {
        return resolvePriceableColumnSources(leaseColumnFacts(state, offerType, termMonthsList, { atOpen: true }));
    }

    const dealIsUserProgramQuotes = isGridUserProgramQuotes(state, offerType);
    const currentTermMonths = getSelectedTermMonths(state, offerType);
    const isTemplateGrid = isTemplateRateGridActive(state);

    return termMonthsList.reduce<Record<number, PaymentGridSource>>((sources, termMonths) => {
        sources[termMonths] =
            dealIsUserProgramQuotes && (isTemplateGrid || termMonths === currentTermMonths)
                ? PaymentGridSource.UserProgramQuotes
                : PaymentGridSource.ProgramQuotes;
        return sources;
    }, {});
};

const loadRegularPaymentGridData = (
    store: PaymentGridMiddlewareStore,
    offerType: OfferType,
    downPaymentList: number[],
    termMonthsList: number[],
    preInitializedPaymentGrid: GenericObject,
    upqFallbackDisabled: boolean,
    sourceApplication?: string
) => {
    // DE744178: SV must display the MV-priced grid, not recalculate it, so a pre-initialized grid loaded by Shopper
    // Platform skips the per-row payment call. MV keeps recalculating on edit - it is the system that priced the grid.
    // Not honoured for Lease: MV paints its cells from `terms.byId` alone, so skipping the calls renders an empty grid.
    const isShopperPlatform = sourceApplication === ApplicationCodes.SP;
    /**
     * Finance only, deliberately. This reason has always been finance-only in effect, because
     * `updateLeaseTermsGrid` ignored the flag and `updatePaymentGridRow` never passed it to balloon. Both now
     * honour it for US2085313, so without this restriction a Shopper Platform lease or balloon load would
     * start skipping calls it has always made — with the persistence toggle off, and on the very path the
     * note above warns renders an empty grid.
     */
    const isRetainingSharedGrid = getFeatureToggle(store.getState(), SV_RETAIN_PROGRAM_TOGGLE);
    const shouldSkipPaymentCall =
        Boolean(preInitializedPaymentGrid) && isShopperPlatform && (offerType === FINANCE || isRetainingSharedGrid);

    /**
     * US2085313: a row whose every cell already holds a quote is not re-priced when the grid opens.
     *
     * The comment above records why Lease could not skip before — "MV paints its cells from `terms.byId`
     * alone, so skipping the calls renders an empty grid". Restoring the stored grid into `terms.byId` is
     * exactly what removes that objection: the cells are there to paint, so the calls are pure waste.
     *
     * Judged per row off the records themselves rather than from a "was restored" flag, so a partial
     * restore still prices only the rows it is missing, and a row the dealer later edits reprices normally.
     */
    const isRowFullyQuoted = (currentState: State, downPayment: number, rowTerms: number[]) => {
        /**
         * The priced-at stamps, not the term records, are what say "this grid quoted this cell".
         *
         * `terms.byId` is merge-only and outlives the grid that produced it, so record presence alone let a
         * plain reopen skip: price the grid, close it, edit a fee on the deal screen — only the active term
         * re-prices — and every row still read as quoted, serving pre-edit payments as current. The stamps
         * cannot say that, because `RESET_PAYMENT_GRID` drops them for precisely this reason ("a rebuilt
         * grid must not inherit them and present a cell as having priced when it has not"), while it cannot
         * reach the term records. A restore puts both back, so a restored row still skips.
         */
        const cellPricedAt = getPaymentGridCellPricedAt(currentState, offerType);
        return (
            rowTerms.length > 0 &&
            rowTerms.every((term) => {
                /**
                 * `getTermId`, deliberately: this must agree with what *renders* the cell.
                 * `getPaymentGridItem` keys every cell with `getTermId` too, so keying this check any other
                 * way would let a row be judged quoted off records the grid cannot paint. dr-dash sets
                 * `isTermIdWithDownPayment` true at `INIT_PENCIL_SUCCESS`, so the flag is on for every MV
                 * grid; a consumer that leaves it unset collapses the cash-down axis here exactly as it does
                 * in the renderer, which is consistent rather than skewed.
                 */
                const termId = getTermId(offerType, term, downPayment);
                if (!termId) return false;
                const termRecord = getTerm(currentState, termId);
                return Boolean(termRecord) && !isEmpty(termRecord) && cellPricedAt[termId] !== undefined;
            })
        );
    };

    const state = store.getState();
    const isLeaseRedesignGrid = isLeaseRedesign(state, offerType);

    const isRedesignGrid = isAspPgRedesignEnabled(state, offerType);

    /** Whether this open resolves a verdict per column at all. Finance does since US2118720. */
    const hasOpenColumnVerdicts = isLeaseRedesignGrid || isRedesignGrid;

    /**
     * The columns this open may ask a payment for, and the engine each needs. Derived from state rather than
     * `columnSources`, which is empty at open and would never split.
     */
    const openColumnSources = hasOpenColumnVerdicts ? openColumnSourcesFor(state, offerType, termMonthsList) : {};
    const termsToPrice = hasOpenColumnVerdicts ? Object.keys(openColumnSources).map(Number) : termMonthsList;

    // Nothing in the grid can be priced. No row call goes out, but the columns must still be resolved or nothing tells
    // the render gate to stop waiting on them.
    if (!termsToPrice.length) {
        handleGridFallbackPass(store, state, offerType, upqFallbackDisabled);
        return;
    }

    // One id for every call this open fans out. Taken from the build minted in `initializePaymentGridData`; minted here
    // only for a path that reached the rows without one (a template restore). Lease only.
    const gridBuildId = isLeaseRedesignGrid ? getPaymentGridBuild(state, offerType)?.id : undefined;
    const gridRoundId = isLeaseRedesignGrid ? gridBuildId ?? nextGridRoundId() : undefined;

    // Engine rules live in splitTermsByEngine, shared with the row and cash-down edits so no two paths can
    // disagree about which engine a column is on.
    const {
        priceableTerms,
        programQuotesTerms,
        userProgramQuotesTerms,
        isRowSplit: spansBothEngines
    } = splitTermsByEngine({
        termList: termsToPrice,
        columnSources: openColumnSources,
        dealIsUserProgramQuotes: isGridUserProgramQuotes(state, offerType)
    });
    // Finance included since US2118720: its columns no longer all share the deal's engine, so a row that spans both
    // has to go out as two calls there too.
    const isRowSplit = isRedesignGrid && spansBothEngines;

    /**
     * Each call states its own engine rather than letting the payload builder derive one from the deal's endpoint.
     * Finance needs this now that only its deal column follows that endpoint: the inactive columns' program-quotes half
     * has to be forced, or an Auto-Select-off deal would send it to user-program-quotes like everything else.
     */
    const shouldForceEngineOnOpen = isRedesignGrid;

    /**
     * A restored column verdict, if the load put one back. `columnSources` is cleared by
     * `RESET_PAYMENT_GRID` and otherwise written only by a payment outcome, so anything present at open
     * came from the restore.
     */
    const restoredColumnVerdict = getPaymentGridColumnSources(state, offerType);
    const requiresEveryColumnVerdict = getFeatureToggle(state, MULTI_SCENARIO_GRID_CONSISTENCY_TOGGLE as any);
    const hasRestoredColumnVerdict = requiresEveryColumnVerdict
        ? termsToPrice.filter(hasTerm).every((termMonths) => restoredColumnVerdict[termMonths] !== undefined)
        : Object.keys(restoredColumnVerdict).length > 0;

    /**
     * The terms a stored cell could exist for. A column saved `unavailable` was one nothing could price, so the
     * save holds no cell for it - and requiring one made EVERY row read as unquoted, so a reload re-priced the
     * whole grid instead of painting what was saved. Lease already excludes these from `priceableTerms` via
     * `resolvePriceableColumnSources`, so this only bites Finance, where `openColumnSources` is empty.
     */
    const quotableTerms = priceableTerms.filter(
        (termMonths) => hasTerm(termMonths) && restoredColumnVerdict[termMonths] !== PaymentGridSource.Unavailable
    );

    /**
     * With the redesign on, the calls may be skipped only where the verdict was restored. With DE774740 off this
     * is the same predicate `handleGridFallbackPass` uses to decide whether it runs, so the two cannot disagree;
     * with it on the pass does not run on this path, and a restore missing any column's verdict keeps its calls.
     *
     * Lease needs it to paint at all: a masked cell reads its column's source, and
     * `resolveLeaseColumnSources` marks a column `Unavailable` unless `isColumnFullyPriced`, whose Lease
     *
     * Finance needs it for a different reason. Its columns resolve through `resolveColumnSources`, where
     * `hasProgramForTerm` reads `programInfoByTerm`, and only lender-program discovery writes that. A
     * restored grid skips discovery, so with no verdict to stand on every column would resolve
     * `UserProgramQuotes` and the pass would re-price the whole grid on the wrong engine — worse than the
     * round trip skipping it saved. A grid stored before the source was saved therefore prices as before.
     *
     * Balloon and cash never reach this: the redesign gate excludes them, as its own comment records.
     */
    const skipRestoredRows =
        getFeatureToggle(state, PAYMENT_GRID_BAGGAGE_COMPRESSION_TOGGLE as any) &&
        (!isAspPgRedesignEnabled(state, offerType) || hasRestoredColumnVerdict);

    /**
     * Every row already quoted means no payment leaves this open, so nothing would otherwise tell the
     * columns they are resolved — `columnSources` is written only from a response.
     *
     * Where the restore supplied that verdict it stands, and the pass must not run: it would recompute the
     * columns from rows that deliberately never priced and mark them all `Unavailable`.
     *
     * Where it did not, the pass earns its place on Finance with the redesign on: a column with no
     * `columnSources` entry leaves every cell of it spinning through `isColumnDecided`, so the columns have
     * to be resolved even though no payment is coming. It is the same step the `!termsToPrice.length` return
     * above uses for its own "nothing will be priced" case. Masked Lease no longer depends on it — it either
     * has a restored verdict or keeps its calls — so this block is live for Finance, not dead code.
     *
     * Under DE774740 the pass does not run: a restore counts only when it supplied a verdict for every column,
     * and one that left any column without a verdict keeps its calls instead.
     */
    const restorableDownPayments = validDownPayments(downPaymentList);
    const everyRowRestored =
        skipRestoredRows &&
        restorableDownPayments.length > 0 &&
        restorableDownPayments.every((dp) => isRowFullyQuoted(state, dp, quotableTerms));

    if (everyRowRestored) {
        // The restore's own verdict is the answer, and the pass would replace it with one computed from
        // rows that deliberately never priced. Where there is none, the pass resolves the columns as it does
        // for any open that will ask for no payment.
        if (!requiresEveryColumnVerdict && !hasRestoredColumnVerdict) {
            handleGridFallbackPass(store, state, offerType, upqFallbackDisabled, termsToPrice);
        }
        return;
    }

    gridRowsFor(state, offerType).forEach(({ rowIndex, downPayment }) => {
        // Only the terms this open would actually ask about count towards "already quoted".
        const skipThisRow = skipRestoredRows && isRowFullyQuoted(state, downPayment, quotableTerms);

        // Tag the row payment with its offer type so calculatePayments builds the payload for THIS
        // offer type rather than the deal's current one. For main-grid flows offerType === current
        // offer, so this is a no-op there.
        const buildRow = (
            terms: number[],
            forceUserProgramQuotes: boolean,
            isSplitPart: boolean,
            columnIndices?: number[],
            columnSellRate?: number,
            ignoreTermOverride?: boolean,
            columnResidualInfo?: GenericObject
        ) =>
            addPaymentGridMeta(
                tagOfferType(
                    updatePaymentGridRow(offerType, terms, downPayment, {
                        shouldSkipPaymentCall: shouldSkipPaymentCall || skipThisRow,
                        ...(isAspPgRedesignEnabled(state, offerType) && {
                            gridColumnIndices: columnIndices ?? coveredColumnIndices(state, offerType, terms)
                        }),
                        ...columnScopedMeta(
                            state,
                            offerType,
                            terms,
                            columnIndices,
                            columnSellRate,
                            columnResidualInfo,
                            ignoreTermOverride
                        ),
                        gridRowIndices: [rowIndex],
                        ...(shouldForceEngineOnOpen && { forceUseGenericCalcs: forceUserProgramQuotes }),
                        // See handleRegularPaymentGrid: only the two halves of an actual split are marked, so an
                        // ordinary single-call row can still trigger a genuine fallback if it fails.
                        ...(isSplitPart && { isGridRowSplitPart: true })
                    }),
                    offerType
                ),
                withLeaseProgramPin(state, offerType, {
                    isMainPayment: false,
                    gridRoundId,
                    gridBuildId,
                    ...(isRedesignGrid && { gridRoundTerms: priceableTerms })
                })
            );

        // No row here is the deal's own payment - the open only ever dispatches - so no half is the main one.
        for (const call of planGridRowCalls(
            { priceableTerms, programQuotesTerms, userProgramQuotesTerms },
            { isRowSplit, isMainPayment: false, currentTermMonth: getSelectedTermMonths(state, offerType) }
        )) {
            const { sharedTerms, columnCalls } = isAspPgRedesignEnabled(state, offerType)
                ? splitTermsByColumn(state, offerType, call.terms, call.forceUserProgramQuotes)
                : { sharedTerms: call.terms, columnCalls: [] as ColumnScopedCall[] };

            columnCalls.forEach(({ term, columnIndex, sellRate, residualInfo, ignoreTermOverride }) =>
                store.dispatch(
                    buildRow(
                        [term],
                        call.forceUserProgramQuotes,
                        call.isSplitPart,
                        [columnIndex],
                        sellRate,
                        ignoreTermOverride,
                        residualInfo
                    )
                )
            );
            splitTermsByProgram(state, offerType, sharedTerms).forEach((group) =>
                store.dispatch(buildRow(group, call.forceUserProgramQuotes, call.isSplitPart))
            );
        }
    });
};

const residualValueOf = (percent?: number, dollar?: number): GenericObject | undefined =>
    percent ? { value: percent, unitType: RESIDUAL_PERCENT } : dollar ? { value: dollar, unitType: RESIDUAL_DOLLAR } : undefined;

const typedResidualValueOf = (percent?: number, dollar?: number): GenericObject | undefined =>
    percent != null
        ? { value: percent, unitType: RESIDUAL_PERCENT }
        : dollar != null
        ? { value: dollar, unitType: RESIDUAL_DOLLAR }
        : undefined;

const heldBaseResidual = (state: State, offerType: OfferType, termMonths: number): GenericObject | undefined => {
    const override = getResidualInfoOverrideByOfferTypeTerm(state, offerType, termMonths);
    const term = getTermByOfferTypeAndMonths(state, offerType, termMonths);
    return (
        typedResidualValueOf(override?.baseResidualPercent, override?.baseResidualAmount) ??
        residualValueOf(term?.baseResidualPercent, term?.baseResidualDollar)
    );
};

/**
 * Holds what the ACTIVE column was priced at, keyed by the term it moves to, so its fallback prices from a rate the
 * deal had rather than the 0 a missing record resolves to. Held, not written: the program-quotes attempt is in flight.
 */
const holdFallbackRateForTermChange = (
    store: PaymentGridMiddlewareStore,
    preEditState: State,
    offerType: OfferType,
    {
        prevTerm,
        incomingTermMonths,
        isCurrentTermEdited,
        isUPQ,
        pendingSellRate
    }: {
        prevTerm: number | null | undefined;
        incomingTermMonths: number;
        isCurrentTermEdited: boolean;
        isUPQ: boolean;
        pendingSellRate?: number;
    }
): void => {
    const isLease = isLeaseRedesign(preEditState, offerType);
    if (offerType !== FINANCE && !isLease) return;
    if (!isAspPgRedesignEnabled(preEditState, offerType)) return;
    if (!getPaymentGridToggle(preEditState, offerType)) return;
    if (isUPQ) return;
    if (!isCurrentTermEdited) return;
    // A parked rate is already becoming the incoming term's override, so the column needs no fallback.
    if (isNumber(pendingSellRate)) return;
    if (!isNumber(prevTerm)) return;

    const outgoingTerm = getTermByOfferTypeAndMonths(preEditState, offerType, prevTerm as number);
    // A hold already keyed to the outgoing term is carried forward, or a second term change on the same column
    // before the first fallback settles loses the rate: that column has no override and no record of its own
    const heldForOutgoingTerm = getPaymentGridFallbackRates(preEditState, offerType)[prevTerm as number];

    const sellRate =
        getSellRateOverrideByOfferTypeAndTerm(preEditState, offerType, prevTerm as number) ??
        outgoingTerm?.sellRate ??
        heldForOutgoingTerm?.sellRate;
    if (!isNumber(sellRate)) return;

    const buyRate =
        getBuyRateOverrideByOfferTypeAndTerm(preEditState, offerType, prevTerm as number) ??
        (isLease ? undefined : outgoingTerm?.buyRate) ??
        heldForOutgoingTerm?.buyRate;
    // The unit the VALUE was captured in: `getGridRateTypeByTerm` prefers the per-term stamp US2075533 stores beside
    // an override, so a typed rate is not relabelled with the record's older unit. Narrowed to the two the grid can
    // label and convert; see `PaymentGridFallbackRates`.
    const declaredRateType = getGridRateTypeByTerm(preEditState, offerType, prevTerm as number) ?? heldForOutgoingTerm?.rateType;
    const rateType =
        declaredRateType === RATE_TYPE.APR || declaredRateType === RATE_TYPE.MoneyFactor ? declaredRateType : undefined;

    const residual = isLease
        ? heldBaseResidual(preEditState, offerType, prevTerm as number) ?? heldForOutgoingTerm?.residual
        : undefined;
    if (isLease && !residual) return;

    store.dispatch(
        setPaymentGridFallbackRates(offerType, {
            [incomingTermMonths]: {
                sellRate: sellRate as number,
                ...(isNumber(buyRate) && { buyRate: buyRate as number }),
                ...(rateType && { rateType }),
                ...(residual && { residual })
            },
            // The outgoing term's own hold has moved, so it is released here rather than left for the stale sweep.
            ...(prevTerm !== incomingTermMonths && { [prevTerm as number]: null })
        })
    );
};

/**
 * Released once the column prices, or once it no longer holds that term. A column falling back in this same pass is
 * exempt even where it priced: with Auto Select on it can be quoted and still have no program, which is the hold's case.
 */
const releaseStaleFallbackRates = (
    store: PaymentGridMiddlewareStore,
    offerType: OfferType,
    gridTermMonths: (number | null)[],
    isColumnFullyPriced: (termMonths: number) => boolean,
    fallbackTerms: number[]
): void => {
    const heldRates = getPaymentGridFallbackRates(store.getState(), offerType);

    const stale = Object.keys(heldRates)
        .map(Number)
        .filter((termMonths) => !Number.isNaN(termMonths))
        .filter((termMonths) => !fallbackTerms.includes(termMonths))
        .filter((termMonths) => !gridTermMonths.includes(termMonths) || isColumnFullyPriced(termMonths));
    if (!stale.length) return;

    store.dispatch(
        setPaymentGridFallbackRates(
            offerType,
            stale.reduce<Record<number, null>>((released, termMonths) => {
                released[termMonths] = null;
                return released;
            }, {})
        )
    );
};

/**
 * Promotes a held rate to the falling-back column's own override, so the forced call has something to price with. The
 * same two writes `restoreHeldRates` makes on the deal screen; neither carries a payment, the fallback row does.
 */
const restoreFallbackRatesForTerms = (store: PaymentGridMiddlewareStore, offerType: OfferType, fallbackTerms: number[]): void => {
    const state = store.getState();
    // `updateBuyRateOverride` carries no offer type, so the pair can only be written for the deal's own offer -
    // otherwise the rate lands on this offer type and the buy rate on the deal's, splitting the pair.
    if (offerType !== getCurrentOfferType(state)) return;

    const heldRates = getPaymentGridFallbackRates(state, offerType);
    if (!Object.keys(heldRates).length) return;

    const selectedTermMonths = getSelectedTermMonths(state, offerType);

    fallbackTerms.forEach((termMonths) => {
        const held = heldRates[termMonths];
        if (!held || !isNumber(held.sellRate)) return;
        // A rate of the column's own outranks the held one, typed or promoted by an earlier fallback - and the hold
        // is spent either way, or it would resurface on whichever column next lands on this term.
        if (getSellRateOverrideByOfferTypeAndTerm(state, offerType, termMonths) != null) {
            store.dispatch(setPaymentGridFallbackRates(offerType, { [termMonths]: null }));
            return;
        }

        const isSelectedTerm = termMonths === selectedTermMonths;

        store.dispatch(
            updateSellRateOverride(
                held.sellRate as number,
                termMonths,
                {
                    isSelectedTerm,
                    skipPaymentCall: true,
                    ...(held.rateType && { rateType: held.rateType }),
                    ...(held.rateType && held.residual && isSelectedTerm && { pinDealLevelRateType: true })
                },
                offerType
            )
        );
        if (isNumber(held.buyRate)) {
            store.dispatch(updateBuyRateOverride(held.buyRate as number, termMonths, { isSelectedTerm, withPayment: false }));
        }
        if (held.residual) {
            store.dispatch(
                updateBaseResidualOverride(
                    { value: held.residual.value, months: termMonths, unitType: held.residual.unitType },
                    false
                )
            );
        }
        store.dispatch(setPaymentGridFallbackRates(offerType, { [termMonths]: null }));
    });
};

const hasHeldLeasePair = (state: State, offerType: OfferType, termMonths: number): boolean => {
    const held = getPaymentGridFallbackRates(state, offerType)[termMonths];
    return isLeaseRedesign(state, offerType) && isNumber(held?.sellRate) && held?.residual != null;
};

const leftTermToHold = (
    state: State,
    offerType: OfferType,
    gridTerms: (number | null)[],
    key: number,
    newTermVal: number | null | undefined
): number | undefined => {
    const leftTermMonths = gridTerms[key];
    if (!newTermVal || !isNumber(leftTermMonths) || leftTermMonths === newTermVal) return undefined;
    if (!isAspPgRedesignEnabled(state, offerType) || isGridAspOff(state, offerType)) return undefined;
    const isStillShown = gridTerms.some((termMonths, columnIndex) => columnIndex !== key && termMonths === leftTermMonths);
    return isStillShown ? undefined : (leftTermMonths as number);
};

const holdLeftTermUntilPriced = (
    store: PaymentGridMiddlewareStore,
    preEditState: State,
    offerType: OfferType,
    leftTermMonths: number,
    incomingTermMonths: number
): void => {
    const held = getPaymentGridLeftTerms(preEditState, offerType);
    const carried = held[leftTermMonths] ?? [];
    const leftTerms = [...new Set([...(held[incomingTermMonths] ?? []), ...carried, leftTermMonths])].filter(
        (termMonths) => termMonths !== incomingTermMonths
    );

    store.dispatch(
        setPaymentGridLeftTerms(offerType, {
            [incomingTermMonths]: leftTerms,
            ...(carried.length && { [leftTermMonths]: null })
        })
    );
};

const releaseLeftTermsOncePriced = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType
): void => {
    const state = store.getState();
    const held = getPaymentGridLeftTerms(state, offerType);
    if (!Object.keys(held).length) return;

    const responseTerms = action.payload?.terms;
    const termIds: string[] = responseTerms?.[RESPONSE_TERM_IDS_KEY[offerType]] ?? [];
    const pricedTerms = [
        ...new Set(
            termIds
                .map((termId) => responseTerms.byId?.[termId])
                .filter((term) => term?.monthlyPayment != null && held[term.months] != null)
                .map((term) => term.months as number)
        )
    ];
    if (!pricedTerms.length) return;

    const gridTerms = getPaymentGridTerms(state, offerType);
    const dealTermMonths = getSelectedTermMonths(state, offerType);
    pricedTerms
        .flatMap((termMonths) => held[termMonths])
        .filter((leftTermMonths) => !gridTerms.includes(leftTermMonths) && leftTermMonths !== dealTermMonths)
        .forEach((leftTermMonths) => store.dispatch(clearPaymentGridColumnFigures(offerType, leftTermMonths)));

    store.dispatch(
        setPaymentGridLeftTerms(
            offerType,
            pricedTerms.reduce<Record<number, null>>((released, termMonths) => {
                released[termMonths] = null;
                return released;
            }, {})
        )
    );
};

const isDealUpqTermEdit = (state: State, offerType: OfferType): boolean =>
    isAspPgRedesignEnabled(state, offerType) && isGridAspOff(state, offerType) && !isLeaseRedesign(state, offerType);

const handleUpdatePaymentGridTerm = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    state: State,
    offerType: OfferType
) => {
    const { key, val: newTermVal } = action.payload;
    const termMonthsList = getUniquePaymentGridTerms(updatedState);
    const currentTermMonth = getSelectedTermMonths(updatedState);
    const currentDownPayment = getDownPayment(updatedState) || 0;
    const initialPaymentDiscount = getInitialPaymentDiscount(updatedState);
    const prevTermsList = getPaymentGridTerms(state);
    const prevTerm = prevTermsList[key];
    const editedColumnIndex = isAspPgRedesignEnabled(updatedState, offerType)
        ? resolveEditedColumnIndex(prevTermsList, key, newTermVal)
        : undefined;

    //Type guards
    if (!isNumber(currentTermMonth)) return;

    if (!newTermVal) {
        selectNewActiveCellForTerm(
            store,
            termMonthsList,
            currentTermMonth,
            currentDownPayment,
            initialPaymentDiscount,
            offerType,
            updatedState,
            state,
            key
        );
        return;
    }

    if (editedColumnIndex != null && isDealUpqTermEdit(updatedState, offerType)) {
        const carriedOverride = getOwnColumnOverride(state, offerType, key, prevTerm);
        if (carriedOverride?.sellRate != null) {
            store.dispatch(
                setPaymentGridColumnOverride(offerType, editedColumnIndex, {
                    sellRate: carriedOverride.sellRate,
                    ...(carriedOverride.rateType != null && { rateType: carriedOverride.rateType })
                })
            );
            const restored = getPaymentGridRestoredInputs(state, offerType)?.[key];
            if (restored?.term === prevTerm && restored.sellRate === false) {
                store.dispatch(
                    setPaymentGridRestoredInputs(offerType, {
                        ...getPaymentGridRestoredInputs(store.getState(), offerType),
                        [editedColumnIndex]: { ...restored, term: newTermVal }
                    })
                );
            }
        }
    }

    const isActiveColumnEdited =
        isAspPgRedesignEnabled(updatedState, offerType) && getResolvedPaymentGridSelectedColumnIndex(state, offerType) === key;

    if (isActiveColumnEdited && !getPaymentGridDealTermBeforeEdit(state, offerType)) {
        const preEditDetails = getOfferDetails(state, offerType);
        const heldOverrides =
            prevTerm === currentTermMonth
                ? pickGridWrittenOverrides(
                      preEditDetails.overridesByTerm?.[prevTerm],
                      (preEditDetails.paymentGridData as GridPaymentGridData)?.gridWrittenTerms?.[prevTerm]
                  )
                : undefined;
        store.dispatch(
            setPaymentGridDealTermBeforeEdit(
                offerType,
                getTermIdForOfferType(state, offerType),
                heldOverrides && { [currentTermMonth]: heldOverrides }
            )
        );
    }

    updateSelectedTermIfNeeded(
        store,
        termMonthsList,
        currentTermMonth,
        newTermVal,
        offerType,
        currentDownPayment,
        isActiveColumnEdited,
        isDealParkedOffActiveColumn(state, offerType)
    );

    const currentTermId = getTermId(offerType, currentTermMonth, currentDownPayment);
    const isSingleSelectedOptionChanged = prevTermsList.filter((term) => term === prevTerm).length < 2;
    const termId = getTermId(offerType, prevTerm ?? undefined, currentDownPayment);
    const isCurrentTermEdited = currentTermId === termId;
    const isMainPayment = isCurrentTermEdited && isSingleSelectedOptionChanged;
    // The engine this edit prices on: Auto Select off as the grid recorded it, not the active cell's engine. Finance has
    // no build, so there it is still the endpoint a cell selection moves.
    const isUPQ = isGridAspOff(updatedState, offerType);
    const isProgramQuotes = !isUPQ;

    if (
        isAspPgRedesignEnabled(updatedState, offerType) &&
        isProgramQuotes &&
        newTermVal !== prevTerm &&
        newTermVal !== currentTermMonth &&
        !isTermDuplicated(getPaymentGridTerms(updatedState, offerType), newTermVal)
    ) {
        if (isLeaseRedesign(updatedState, offerType)) store.dispatch(clearPaymentGridColumnFigures(offerType, newTermVal));
        store.dispatch(clearPaymentGridTermRecords(offerType, newTermVal));
    }

    const leftTerm = leftTermToHold(state, offerType, prevTermsList, key, newTermVal);
    if (isNumber(leftTerm)) holdLeftTermUntilPriced(store, state, offerType, leftTerm as number, newTermVal);

    const program = getPaymentProgramInfoByTerm(updatedState, offerType, newTermVal);

    // Read from the PRE-edit state: parked rates are keyed by column index and the reducer has already re-sorted.
    const pendingSellRate = isAspPgRedesignEnabled(updatedState, offerType)
        ? getPaymentGridPendingSellRates(state, offerType)[key]
        : undefined;

    if (isNumber(pendingSellRate)) {
        // ASP on still attempts program-quotes; the ASP-off write is left to updateUPQRatesForTerm, or the override goes out twice.
        if (!isUPQ) {
            store.dispatch(
                updateSellRateOverride(
                    pendingSellRate,
                    newTermVal,
                    {
                        isSelectedTerm: isCurrentTermEdited,
                        skipPaymentCall: true,
                        userIntent: PAYMENT_GRID_SELL_RATE_INTENT
                    },
                    offerType
                )
            );
        }
        store.dispatch(setPaymentGridWrittenTerm(offerType, newTermVal, 'rate'));
        const isDuplicatedTerm = isTermDuplicated(getPaymentGridTerms(updatedState, offerType), newTermVal);
        const keepsParkedRateOnColumn = getFeatureToggle(updatedState, 'US2139463_PG_Override_Indicator')
            ? isLeaseRedesign(updatedState, offerType) || !isDuplicatedTerm
            : isLeaseRedesign(updatedState, offerType) && isDuplicatedTerm;
        if (editedColumnIndex != null && keepsParkedRateOnColumn) {
            store.dispatch(setPaymentGridColumnOverride(offerType, editedColumnIndex, { sellRate: pendingSellRate }));
        }
    }

    // Read from the PRE-edit state, before this column's own record stops describing the term being left.
    holdFallbackRateForTermChange(store, state, offerType, {
        prevTerm,
        incomingTermMonths: newTermVal,
        isCurrentTermEdited,
        isUPQ,
        pendingSellRate
    });

    // Discover even when a program is cached: qualification depends on this column's down payment and rate, and the cache never clears.
    const shouldRunDiscovery =
        !isExplicitLenderProgramGridActive(updatedState, offerType) &&
        isProgramQuotes &&
        (isAspPgRedesignEnabled(updatedState, offerType) || !program);

    if (shouldRunDiscovery) {
        // The same rule as the grid-open probe. `currentDownPayment` reads 0 on Lease, which is why the anchor exists:
        // a term edit re-discovered at zero and dropped every advance-capped program.
        const discoveryDownPayment =
            getRedesignDiscoveryAnchor(updatedState, offerType, getUniquePaymentGridCashDown(updatedState, offerType)[1]) ??
            currentDownPayment;
        const action = loadPaymentGridTermLenderProgram(key, newTermVal, discoveryDownPayment, offerType, {
            skipProgramOverride: isAspPgRedesignEnabled(updatedState, offerType),
            dropOverrides: isAspPgRedesignEnabled(updatedState, offerType),
            forceProgramQuotes: isAspPgRedesignEnabled(updatedState, offerType)
        });
        const discoveryAction = addPaymentGridMeta(action, {
            isMainPayment,
            ...(isMainPayment && { mainPaymentActionType: types.SELECTED_TERM })
        });
        if (editedColumnIndex != null) {
            discoveryAction.meta = {
                ...discoveryAction.meta,
                ...(shouldIgnoreTermFigure(updatedState, offerType, editedColumnIndex, 'sellRate') && {
                    ignoreTermSellRateOverride: true
                }),
                gridColumnIndex: editedColumnIndex,
                gridColumnIndices: [editedColumnIndex]
            };
        }
        store.dispatch(discoveryAction);
        return;
    }

    if (isUPQ) {
        updateUPQRatesForTerm(store, updatedState, currentTermMonth, isCurrentTermEdited, prevTerm, newTermVal, pendingSellRate);
    }

    store.dispatch(
        loadPaymentGridTermData(key, newTermVal, undefined, {
            ...(editedColumnIndex != null && { gridColumnIndex: editedColumnIndex })
        })
    );
};

const selectNewActiveCellForTerm = (
    store: PaymentGridMiddlewareStore,
    termMonthsList: number[],
    currentTermMonth: number,
    currentDownPayment: number,
    initialPaymentDiscount: number | undefined,
    offerType: OfferType,
    updatedState: State,
    state: State,
    key: number
) => {
    if (isAspPgRedesignEnabled(updatedState, offerType)) {
        if (getResolvedPaymentGridSelectedColumnIndex(state, offerType) !== key) return;

        const rowIndex = getResolvedPaymentGridSelectedRowIndex(state, offerType);
        if (rowIndex == null) return;

        const gridTerms = getPaymentGridTerms(updatedState, offerType);
        const columnIndex = nearestIndexWhere(gridTerms.length, key, (index) =>
            isQuotedGridCell(updatedState, offerType, index, rowIndex)
        );
        if (columnIndex != null) {
            moveDealToGridCell(store, updatedState, offerType, columnIndex, rowIndex);
            return;
        }

        const loadingColumn = nearestIndexWhere(
            gridTerms.length,
            key,
            (index) =>
                hasTerm(gridTerms[index]) && isPaymentGridCellLoading(updatedState, offerType, index, rowIndex, gridTerms[index])
        );
        if (loadingColumn == null) return;

        store.dispatch(setPaymentGridSelectedColumn(offerType, loadingColumn));
        store.dispatch(setPaymentGridSelectedRow(offerType, rowIndex));
        return;
    }

    if (termMonthsList.includes(currentTermMonth)) return;

    const shiftedTerm = key > 1 ? termMonthsList[key - 1] : termMonthsList[0];
    const termId = getTermId(offerType, shiftedTerm, {
        DOWNPAYMENT: currentDownPayment,
        FORDFLEXBUY: initialPaymentDiscount
    });

    if (termId) {
        store.dispatch(selectedTerm(termId));
        return;
    }

    const item = getNewSelectedCell(updatedState);
    if (item) {
        store.dispatch(selectedTerm(item.id));
    }
};

const updateSelectedTermIfNeeded = (
    store: PaymentGridMiddlewareStore,
    termMonthsList: number[],
    currentTermMonth: number,
    newTermVal: number,
    offerType: OfferType,
    currentDownPayment: number,
    isActiveColumnEdited: boolean | undefined,
    isDealParked: boolean
) => {
    if (isActiveColumnEdited || (!termMonthsList.includes(currentTermMonth) && !isDealParked)) {
        const newTermID = getTermId(offerType, newTermVal, currentDownPayment);
        store.dispatch(selectedTerm(newTermID, { payment: false, meta: { userIntent: GRID_AXIS_EDIT_INTENT } }));
    }
};

const resolveSellRateFromReplacedTerm = (
    updatedState: State,
    offerType: OfferType,
    replacedTermMonth: number,
    carriedSellRate: number | undefined
): number => {
    if (isNumber(carriedSellRate)) return carriedSellRate;
    return getSellRateOverrideByOfferTypeAndTerm(updatedState, offerType, replacedTermMonth, true, true) ?? 0;
};

const updateUPQRatesForTerm = (
    store: PaymentGridMiddlewareStore,
    updatedState: State,
    currentTermMonth: number,
    isCurrentTermEdited: boolean,
    prevTermMonth: number | null,
    futureTermMonth: number,
    parkedSellRate?: number
) => {
    const termMonth = prevTermMonth || currentTermMonth;
    const offerType = getCurrentOfferType(updatedState);
    const isRedesignLease = isLeaseRedesign(updatedState, offerType);

    if (isRedesignLease && !isNumber(parkedSellRate)) {
        const inheritedResidual = getResidualInfoOverrideByOfferTypeTerm(updatedState, offerType, futureTermMonth);
        if (inheritedResidual?.programResidualPercent != null || inheritedResidual?.programResidual != null) {
            store.dispatch(
                removeProgramResidualOverride(
                    futureTermMonth,
                    false,
                    { isSelectedTerm: false, userIntent: PAYMENT_GRID_RESIDUAL_INTENT },
                    offerType
                )
            );
        }
        return;
    }

    // A parked rate outranks the rate carried over from the term being replaced.
    const carriedSellRate = isNumber(parkedSellRate)
        ? parkedSellRate
        : getSellRateOverrideByOfferTypeAndTerm(updatedState, offerType, termMonth, false);

    const sellRate = resolveSellRateFromReplacedTerm(updatedState, offerType, termMonth, carriedSellRate);

    store.dispatch(
        updateSellRateOverride(sellRate, futureTermMonth, {
            isSelectedTerm: isCurrentTermEdited,
            skipPaymentCall: true,
            userIntent: PAYMENT_GRID_SELL_RATE_INTENT
        })
    );
    store.dispatch(
        updateBuyRateOverride(sellRate, futureTermMonth, {
            isSelectedTerm: isCurrentTermEdited,
            withPayment: false,
            userIntent: PAYMENT_GRID_SELL_RATE_INTENT
        })
    );
};

const handleLoadPaymentGridTermData = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    state: State,
    offerType: OfferType
) => {
    const { key, termMonth } = action.payload;
    const resolvedOfferType = resolveGridOfferType(action, offerType);

    // Off-current-offer column reload (Alt Deal Structure lease single-term edit while the deal is on
    // finance). Fire just the edited term across rows, tagged with the target offer type, and never as
    // a main payment — we must not touch the deal's selected term/offer. Mirrors loadRegularPaymentGridData.
    if (resolvedOfferType !== offerType) {
        gridRowsFor(updatedState, resolvedOfferType).forEach(({ rowIndex, downPayment }) => {
            const updateRow = updatePaymentGridRow(resolvedOfferType, [termMonth], downPayment, {
                ...(isAspPgRedesignEnabled(updatedState, resolvedOfferType) && {
                    gridColumnIndices: coveredColumnIndices(updatedState, resolvedOfferType, [termMonth]),
                    gridRowIndices: [rowIndex]
                })
            });
            store.dispatch(
                addPaymentGridMeta(
                    tagOfferType(updateRow, resolvedOfferType),
                    withLeaseProgramPin(updatedState, resolvedOfferType, { isMainPayment: false })
                )
            );
        });
        return;
    }

    const prevTermsList = getPaymentGridTerms(state);
    const currentTermMonth = getSelectedTermMonths(updatedState);
    const currentDownPayment = getDownPayment(updatedState);

    //Type guard
    if (!isNumber(currentTermMonth)) return;
    const currentTermId = getTermId(offerType, currentTermMonth, currentDownPayment);
    const prevTerm = prevTermsList[key];
    const isSingleSelectedOptionChanged = prevTermsList.filter((term) => term === prevTerm).length < 2;

    // A new term has no recorded source, so the engine comes from the discriminator and this term's own program fact -
    // never the replaced term's source.
    const isRedesignTermEdit = isAspPgRedesignEnabled(updatedState, offerType);
    // Auto Select off as the grid recorded it (the endpoint on Finance); this term's own program fact decides the rest.
    const isDealUserProgramQuotes = isGridAspOff(updatedState, offerType);
    // Prefer this discovery's verdict; the program cache is only consulted when no discovery ran.
    const discoveryVerdict = isExplicitLenderProgramGridActive(updatedState, offerType)
        ? undefined
        : action.meta?.hasDiscoveredProgram;
    const hasProgramForTerm =
        typeof discoveryVerdict === 'boolean' ? discoveryVerdict : hasGridProgramForTerm(updatedState, offerType, termMonth);
    const forceUserProgramQuotes = isRedesignTermEdit ? isDealUserProgramQuotes || !hasProgramForTerm : undefined;

    const termEditColumnIndex = (action.meta?.gridColumnIndex as number | undefined) ?? key;
    const isTermEditSibling = isSharedTermSibling(updatedState, offerType, termEditColumnIndex, termMonth);

    const restoresHeldLeasePair =
        Boolean(forceUserProgramQuotes) && !isTermEditSibling && hasHeldLeasePair(updatedState, offerType, termMonth);
    if (restoresHeldLeasePair) restoreFallbackRatesForTerms(store, offerType, [termMonth]);
    const judgedState = restoresHeldLeasePair ? store.getState() : updatedState;

    // One authority for "can this column be priced", shared with the open and cash-down paths. An ad-hoc test
    // disagreed with `resolvePriceableColumnSources` for the ASP-off current-term column, so the two paths recorded
    // opposite verdicts. `hasResolvedProgram` is overridden because this path holds its own discovery verdict.
    const isColumnPriceable =
        resolvePriceableColumnSources({
            ...leaseColumnFacts(judgedState, offerType, [termMonth], { columnIndex: termEditColumnIndex }),
            isDealOnUserProgramQuotes: false,
            hasResolvedProgram: () => hasProgramForTerm
        })[termMonth] != null;

    const { quotedTerms, columnSources } = getPaymentGridColumnView(judgedState, offerType);
    const siblingHalves = isTermEditSibling
        ? getGridColumnPairHalves(judgedState, offerType, termEditColumnIndex, termMonth)
        : undefined;

    const isAwaitingPair =
        isLeaseRedesign(updatedState, offerType) &&
        isColumnAwaitingPair({
            source: isTermEditSibling
                ? getColumnVerdict(judgedState, offerType, termEditColumnIndex, termMonth)
                : columnSources[termMonth],
            isUserProgramQuotesEngine: Boolean(forceUserProgramQuotes),
            hasUserSuppliedRate: siblingHalves
                ? siblingHalves.hasRate
                : hasUserSuppliedGridSellRate(judgedState, offerType, termMonth),
            hasUserSuppliedResidual: siblingHalves
                ? siblingHalves.hasResidual
                : hasGridColumnPricingResidual(judgedState, offerType, termMonth),
            isQuoted: !isTermEditSibling && quotedTerms.has(termMonth),
            isRefused: false
        });

    const recordTermEditVerdict = (verdict: PaymentGridSource) =>
        store.dispatch(
            isTermEditSibling
                ? setPaymentGridDecidedColumns(offerType, { [termEditColumnIndex]: verdict })
                : setPaymentGridColumnSources(offerType, { [termMonth]: verdict })
        );

    // A column nothing can price is recorded unavailable here and now: no call goes out, so no response will arrive to
    if (isLeaseRedesign(updatedState, offerType) && (!isColumnPriceable || isAwaitingPair)) {
        recordTermEditVerdict(PaymentGridSource.Unavailable);
        parkDealOnTermBeforeEdit(store, offerType);
        return;
    }

    // ASP on with no program found: this call already IS the column's fallback, so record it or the pass spends a second
    // identical UPQ round. Lease dispatches no fallback round and its resolution pass is the single writer.
    if (isRedesignTermEdit && !isLeaseRedesign(updatedState, offerType) && !isDealUserProgramQuotes && !hasProgramForTerm) {
        recordTermEditVerdict(PaymentGridSource.UserProgramQuotes);
    }

    // Promoted here, not in the fallback pass: these rows ARE the column's fallback, and the pass runs after they
    if (forceUserProgramQuotes && !isTermEditSibling) restoreFallbackRatesForTerms(store, offerType, [termMonth]);

    // One id for the three rows this term edit fans out, so the column's cells are held together until all three
    // have settled instead of painting one at a time. Lease only.
    const termEditRoundId = isLeaseRedesign(updatedState, offerType) ? nextGridRoundId() : undefined;
    const termEditColumnOverride = getOwnColumnOverride(updatedState, offerType, termEditColumnIndex, termMonth);
    // Finance UPQ rate order: own column override, then the term's own override (read after the restore above), then the active cell.
    const termEditOwnSellRate =
        offerType === FINANCE &&
        forceUserProgramQuotes &&
        !shouldIgnoreTermFigure(updatedState, offerType, termEditColumnIndex, 'sellRate')
            ? getSellRateOverrideByOfferTypeAndTerm(store.getState(), offerType, termMonth)
            : undefined;
    const termEditColumnSellRate =
        termEditColumnOverride?.sellRate ??
        termEditOwnSellRate ??
        activeCellSellRate(updatedState, offerType, Boolean(forceUserProgramQuotes));

    gridRowsFor(updatedState, offerType).forEach(({ rowIndex, downPayment }) => {
        const termId = getTermId(offerType, prevTerm ?? undefined, downPayment);
        const isCurrentTermEdited = currentTermId === termId;
        const isMainPayment = isCurrentTermEdited && isSingleSelectedOptionChanged;

        const updateRow = updatePaymentGridRow(offerType, [termMonth], downPayment, {
            ...(typeof forceUserProgramQuotes === 'boolean' && { forceUseGenericCalcs: forceUserProgramQuotes }),
            ...(isRedesignTermEdit && { gridColumnIndices: [termEditColumnIndex], gridRowIndices: [rowIndex] }),
            ...(isRedesignTermEdit &&
                shouldIgnoreTermFigure(updatedState, offerType, termEditColumnIndex, 'sellRate') && {
                    ignoreTermSellRateOverride: true
                }),
            ...(isRedesignTermEdit &&
                termEditColumnOverride?.residualInfo == null &&
                shouldIgnoreTermFigure(updatedState, offerType, termEditColumnIndex, 'residualInfo') && {
                    ignoreTermResidualOverride: true
                }),
            ...(isRedesignTermEdit && termEditColumnSellRate != null && { columnSellRate: termEditColumnSellRate }),
            ...(isRedesignTermEdit &&
                isTermDuplicated(getPaymentGridTerms(updatedState, offerType), termMonth) && {
                    columnScopedForDuplicateTerm: true
                })
        });
        store.dispatch(
            addPaymentGridMeta(
                updateRow,
                withLeaseProgramPin(updatedState, offerType, {
                    isMainPayment,
                    ...(isMainPayment && { mainPaymentActionType: types.SELECTED_TERM }),
                    ...(termEditRoundId && { gridRoundId: termEditRoundId, gridRoundTerms: [termMonth] })
                })
            )
        );
    });
};

type RevivedColumns = {
    persistedColumnSources: Record<number, PaymentGridSource>;
    priceableByFacts: Record<number, PaymentGridSource>;
    reDecided: Record<number, PaymentGridSource>;
    restoredTerms: number[];
    restoredColumnPrograms: Record<number, GenericObject>;
};

const reviveStuckColumns = (
    store: PaymentGridMiddlewareStore,
    updatedState: State,
    offerType: OfferType,
    termMonthsList: number[]
): RevivedColumns => {
    const isLeaseRedesignRow = isLeaseRedesign(updatedState, offerType);

    const persistedColumnSources = getPaymentGridColumnSources(updatedState, offerType);
    const refusedTermFlags = getPaymentGridRefusedTerms(updatedState, offerType);

    const isReconsiderable = (termMonths: number) =>
        persistedColumnSources[termMonths] === PaymentGridSource.Unavailable &&
        (!isLeaseRedesignRow || refusedTermFlags[termMonths] === true);

    const gridTermPositions = getPaymentGridTerms(updatedState, offerType);
    const soleColumnOf = (termMonths: number): number | undefined => {
        const positions = gridTermPositions.reduce<number[]>((held, gridTerm, columnIndex) => {
            if (gridTerm === termMonths) held.push(columnIndex);
            return held;
        }, []);
        return positions.length === 1 ? positions[0] : undefined;
    };

    const rehydratedTermOverrides = termMonthsList.reduce<Record<number, GenericObject>>((byTerm, termMonths) => {
        if (!isReconsiderable(termMonths)) return byTerm;
        const columnIndex = soleColumnOf(termMonths);
        if (columnIndex == null) return byTerm;
        const own = getOwnColumnOverride(updatedState, offerType, columnIndex, termMonths);

        const entry: GenericObject = {
            ...(own?.sellRate != null &&
                getSellRateOverrideByOfferTypeAndTerm(updatedState, offerType, termMonths) == null && {
                    sellRate: own.sellRate,
                    ...(own.rateType && { rateType: own.rateType })
                }),
            ...(own?.residualInfo &&
                !getResidualInfoOverrideByOfferTypeTerm(updatedState, offerType, termMonths) && {
                    residualInfo: own.residualInfo
                })
        };
        if (Object.keys(entry).length) byTerm[termMonths] = entry;
        return byTerm;
    }, {});
    if (Object.keys(rehydratedTermOverrides).length) {
        store.dispatch(restorePaymentGridTermOverrides(offerType, rehydratedTermOverrides));
    }
    const factsState = Object.keys(rehydratedTermOverrides).length ? store.getState() : updatedState;

    /**
     * A Lease column nothing can price is not asked for a payment here either. `splitTermsByEngine` drops a column
     * recorded `unavailable`, but an UNDECIDED one went out on the deal's engine and came back priced from a
     * substituted rate. Derived facts fill only the undecided columns; a recorded verdict always wins.
     */
    const priceableByFacts = isLeaseRedesignRow
        ? resolvePriceableColumnSources(leaseColumnFacts(factsState, offerType, termMonthsList))
        : {};

    const dealIsUserProgramQuotes = isGridUserProgramQuotes(updatedState, offerType);
    const dealTermMonths = getSelectedTermMonths(updatedState, offerType);
    const dealHasExplicitLenderProgram =
        isExplicitLenderProgramGridActive(updatedState, offerType) && hasDealLenderProgram(updatedState, offerType);

    const recordedPrograms = getPaymentGridOriginalColumnPrograms(updatedState, offerType);
    const isLenderProgram = (program: GenericObject | undefined): program is GenericObject =>
        isValidProgram(program) && !isUserPricedProgram(program);

    const lenderProgramForTerm = (termMonths: number): GenericObject | undefined => {
        const latestProgram = getGridColumnProgramInfo(updatedState, offerType, termMonths);
        if (isLenderProgram(latestProgram)) return latestProgram;

        if (isLenderProgram(recordedPrograms[termMonths])) return recordedPrograms[termMonths];

        if (termMonths !== dealTermMonths) return undefined;
        const dealProgram = getSelectedTerm(updatedState, offerType).lenderProgram;
        return isLenderProgram(dealProgram) ? dealProgram : undefined;
    };

    const hasLenderProgramForTerm = (termMonths: number): boolean =>
        dealHasExplicitLenderProgram || lenderProgramForTerm(termMonths) != null;

    const financeIntendedSource = (termMonths: number): PaymentGridSource =>
        (dealIsUserProgramQuotes && termMonths === dealTermMonths) || !hasLenderProgramForTerm(termMonths)
            ? PaymentGridSource.UserProgramQuotes
            : PaymentGridSource.ProgramQuotes;

    const originalColumnSources = getPaymentGridOriginalColumnSources(updatedState, offerType);

    const reDecided = termMonthsList.reduce<Record<number, PaymentGridSource>>((sources, termMonths) => {
        if (!isReconsiderable(termMonths)) return sources;
        const derived = isLeaseRedesignRow ? priceableByFacts[termMonths] : financeIntendedSource(termMonths);
        const intended = isLeaseRedesignRow && derived == null ? undefined : originalColumnSources[termMonths] ?? derived;
        if (intended != null) sources[termMonths] = intended;
        return sources;
    }, {});

    const restoredTerms = Object.keys(reDecided).map(Number);

    if (Object.keys(reDecided).length) {
        store.dispatch(
            setPaymentGridColumnSources(offerType, reDecided, {
                ...(isLeaseRedesignRow && { refusedTerms: {}, pairRejectedTerms: {} })
            })
        );
        restoreFallbackRatesForTerms(store, offerType, restoredTerms);

        const decidedColumns = getPaymentGridDecidedColumns(updatedState, offerType);
        const revivedHolders = gridTermPositions.reduce<Record<number, PaymentGridSource>>((holders, gridTerm, columnIndex) => {
            const revived = isNumber(gridTerm) ? reDecided[gridTerm as number] : undefined;
            if (
                revived != null &&
                soleColumnOf(gridTerm as number) == null &&
                decidedColumns[columnIndex] === PaymentGridSource.Unavailable
            ) {
                holders[columnIndex] = revived;
            }
            return holders;
        }, {});
        if (Object.keys(revivedHolders).length) store.dispatch(setPaymentGridDecidedColumns(offerType, revivedHolders));
    }

    const restoredColumnPrograms = restoredTerms.reduce<Record<number, GenericObject>>((programs, termMonths) => {
        if (reDecided[termMonths] !== PaymentGridSource.ProgramQuotes) return programs;
        const program = lenderProgramForTerm(termMonths);
        if (program) programs[termMonths] = program;
        return programs;
    }, {});

    return { persistedColumnSources, priceableByFacts, reDecided, restoredTerms, restoredColumnPrograms };
};

const handleUpdatePaymentGridCashDown = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    state: State,
    offerType: OfferType
) => {
    const { key, val: cashDown } = action.payload;
    const termMonthsList = getUniquePaymentGridTerms(updatedState);
    const isFlexBuy = isFlexBuyPayment(updatedState);
    //currentTermMonth allowed to be undefined
    const currentTermMonth = getSelectedTermMonths(updatedState);
    const currentDownPayment = getDownPayment(updatedState) || 0;
    const downPaymentList = getUniquePaymentGridCashDown(updatedState);
    const prevDownPaymentList = isFlexBuy ? getFlexBuyPaymentGridCashDowns(state) : (getPaymentGridCashDown(state) as number[]);

    if (cashDown === null) {
        handleNullCashDown(store, downPaymentList, currentDownPayment, currentTermMonth, offerType, key, updatedState, state);
        return;
    }

    const isActiveRowEdited =
        isAspPgRedesignEnabled(updatedState, offerType) && getResolvedPaymentGridSelectedRowIndex(state, offerType) === key;
    const parkedDealTerm =
        isActiveRowEdited && isNumber(currentTermMonth) && !termMonthsList.includes(currentTermMonth as number)
            ? (currentTermMonth as number)
            : undefined;

    updateSelectedTermForCashDown(
        store,
        downPaymentList,
        currentDownPayment,
        currentTermMonth,
        cashDown,
        offerType,
        isActiveRowEdited
    );

    const isMainPayment =
        parkedDealTerm != null ||
        calculateIsMainPaymentForCashDown(
            termMonthsList,
            currentTermMonth,
            currentDownPayment,
            prevDownPaymentList,
            key,
            offerType
        );

    if (offerType === FINANCE && isFlexBuy) {
        handleFlexBuyCashDownUpdate(store, state, updatedState, prevDownPaymentList, key, cashDown);
    } else if (isAspPgRedesignEnabled(updatedState, offerType)) {
        // A Lease row can span both engines (a user-priced column beside programmed neighbours) and a request carries
        // one engine, so such a row costs two calls. The shared `gridRoundId` below is what keeps it at two.
        const isLeaseRedesignRow = isLeaseRedesign(updatedState, offerType);

        const { persistedColumnSources, priceableByFacts, reDecided, restoredTerms, restoredColumnPrograms } = reviveStuckColumns(
            store,
            updatedState,
            offerType,
            termMonthsList
        );

        const recordedColumnSources = { ...persistedColumnSources, ...reDecided };
        const isRequestable = (termMonths: number) =>
            priceableByFacts[termMonths] != null || recordedColumnSources[termMonths] != null;

        // Recorded now so the column stops being undecided and no later edit can price it either. Merged, so no
        // other column's verdict is touched.
        const unpriceableUndecided = isLeaseRedesignRow ? termMonthsList.filter((term) => !isRequestable(term)) : [];
        if (unpriceableUndecided.length) {
            store.dispatch(
                setPaymentGridColumnSources(
                    offerType,
                    unpriceableUndecided.reduce<Record<number, PaymentGridSource>>((sources, termMonths) => {
                        sources[termMonths] = PaymentGridSource.Unavailable;
                        return sources;
                    }, {})
                )
            );
        }

        const {
            priceableTerms: priceableTermList,
            programQuotesTerms,
            userProgramQuotesTerms,
            isRowSplit
        } = splitTermsByEngine({
            termList: [
                ...(isLeaseRedesignRow ? termMonthsList.filter(isRequestable) : termMonthsList),
                ...(parkedDealTerm != null ? [parkedDealTerm] : [])
            ],
            columnSources: withSharedTermsJudgedByHolder(
                store.getState(),
                offerType,
                isLeaseRedesignRow ? { ...priceableByFacts, ...recordedColumnSources } : recordedColumnSources
            ),
            dealIsUserProgramQuotes: isGridUserProgramQuotes(updatedState, offerType)
        });

        // One id for both halves of a split row - see nextGridRoundId. Lease only.
        const gridRoundId = isLeaseRedesignRow ? nextGridRoundId() : undefined;

        const buildCashDownRowAction = (
            terms: number[],
            isMain: boolean,
            forceUserProgramQuotes: boolean,
            isSplitPart: boolean,
            columnIndices?: number[],
            columnSellRate?: number,
            ignoreTermOverride?: boolean,
            columnResidualInfo?: GenericObject,
            row: { downPayment: number; rowIndex: number; roundId?: string } = {
                downPayment: cashDown,
                rowIndex: key,
                roundId: gridRoundId
            },
            roundColumns?: number[]
        ) =>
            addPaymentGridMeta(
                updatePaymentGridRow(offerType, terms, row.downPayment, {
                    forceUseGenericCalcs: forceUserProgramQuotes,
                    ...(terms.some((term) => restoredColumnPrograms[term]) && {
                        restoredColumnPrograms: terms.reduce<Record<number, GenericObject>>((programs, term) => {
                            if (restoredColumnPrograms[term]) programs[term] = restoredColumnPrograms[term];
                            return programs;
                        }, {})
                    }),
                    ...(isAspPgRedesignEnabled(updatedState, offerType) && {
                        gridColumnIndices: columnIndices ?? coveredColumnIndices(updatedState, offerType, terms),
                        gridRowIndices: [row.rowIndex]
                    }),
                    ...columnScopedMeta(
                        updatedState,
                        offerType,
                        terms,
                        columnIndices,
                        columnSellRate,
                        columnResidualInfo,
                        ignoreTermOverride
                    ),
                    ...(isSplitPart && { isGridRowSplitPart: true })
                }),
                withLeaseProgramPin(updatedState, offerType, {
                    isMainPayment: isMain,
                    gridRoundId: row.roundId,
                    // Both halves of a split row name the same terms, so the verdict covers every column this edit
                    // asked about rather than only the half that settled last.
                    ...(isAspPgRedesignEnabled(updatedState, offerType) && { gridRoundTerms: priceableTermList }),
                    ...(roundColumns && { gridRoundColumns: roundColumns }),
                    ...(isMain && { mainPaymentActionType: types.UPDATED_DOWN_PAYMENT_IMMEDIATE })
                })
            );

        if (!priceableTermList.length) {
            return;
        }

        // This handler only ever dispatches, so the main half is marked but not routed through next().
        const cashDownSelectedColumn = getPaymentGridDealColumnIndex(updatedState, offerType);

        const dispatchLeaseRowPlan = (
            plan: LeaseRowPlan,
            isMainRow: boolean,
            row?: { downPayment: number; rowIndex: number; roundId?: string }
        ) => {
            // Both halves of a genuine split are marked, so a failure on either can still trigger its own fallback.
            const isSplitPart = isLeaseRowPlanSplit(plan);
            const roundColumns = leaseRowPlanColumns(plan);
            const planMain = isMainRow ? leaseRowPlanMain(plan, cashDownSelectedColumn) : undefined;
            const mainColumnCall = planMain?.columnCall;
            const isProgramMain = Boolean(planMain?.isProgramBatch);
            const isUpqBatchMain = Boolean(planMain?.isUpqBatch);

            plan.columnCalls.forEach((call) =>
                store.dispatch(
                    buildCashDownRowAction(
                        [call.term],
                        call === mainColumnCall,
                        call.forceUserProgramQuotes,
                        isSplitPart,
                        [call.columnIndex],
                        call.sellRate,
                        call.ignoreTermOverride,
                        call.residualInfo,
                        row,
                        roundColumns
                    )
                )
            );
            if (plan.upqTerms.length) {
                store.dispatch(
                    buildCashDownRowAction(
                        plan.upqTerms,
                        isUpqBatchMain,
                        true,
                        isSplitPart,
                        plan.upqColumns,
                        undefined,
                        undefined,
                        undefined,
                        row,
                        roundColumns
                    )
                );
            }
            if (plan.programTerms.length) {
                store.dispatch(
                    buildCashDownRowAction(
                        plan.programTerms,
                        isProgramMain,
                        false,
                        isSplitPart,
                        plan.programColumns,
                        undefined,
                        undefined,
                        undefined,
                        row,
                        roundColumns
                    )
                );
            }
        };

        if (restoredTerms.length) {
            const restoredByEngine = [
                { terms: restoredTerms.filter((term) => reDecided[term] === PaymentGridSource.ProgramQuotes), forceUpq: false },
                {
                    terms: restoredTerms.filter((term) => reDecided[term] === PaymentGridSource.UserProgramQuotes),
                    forceUpq: true
                }
            ].filter(({ terms }) => terms.length);

            const leaseRestorePlan = isLeaseRedesignRow
                ? planLeaseColumnsByOwnedOverride(store.getState(), offerType, restoredTerms)
                : undefined;
            const isRestoreSplit = isLeaseRedesignRow && restoredByEngine.length > 1;

            gridRowsFor(updatedState, offerType)
                .filter(({ rowIndex, downPayment }) => rowIndex !== key && isNumber(downPayment))
                .forEach(({ rowIndex, downPayment }) => {
                    const rowTarget = {
                        downPayment: downPayment as number,
                        rowIndex,
                        roundId: isLeaseRedesignRow ? nextGridRoundId() : undefined
                    };
                    if (leaseRestorePlan) {
                        dispatchLeaseRowPlan(leaseRestorePlan, false, rowTarget);
                        return;
                    }
                    restoredByEngine.forEach(({ terms, forceUpq }) => {
                        const { sharedTerms, columnCalls } = splitTermsByColumn(updatedState, offerType, terms, forceUpq);
                        columnCalls.forEach(({ term, columnIndex, sellRate, residualInfo, ignoreTermOverride }) =>
                            store.dispatch(
                                buildCashDownRowAction(
                                    [term],
                                    false,
                                    forceUpq,
                                    isRestoreSplit,
                                    [columnIndex],
                                    sellRate,
                                    ignoreTermOverride,
                                    residualInfo,
                                    rowTarget
                                )
                            )
                        );
                        if (sharedTerms.length) {
                            store.dispatch(
                                buildCashDownRowAction(
                                    sharedTerms,
                                    false,
                                    forceUpq,
                                    isRestoreSplit,
                                    undefined,
                                    undefined,
                                    undefined,
                                    undefined,
                                    rowTarget
                                )
                            );
                        }
                    });
                });
        }

        const leasePlan = isLeaseRedesignRow
            ? planLeaseColumnsByOwnedOverride(store.getState(), offerType, priceableTermList)
            : undefined;
        if (leasePlan) {
            dispatchLeaseRowPlan(leasePlan, isMainPayment && parkedDealTerm == null);
            if (parkedDealTerm != null) {
                store.dispatch(
                    buildCashDownRowAction(
                        [parkedDealTerm],
                        true,
                        isGridUserProgramQuotes(updatedState, offerType),
                        isLeaseRowPlanSplit(leasePlan),
                        []
                    )
                );
            }
            return;
        }

        for (const call of planGridRowCalls(
            { priceableTerms: priceableTermList, programQuotesTerms, userProgramQuotesTerms },
            { isRowSplit, isMainPayment, currentTermMonth }
        )) {
            const { sharedTerms, columnCalls } = splitTermsByColumn(
                updatedState,
                offerType,
                call.terms,
                call.forceUserProgramQuotes
            );

            const mainColumnIndex = call.isMain ? cashDownSelectedColumn : undefined;

            columnCalls.forEach(({ term, columnIndex, sellRate, residualInfo, ignoreTermOverride }) =>
                store.dispatch(
                    buildCashDownRowAction(
                        [term],
                        columnIndex === mainColumnIndex,
                        call.forceUserProgramQuotes,
                        call.isSplitPart,
                        [columnIndex],
                        sellRate,
                        ignoreTermOverride,
                        residualInfo
                    )
                )
            );
            if (sharedTerms.length) {
                const isSharedMain = call.isMain && !columnCalls.some(({ columnIndex }) => columnIndex === mainColumnIndex);
                store.dispatch(buildCashDownRowAction(sharedTerms, isSharedMain, call.forceUserProgramQuotes, call.isSplitPart));
            }
        }
    } else {
        const updateRow = updatePaymentGridRow(offerType, termMonthsList, cashDown);
        store.dispatch(
            addPaymentGridMeta(updateRow, {
                isMainPayment,
                ...(isMainPayment && { mainPaymentActionType: types.UPDATED_DOWN_PAYMENT_IMMEDIATE })
            })
        );
    }
};

const handleNullCashDown = (
    store: PaymentGridMiddlewareStore,
    downPaymentList: number[],
    currentDownPayment: number,
    currentTermMonth: number | undefined,
    offerType: OfferType,
    key: number,
    updatedState: State,
    state: State
) => {
    if (isAspPgRedesignEnabled(updatedState, offerType)) {
        if (getResolvedPaymentGridSelectedRowIndex(state, offerType) !== key) return;

        const columnIndex = getResolvedPaymentGridSelectedColumnIndex(state, offerType);
        const gridCashDown = getPaymentGridCashDown(updatedState, offerType);
        const rowIndex =
            columnIndex == null
                ? undefined
                : nearestIndexWhere(gridCashDown.length, key, (index) =>
                      isQuotedGridCell(updatedState, offerType, columnIndex, index)
                  );
        if (columnIndex != null && rowIndex != null) {
            moveDealToGridCell(store, updatedState, offerType, columnIndex, rowIndex);
            return;
        }

        const remainingRow = nearestIndexWhere(gridCashDown.length, key, (index) => isNumber(gridCashDown[index]));
        if (remainingRow == null) return;

        const remainingCashDown = gridCashDown[remainingRow] as number;
        const dealCashDown = updateDownPaymentImmediate(remainingCashDown);
        store.dispatch({ ...dealCashDown, meta: { ...dealCashDown.meta, userIntent: GRID_AXIS_EDIT_INTENT } });
        if (isDealParkedOffActiveColumn(state, offerType)) store.dispatch(setPaymentGridSelectedRow(offerType, remainingRow));
        store.dispatch(
            selectedTerm(getTermId(offerType, currentTermMonth, remainingCashDown), { meta: { userIntent: GRID_AXIS_EDIT_INTENT } })
        );
        return;
    }

    if (!downPaymentList.includes(currentDownPayment)) {
        const downPayment = key > 1 ? downPaymentList[key - 1] : downPaymentList[0];
        const termId = getTermId(offerType, currentTermMonth, downPayment);
        // Cannot add typeguard for termId here as scenario with undefined termId is covered in tests
        store.dispatch(updateDownPaymentImmediate(downPayment));
        store.dispatch(selectedTerm(termId));
    }
};

const updateSelectedTermForCashDown = (
    store: PaymentGridMiddlewareStore,
    downPaymentList: number[],
    currentDownPayment: number,
    currentTermMonth: number | undefined,
    cashDown: number,
    offerType: OfferType,
    isActiveRowEdited: boolean | undefined
) => {
    if (isActiveRowEdited || !downPaymentList.includes(currentDownPayment)) {
        const newTermID = getTermId(offerType, currentTermMonth, cashDown);
        const dealCashDown = updateDownPaymentImmediate(cashDown);
        store.dispatch({ ...dealCashDown, meta: { ...dealCashDown.meta, userIntent: GRID_AXIS_EDIT_INTENT } });
        // Canot add typescript guard here as scenario with undefined newTermId is covered in tests
        store.dispatch(selectedTerm(newTermID, { payment: false, meta: { userIntent: GRID_AXIS_EDIT_INTENT } }));
    }
};

const calculateIsMainPaymentForCashDown = (
    termMonthsList: number[],
    currentTermMonth: number | undefined,
    currentDownPayment: number,
    prevDownPaymentList: number[],
    key: number,
    offerType: OfferType
) => {
    const currentTermId = getTermId(offerType, currentTermMonth, currentDownPayment);
    const prevDownPayment = prevDownPaymentList[key];

    const isCurrentTermEdited = termMonthsList.some((term) => {
        const termId = getTermId(offerType, term, prevDownPayment);
        return currentTermId === termId;
    });

    const isSingleSelectedOptionChanged = prevDownPaymentList.filter((cashdown) => cashdown === prevDownPayment).length < 2;
    return isCurrentTermEdited && isSingleSelectedOptionChanged;
};

const handleFlexBuyCashDownUpdate = (
    store: PaymentGridMiddlewareStore,
    state: State,
    updatedState: State,
    prevDownPaymentList: number[],
    key: number,
    cashDown: number
) => {
    const flexBuyPercentsList = getFlexBuyPaymentGridPercents(state);
    const flexBuyTermsList = getFlexBuyPaymentGridTerms(updatedState);
    const flexBuyDownPayments = prevDownPaymentList.map((downPayment, index) => {
        return index === key ? cashDown : downPayment;
    });

    store.dispatch(loadingUpdateFlexBuyCashDown({ rowId: key }));

    const updateRow = updateFlexBuyTermsGrid(
        {
            flexBuyDownPayments: flexBuyDownPayments,
            flexBuyTerms: flexBuyTermsList,
            flexBuyPercents: flexBuyPercentsList
        },
        { isMainPayment: false }
    );
    store.dispatch(updateRow);
};

/** A fallback column must price on user-program-quotes, forced on the selection action so the deal's own endpoint is left alone. */
const forceEngineForSelectedCell = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    upqFallbackDisabled: boolean
) => {
    const state = store.getState();
    const offerType = getCurrentOfferType(state);

    if (upqFallbackDisabled) return;
    if (!isAspPgRedesignEnabled(state, offerType)) return;
    if (!getPaymentGridToggle(state, offerType)) return;

    const termMonths = parseSelectedTermMonths(action.payload);
    if (!isNumber(termMonths)) return;

    const source = resolveCellVerdict(
        getPaymentGridTerms(state, offerType),
        getPaymentGridDecidedColumns(state, offerType),
        getPaymentGridColumnSources(state, offerType),
        getPaymentGridSelectedColumnIndex(state, offerType),
        termMonths as number
    );
    if (!source || source === PaymentGridSource.Unavailable) return;

    // Set both ways, before the payload is built, so re-selecting a pinned column asks for program-quotes explicitly.
    action.meta = {
        ...action.meta,
        forceUseGenericCalcs: source === PaymentGridSource.UserProgramQuotes,
        // `paymentReducer`'s forced-call guard needs to know this response answers a GRID cell selection on Lease, and
        // a cell click is a bare SELECTED_TERM with no `meta.paymentGrid` to read it from.
        gridCellSelectionOfferType: offerType
    };
};

/**
 * Gives every user-program-quotes column that was priced by the DEAL a rate of its own, before the deal stops being
 * on that engine.
 *
 * The deal's own column is the one column whose figures live at the deal level rather than per term - nothing seeds
 * `overridesByTerm` for it, because the deal itself was pricing it. So the moment a cell selection moves the deal onto
 * a program-backed column, that column is orphaned: `intendedSource` asks it for a program (it has none, which is why
 * it was on user-program-quotes) and then for a rate of its own (never written), answers `undefined`, and the next
 * resolution pass - the settling round of whatever the user edits next - records it `unavailable`. On Lease that takes
 * the column off `quotedTerms`, so every cell it had already quoted blanks at once, and Lease dispatches no fallback
 * row to recover it.
 *
 * Keyed on "recorded user-program-quotes with no rate of its own" rather than on which term the deal is leaving: that
 * set IS the orphans, since every other route to a user-program-quotes verdict already requires a rate
 * (`intendedSource`'s own rungs), and it needs no pre-`next()` state to identify.
 *
 * The rate is the one the column's cells were quoted at - the same read the user-program-quotes direction uses for the
 * incoming cell, so the two directions agree on what "this column's rate" means. A falsy rate is left alone: there the
 * column has no rate of its own (AC-OFF-3) and `unavailable` is the honest verdict.
 *
 * Only the rate. With one on record the column can claim the lender residual for its own term
 * (`getClaimableLenderResidualForTerm`, which allows it precisely because this build quoted the column), so
 * `hasRequiredFieldsForUPQ` passes and the payload resolves the residual per term.
 */
const pinRatesForOrphanedUpqColumns = (store: PaymentGridMiddlewareStore, state: State, offerType: OfferType): void => {
    const columnSources = getPaymentGridColumnSources(state, offerType);

    getPaymentGridTerms(state, offerType).forEach((termMonths) => {
        if (!isNumber(termMonths)) return;
        const term = termMonths as number;
        if (columnSources[term] !== PaymentGridSource.UserProgramQuotes) return;
        // Already its own, so there is nothing to promote and the write would only restate it.
        if (getSellRateOverrideByOfferTypeAndTerm(state, offerType, term) != null) return;

        const quotedRate = getSellRateOverrideByOfferTypeAndTerm(state, offerType, term, true, true);
        if (!quotedRate) return;

        const declaredRateType = getGridRateTypeByTerm(state, offerType, term);
        // Per-column, so the unit travels with the value and the deal-level unit is deliberately not claimed - see
        // `pinDealLevelRateType`, which a write for one column must not set.
        const rateType =
            declaredRateType === RATE_TYPE.APR || declaredRateType === RATE_TYPE.MoneyFactor ? declaredRateType : undefined;

        store.dispatch(
            updateSellRateOverride(
                quotedRate,
                term,
                { isSelectedTerm: false, skipPaymentCall: true, ...(rateType && { rateType }) },
                offerType
            )
        );
    });
};

const moveDealEngineToSource = (
    store: PaymentGridMiddlewareStore,
    updatedState: State,
    offerType: OfferType,
    source: PaymentGridSource | undefined,
    clickedColumn: number | undefined,
    termMonths: number
) => {
    if (source !== PaymentGridSource.ProgramQuotes && source !== PaymentGridSource.UserProgramQuotes) return;

    const targetEndpoint =
        source === PaymentGridSource.UserProgramQuotes ? CALC_ENDPOINTS.userProgramQuotes : CALC_ENDPOINTS.programQuotes;
    if (getCalculationEndpointByOfferType(updatedState, offerType) === targetEndpoint) return;

    if (targetEndpoint === CALC_ENDPOINTS.userProgramQuotes) {
        const sharedTermRate = ownRateOnSharedTerm(
            updatedState,
            offerType,
            clickedColumn,
            termMonths,
            getSelectedTerm(updatedState, offerType)
        );
        const cellSellRate = sharedTermRate
            ? sharedTermRate.sellRate
            : getSellRateOverrideByOfferTypeAndTerm(updatedState, offerType, termMonths, true, true);
        if (isNumber(cellSellRate)) {
            store.dispatch(
                updateSellRateForSelectedTermUPQ(
                    cellSellRate as number,
                    sharedTermRate ? sharedTermRate.rateType : getPaymentGridTermRateType(updatedState, offerType, termMonths)
                )
            );
        }
    }

    // Before the endpoint dispatch, like the incoming cell's own rate sync above: once the deal is off
    // user-program-quotes the columns it was pricing have nothing left to identify their rate by.
    if (targetEndpoint === CALC_ENDPOINTS.programQuotes) {
        pinRatesForOrphanedUpqColumns(store, updatedState, offerType);
    }

    // The selection moves the deal's endpoint only. The grid's Auto Select Program intent on the build is left alone:
    // every edited column reads its engine from that intent and its own source, so writing a user-priced cell's engine
    // here re-priced every other column on user-program-quotes (DE774455). The checkbox reads `checkedByOfferType`, and
    // the endpoint only until that is recorded.
    store.dispatch(
        updateCurrentCalculationEndpoint(targetEndpoint, offerType, {
            withPayment: false,
            skipGridRebuild: true,
            userIntent: PAYMENT_GRID_CELL_SELECTED_INTENT
        })
    );

    // After the endpoint dispatch on purpose: before it the deal is still on program-quotes and the seed's own UPQ
    // gate returns, leaving the deal-level slots describing whichever term last owned them.
    if (targetEndpoint === CALC_ENDPOINTS.userProgramQuotes) {
        seedResidualForIncomingTerm(store, store.getState(), offerType, termMonths, undefined);
    }
};

/**
 * The deal adopts the engine of the cell just selected. `forceEngineForSelectedCell` gives the SELECTION its column's
 * engine but leaves the deal's endpoint alone, which left the Auto Select Program checkbox a dead control and made a
 * later deal-screen edit re-price on an engine the checkbox denied.
 *
 * Narrow on five axes: Lease or Finance redesign with the grid open (`supportsGridCellEngineAdoption`); grid cell
 * selections only, by `userIntent`; a column with a verdict only; no payment and no rebuild, so no verdict, pin or
 * typed rate is dropped; and rate before engine, since `shouldUseGenericCalcsStructure` needs the deal to carry one.
 *
 * Auto Select Program intent is not changed here - it lives on the build; see `getGridAspIsUserProgramQuotes`.
 */
const adoptSelectedCellEngine = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    offerType: OfferType,
    upqFallbackDisabled: boolean
) => {
    if (upqFallbackDisabled) return;
    if (!supportsGridCellEngineAdoption(updatedState, offerType)) return;
    if (!getPaymentGridToggle(updatedState, offerType)) return;
    if (action.meta?.userIntent !== PAYMENT_GRID_CELL_SELECTED_INTENT) return;

    const termMonths = getSelectedTermMonths(updatedState, offerType);
    if (!isNumber(termMonths)) return;

    const clickedColumn = getPaymentGridSelectedColumnIndex(updatedState, offerType);
    const source = resolveCellVerdict(
        getPaymentGridTerms(updatedState, offerType),
        getPaymentGridDecidedColumns(updatedState, offerType),
        getPaymentGridColumnSources(updatedState, offerType),
        clickedColumn,
        termMonths as number
    );
    moveDealEngineToSource(store, updatedState, offerType, source, clickedColumn, termMonths as number);
};

const shouldHandleCalculationEngineChange = (prevState: State, action: PaymentGridMiddlewareAction, offerType: OfferType) => {
    const isGridEnabled = isPaymentGridEnabled(prevState, offerType);
    const isFlexBuy = isFlexBuyPayment(prevState);
    const calcEngine = getCalculationEndpointByOfferType(prevState, offerType);
    const newEngine = action.payload;
    const hasChanged = calcEngine !== newEngine;

    // Turning ASP back on rebuilds the grid under the program-quotes rules; turning it off carries `skipGridRebuild`.
    // Restricted to the checkbox for Lease: a rebuild also drops `overridesByTerm`, and plenty of other code switches
    // the endpoint for its own reasons.
    const isLeaseAspCheckboxChange =
        isLeaseRedesign(prevState, offerType) && action.meta?.userIntent === ASP_CHECKBOX_TOGGLED_INTENT;

    return (
        action.type === types.UPDATE_CURRENT_CALCULATION_ENDPOINT &&
        !action.meta?.skipGridRebuild &&
        isGridEnabled &&
        (offerType === FINANCE || isLeaseAspCheckboxChange) &&
        !isFlexBuy &&
        hasChanged
    );
};

const handleCalculationEngineChange = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    offerType: OfferType,
    resolveExplicitLock?: ExplicitLockResolver
) => {
    const isUPQ = getIsUserProgramQuotes(store.getState(), offerType);
    const currentTermMonth = getSelectedTermMonths(updatedState);

    if (!isNumber(currentTermMonth)) return;

    store.dispatch(resetPaymentGrid(offerType, true));

    if (isUPQ) {
        const sellRate = getSellRateOverride(updatedState, false);
        if (!sellRate) {
            const termData = Boolean(getSelectedTerm(updatedState));
            const sellRateOverride = getSellRateOverrideByOfferTypeAndTerm(
                updatedState,
                offerType,
                currentTermMonth,
                true,
                termData
            );
            store.dispatch(
                updateSellRateForSelectedTermUPQ(
                    sellRateOverride,
                    getPaymentGridTermRateType(updatedState, offerType, currentTermMonth)
                )
            );
        }
    } else {
        /**
         * Auto Select Program has just been turned ON, so the per-column overrides are dropped and each column is
         * re-priced against its own program. BOTH rate slots go with them: a rate set while ASP was off has no standing
         * once the programs are deciding, and keeping the flat one resurfaces it on the next untick.
         */
        store.dispatch(updateSellRateForSelectedTermUPQ(undefined));
        store.dispatch(
            updateSellRateOverride(undefined, currentTermMonth, {
                isSelectedTerm: true,
                skipPaymentCall: true
            })
        );
    }

    if (isPaymentGridAwaitingVehicleSwitch(updatedState, offerType)) return;

    initializePaymentGridData(store, action, updatedState, offerType, resolveExplicitLock);
};

/** ASP off with the grid open skips the rebuild, so `columnSources` still describes program-quotes. Realigned rather than cleared: an absent entry means undecided, and spins. */
const shouldRealignColumnSourcesForAspOff = (
    prevState: State,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType
): boolean =>
    action.type === types.UPDATE_CURRENT_CALCULATION_ENDPOINT &&
    Boolean(action.meta?.skipGridRebuild) &&
    action.payload === CALC_ENDPOINTS.userProgramQuotes &&
    (getCalculationEndpointByOfferType(prevState, offerType) !== CALC_ENDPOINTS.userProgramQuotes ||
        getPaymentGridBuild(prevState, offerType)?.isUserProgramQuotes === false) &&
    // A cell selection adopting its column's engine matches every other clause here, and realigning on it would rewrite
    // EVERY column to `userProgramQuotes`, destroying the per-column program pins.
    action.meta?.userIntent !== PAYMENT_GRID_CELL_SELECTED_INTENT &&
    isAspPgRedesignEnabled(prevState, offerType) &&
    getPaymentGridToggle(prevState, offerType) &&
    !isPaymentGridAwaitingVehicleSwitch(prevState, offerType);

const realignColumnSourcesForAspOff = (store: PaymentGridMiddlewareStore, offerType: OfferType) => {
    const state = store.getState();

    // `aspUnticked` is the only fact the engine discriminator lets beat the program on the deal, so an untick is its
    // sole writer - a cell selection moves the deal's engine but must not re-judge the other columns.
    const build = getPaymentGridBuild(state, offerType);
    if (build && build.aspUnticked !== true) {
        store.dispatch(setPaymentGridBuild(offerType, { ...build, isUserProgramQuotes: true, aspUnticked: true }));
    }

    /**
     * On Lease the untick claims NO column: an untick is not a rebuild, so a column's pinned program and engine both
     * survive. Rewriting them made every column read user-priced. Finance keeps the blanket rewrite.
     */
    if (isLeaseRedesign(state, offerType)) return;

    const columnSources = getPaymentGridColumnSources(state, offerType);

    const realignedSources = Object.keys(columnSources).reduce<Record<number, PaymentGridSource>>((sources, key) => {
        const termMonths = Number(key);
        // `unavailable` must not be overwritten: an empty cell presented as user-priced waits for an outcome that never comes.
        if (columnSources[termMonths] !== PaymentGridSource.Unavailable) {
            sources[termMonths] = PaymentGridSource.UserProgramQuotes;
        }
        return sources;
    }, {});

    const stuckOnProgramQuotes = Object.entries(getPaymentGridOriginalColumnSources(state, offerType)).reduce<
        Record<number, PaymentGridSource>
    >((sources, [key, original]) => {
        const termMonths = Number(key);
        if (columnSources[termMonths] === PaymentGridSource.Unavailable && original === PaymentGridSource.ProgramQuotes) {
            sources[termMonths] = PaymentGridSource.UserProgramQuotes;
        }
        return sources;
    }, {});
    if (Object.keys(stuckOnProgramQuotes).length) {
        store.dispatch(setPaymentGridOriginalColumnSources(offerType, { replace: stuckOnProgramQuotes }));
    }

    if (!Object.keys(realignedSources).length) return;

    store.dispatch(setPaymentGridColumnSources(offerType, realignedSources));
};

/**
 * A Lease endpoint change the grid is not rebuilt for still has to move the grid's engine.
 *
 * `shouldHandleCalculationEngineChange` rebuilds Finance on any endpoint change but Lease only for the ASP checkbox,
 * because a rebuild also drops `overridesByTerm`. Everything else that moves the endpoint -
 * `invalidateProgramAndRefetch` and `fallBackToUserProgramQuotes`, both reached from a deal-screen edit made with the
 * grid open, neither carrying a `userIntent` - therefore left `build.isUserProgramQuotes` describing the endpoint the
 * grid OPENED on. `gridAspIsUserProgramQuotes` reads the deal's endpoint only when there is no build, so every later
 * call went out on the engine of a deal state that no longer existed.
 *
 * The stamp alone, so the grid follows the deal without a rebuild: `columnSources` keeps each column's pinned program,
 * which is what restricting the Lease rebuild to the checkbox exists to protect, and no cell is re-priced - the next
 * call is the first to price on the new engine.
 *
 * Confined to the gate-off world, matching the mint's own condition (`initializePaymentGridData`), so whether the stamp
 * is present at all stays one rule. Under `isExplicitLenderProgramGridActive` the discriminator reads `aspUnticked` and
 * the program on the deal instead, and neither is this function's to revise.
 */
const gridBuildFollowingDeal = (
    prevState: State,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType
): PaymentGridBuild | undefined => {
    if (action.type !== types.UPDATE_CURRENT_CALCULATION_ENDPOINT) return undefined;
    if (action.meta?.offerType && action.meta.offerType !== offerType) return undefined;
    if (!isLeaseRedesign(prevState, offerType)) return undefined;
    if (isExplicitLenderProgramGridActive(prevState, offerType)) return undefined;
    if (!getPaymentGridToggle(prevState, offerType)) return undefined;
    // A cell selection never moves the stamp: re-deriving it from one column's engine would claim the whole grid for
    // that column (DE774455). The checkbox writes it on both of its
    // paths - a re-tick through the rebuild's fresh mint, an untick through `realignColumnSourcesForAspOff`, which
    // says more than this can by recording `aspUnticked`.
    if (action.meta?.userIntent === PAYMENT_GRID_CELL_SELECTED_INTENT) return undefined;
    if (action.meta?.userIntent === ASP_CHECKBOX_TOGGLED_INTENT) return undefined;
    if (getCalculationEndpointByOfferType(prevState, offerType) === action.payload) return undefined;

    const build = getPaymentGridBuild(prevState, offerType);
    if (!build) return undefined;

    // Read off the payload, because the caller runs BEFORE next(): a switch dispatched `withPayment: true` is a payment
    // action, which `shouldProcessPaymentAction` fans out into row calls and returns on, so the stamp has to move first
    // for the switch's own rows to price on the engine the deal is moving to. The endpoint slot still holds the old one.
    const isUserProgramQuotes = action.payload === CALC_ENDPOINTS.userProgramQuotes;
    return build.isUserProgramQuotes === isUserProgramQuotes ? undefined : { ...build, isUserProgramQuotes };
};

/** Read off the response, not `programInfoByTerm`: that cache merges per term and never clears. */
const hasProgramInDiscoveryResponse = (payload: GenericObject, offerType: OfferType, termMonths: number): boolean => {
    const terms = payload?.terms;
    const termIds: string[] = terms?.[RESPONSE_TERM_IDS_KEY[offerType]] ?? [];

    return termIds.some((termId) => {
        const term = terms?.byId?.[termId];
        return term?.months === termMonths && isValidProgram(term?.lenderProgram);
    });
};

const recordColumnCells = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType,
    originalAction: PaymentGridMiddlewareAction
) => {
    const columnIndices = originalAction?.meta?.gridColumnIndices as number[] | undefined;
    if (!columnIndices?.length) return;

    const requestOverride = originalAction?.meta?.[PAYMENT_REQUEST_OVERRIDE_NODE]?.[offerType];
    const downPayment = requestOverride?.downPayment;
    if (!isNumber(downPayment)) return;
    const requestedTerms: number[] = requestOverride?.termList ?? [];

    const state = store.getState();
    const gridTerms = getPaymentGridTerms(state, offerType);
    const responseTerms = action.payload?.terms;
    const termIds: string[] = responseTerms?.[RESPONSE_TERM_IDS_KEY[offerType]] ?? Object.keys(responseTerms?.byId ?? {});
    const pricedByMonths = termIds.reduce<Record<number, { monthlyPayment?: number; lenderCode?: string }>>((byMonths, id) => {
        const term = responseTerms?.byId?.[id];
        if (isNumber(term?.months)) byMonths[term.months as number] = term;
        return byMonths;
    }, {});

    const gridCashDown = getPaymentGridCashDown(state, offerType);
    const rowIndices: number[] = (originalAction?.meta?.gridRowIndices as number[] | undefined)?.length
        ? (originalAction.meta.gridRowIndices as number[])
        : gridCashDown.reduce<number[]>((rows, cash, index) => {
              if (cash === downPayment) rows.push(index);
              return rows;
          }, []);

    const cells = columnIndices.reduce<Record<number, Record<number, GenericObject>>>((accumulated, columnIndex) => {
        const gridTerm = gridTerms[columnIndex];
        const termMonths =
            requestedTerms.length === 1
                ? requestedTerms[0]
                : isNumber(gridTerm) && requestedTerms.includes(gridTerm as number)
                ? (gridTerm as number)
                : undefined;
        if (!isNumber(termMonths)) return accumulated;
        const priced = pricedByMonths[termMonths as number];
        const isSharedCell =
            gridTerms.filter((gridTerm) => gridTerm === termMonths).length > 1 ||
            gridCashDown.filter((cash) => cash != null && cash === downPayment).length > 1;
        if (priced?.monthlyPayment == null && !isSharedCell) return accumulated;

        rowIndices.forEach((rowIndex) => {
            accumulated[columnIndex] = {
                ...accumulated[columnIndex],
                [rowIndex]: {
                    ...(priced ?? {}),
                    term: termMonths as number,
                    downPayment
                }
            };
        });
        return accumulated;
    }, {});

    if (Object.keys(cells).length) store.dispatch(setPaymentGridColumnCells(offerType, cells));
};

/** Not a list of action types: `addPaymentGridMeta` preserves the originating type, so a fanned-out row arrives as e.g. `UPDATED_SELL_RATE_OVERRIDE`. */
const isGridRowPayment = (actionType?: string): boolean =>
    actionType !== types.LOAD_PAYMENT_GRID_LENDER_PROGRAMS && actionType !== types.LOAD_PAYMENT_GRID_TERM_LENDER_PROGRAM;

/**
 * True when the cell at this term and cash down holds a payment THIS grid build quoted. The record's existence does not
 * say that: `offer.terms.byId` is merge-only and `preserveOnProgramLoss` keeps the previous record, so a re-price that
 * returned nothing read as a success. `quotedTerms` is cumulative for the build and revised only by a round that
 *
 * Finance keeps the record test, except where a row's latest round asked for the term and its ledger did not take it:
 * that row was just refused, so a record an earlier pricing left behind is not a quote.
 */
const hasCellQuote = (
    state: State,
    offerType: OfferType,
    termMonths: number,
    downPayment: number,
    columnIndex?: number
): boolean => {
    const termRecord = getTerm(state, getTermId(offerType, termMonths, downPayment)!);

    const gridTerms = getPaymentGridTerms(state, offerType);
    if (gridTerms.filter((gridTerm) => gridTerm === termMonths).length > 1) {
        const holders = columnIndex == null ? gridTerms.map((_gridTerm, index) => index) : [columnIndex];
        return holders.some(
            (holderIndex) =>
                gridTerms[holderIndex] === termMonths &&
                getPaymentGridCashDown(state, offerType).some(
                    (cashDown, rowIndex) =>
                        cashDown === downPayment &&
                        getPaymentGridCellRecord(state, offerType, holderIndex, rowIndex, termMonths, downPayment) != null
                )
        );
    }

    // Cast: `quotedTerms` is not on the published PaymentGridRowStatus type yet, as with `columnSources`.
    const rowsStatuses = getRowsStatuses(state, offerType) as Record<number, { quotedTerms?: number[]; terms?: (number | null)[] }>;
    const rowIndices = getPaymentGridCashDown(state, offerType).reduce<number[]>((rows, cash, rowIndex) => {
        if (cash === downPayment) rows.push(rowIndex);
        return rows;
    }, []);

    if (!isLeaseRedesign(state, offerType)) {
        const wasJustRefused = (rowIndex: number) => {
            const rowStatus = rowsStatuses?.[rowIndex];
            return (
                Boolean(rowStatus?.terms?.includes(termMonths)) &&
                Array.isArray(rowStatus?.quotedTerms) &&
                !rowStatus?.quotedTerms?.includes(termMonths)
            );
        };
        return Boolean(termRecord) && !isEmpty(termRecord) && !rowIndices.some(wasJustRefused);
    }

    return (
        rowIndices.every((rowIndex) => rowsStatuses?.[rowIndex]?.quotedTerms?.includes(termMonths)) &&
        termRecord?.monthlyPayment != null
    );
};

const decideDuplicateColumnsFromOwnQuotes = (
    store: PaymentGridMiddlewareStore,
    state: State,
    offerType: OfferType,
    pricedDownPayments: number[],
    termVerdicts: Record<number, PaymentGridSource | string>
): void => {
    if (!pricedDownPayments.length) return;
    const gridTerms = getPaymentGridTerms(state, offerType);
    const failedColumns = getPaymentGridErroredColumns(state, offerType);
    const byColumn = gridTerms.reduce<Record<number, string>>((verdicts, term, columnIndex) => {
        if (term == null || !isTermDuplicated(gridTerms, term)) return verdicts;
        const termVerdict = termVerdicts[term as number];
        if (termVerdict === undefined) return verdicts;

        /**
         * Only a RECORDED FAILURE downgrades a column. Not owning a cell record is NOT evidence of failure:
         * `recordColumnCells` writes cells for the columns the settling response named, so a duplicate whose own
         * call is still in flight - or whose other rows have not landed yet - has no record and would otherwise be
         * flagged in the middle of succeeding. That is what made the grid show an error on a duplicated term whose
         * API calls all came back fine.
         *
         * The restore arm matters as much: `decidedColumns` merges, so a column that failed and then re-priced
         * needs its good verdict written back or the stale `Unavailable` outranks the term map forever.
         */
        if (failedColumns.includes(columnIndex)) {
            verdicts[columnIndex] = PaymentGridSource.Unavailable;
            return verdicts;
        }
        const ownsEveryCell = pricedDownPayments.every((downPayment) =>
            hasCellQuote(state, offerType, term as number, downPayment, columnIndex)
        );
        if (ownsEveryCell) verdicts[columnIndex] = termVerdict as string;
        return verdicts;
    }, {});
    if (Object.keys(byColumn).length) store.dispatch(setPaymentGridDecidedColumns(offerType, byColumn));
};

const clearRejectedColumnFigures = (
    store: PaymentGridMiddlewareStore,
    state: State,
    offerType: OfferType,
    refusedTerms: Record<number, true>
): void => {
    Object.keys(refusedTerms).forEach((key) => {
        const termMonths = Number(key);
        if (!hasUserPricedGridColumn(state, offerType, termMonths)) return;
        if (!hasUserSuppliedGridSellRate(state, offerType, termMonths)) return;
        if (!hasUserSuppliedGridResidual(state, offerType, termMonths)) return;

        store.dispatch(clearPaymentGridColumnFigures(offerType, termMonths));
    });
};

const recordOriginalColumnSources = (
    store: PaymentGridMiddlewareStore,
    state: State,
    offerType: OfferType,
    verdicts: Record<number, PaymentGridSource>,
    previousSources: Record<number, PaymentGridSource>,
    holdsPayment: (termMonths: number, verdict: PaymentGridSource) => boolean
): void => {
    const remember: Record<number, PaymentGridSource> = {};
    const rememberPrograms: Record<number, GenericObject> = {};
    const forget: number[] = [];

    Object.keys(verdicts).forEach((key) => {
        const termMonths = Number(key);
        const previous = previousSources[termMonths];
        if (holdsPayment(termMonths, verdicts[termMonths])) {
            forget.push(termMonths);
            return;
        }
        if (previous == null || previous === PaymentGridSource.Unavailable) return;
        remember[termMonths] = previous;
        const attemptedProgram = getGridColumnProgramInfo(state, offerType, termMonths);
        if (isValidProgram(attemptedProgram) && !isUserPricedProgram(attemptedProgram)) {
            rememberPrograms[termMonths] = attemptedProgram;
        }
    });

    if (!Object.keys(remember).length && !forget.length) return;
    store.dispatch(setPaymentGridOriginalColumnSources(offerType, { remember, rememberPrograms, forget }));
};

// Active cell's row first: the deal's `downPayment` can match no row after a close and reopen.
const getDealRowDownPayment = (state: State, offerType: OfferType): number | undefined =>
    getActiveCellDownPayment(state, offerType) ?? getDownPayment(state, offerType);

const askedHoldersOf = (gridTerms: (number | null)[], termMonths: number, columnsToEvaluate: number[] | undefined): number[] =>
    columnsToEvaluate?.length && isTermDuplicated(gridTerms, termMonths)
        ? columnsToEvaluate.filter((columnIndex) => gridTerms[columnIndex] === termMonths)
        : [];

const withoutTerms = <T>(byTerm: Record<number, T>, terms: number[]): Record<number, T> =>
    terms.length
        ? (Object.fromEntries(Object.entries(byTerm).filter(([termMonths]) => !terms.includes(Number(termMonths)))) as Record<
              number,
              T
          >)
        : byTerm;

const priceLeaseDealColumnOnHeldPair = (
    store: PaymentGridMiddlewareStore,
    state: State,
    offerType: OfferType,
    termMonths: number,
    pricedDownPayments: number[],
    columnIndex: number
): void => {
    restoreFallbackRatesForTerms(store, offerType, [termMonths]);

    const dealDownPayment = getDealRowDownPayment(state, offerType);
    const gridRoundId = nextGridRoundId();

    gridRowsFor(state, offerType)
        .filter(({ downPayment }) => pricedDownPayments.includes(downPayment))
        .forEach(({ rowIndex, downPayment }) => {
            const isMainPayment = downPayment === dealDownPayment;
            const row = updatePaymentGridRow(offerType, [termMonths], downPayment, {
                forceUseGenericCalcs: true,
                gridColumnIndices: [columnIndex],
                gridRowIndices: [rowIndex]
            });
            store.dispatch(
                addPaymentGridMeta(tagOfferType(row, offerType), {
                    ...withLeaseProgramPin(state, offerType, {
                        isMainPayment,
                        ...(isMainPayment && { mainPaymentActionType: types.SELECTED_TERM }),
                        gridRoundId,
                        gridRoundTerms: [termMonths]
                    }),
                    isUserProgramQuotes: true
                })
            );
        });
};

/** Re-prices any column program-quotes could not fully price, in full, through user-program-quotes. Runs only once no row is loading; columns already fallen back are skipped, which stops it re-firing on its own responses. `termsToEvaluate` scopes it to one half of a split row. */
const handleGridFallbackPass = (
    store: PaymentGridMiddlewareStore,
    updatedState: State,
    offerType: OfferType,
    upqFallbackDisabled: boolean,
    termsToEvaluate?: number[],
    /** The ASP state the fan-out being judged went out under. `undefined` where live state IS that state. */
    aspIsUserProgramQuotes?: boolean,
    roundWasAnswered = true,
    columnsToEvaluate?: number[]
) => {
    if (!isAspPgRedesignEnabled(updatedState, offerType)) return;

    // A closed grid has no columns to judge. Both callers enforce that, so the guard that used to sit here was
    // unreachable; a third caller would own this check.

    const downPaymentList = getPaymentGridCashDown(updatedState, offerType);
    const pricedDownPayments = downPaymentList.filter((downPayment): downPayment is number => isNumber(downPayment));
    const rowsStatuses = getRowsStatuses(updatedState, offerType) as Record<number, { isLoading?: boolean }>;

    const gridRowIndices = downPaymentList.reduce<number[]>((rows, cash, rowIndex) => {
        if (isNumber(cash)) rows.push(rowIndex);
        return rows;
    }, []);
    const isAnyRowLoading = isLeaseRedesign(updatedState, offerType)
        ? gridRowIndices.some((rowIndex) => rowsStatuses?.[rowIndex]?.isLoading)
        : Object.values(rowsStatuses).some((rowStatus) => rowStatus?.isLoading);
    if (isAnyRowLoading) return;

    const allTermMonths = getPaymentGridTerms(updatedState, offerType);
    const termMonthsList = termsToEvaluate
        ? allTermMonths.filter((termMonths) => isNumber(termMonths) && termsToEvaluate.includes(termMonths as number))
        : allTermMonths;
    const isColumnFullyPriced = (termMonths: number) =>
        pricedDownPayments.every((downPayment) => hasCellQuote(updatedState, offerType, termMonths, downPayment));
    const isHolderFullyPriced = (termMonths: number, columnIndex: number) =>
        pricedDownPayments.every((downPayment) => hasCellQuote(updatedState, offerType, termMonths, downPayment, columnIndex));
    const dealColumnIndex = getPaymentGridDealColumnIndex(updatedState, offerType);

    // Lease resolves in one pass and dispatches no fallback row: Finance's two-phase flow would price a lease column
    // from a residual it was never quoted at. A blank column waits for a rate of the user's own instead.
    const previousSources = getPaymentGridColumnSources(updatedState, offerType);

    if (isLeaseRedesign(updatedState, offerType)) {
        const {
            columnSources: resolvedSources,
            refusedTerms,
            pairRejectedTerms
        } = resolveLeaseColumnSources({
            ...leaseColumnFacts(updatedState, offerType, termMonthsList, {
                isUserProgramQuotes: aspIsUserProgramQuotes
            }),
            isColumnFullyPriced
        });

        const recordsRefusals = Boolean(termsToEvaluate) && roundWasAnswered;

        const columnVerdicts: Record<number, PaymentGridSource> = {};
        const termsJudgedByColumn: number[] = [];
        (termMonthsList.filter((termMonths) => isNumber(termMonths)) as number[]).forEach((term) => {
            const askedHolders = askedHoldersOf(allTermMonths, term, columnsToEvaluate);
            if (!askedHolders.length) return;

            termsJudgedByColumn.push(term);
            delete resolvedSources[term];
            delete refusedTerms[term];
            delete pairRejectedTerms[term];
            askedHolders.forEach((columnIndex) => {
                const holderVerdict = resolveLeaseColumnSources({
                    ...leaseColumnFacts(updatedState, offerType, [term], {
                        isUserProgramQuotes: aspIsUserProgramQuotes,
                        columnIndex
                    }),
                    isColumnFullyPriced: () => isHolderFullyPriced(term, columnIndex)
                });
                columnVerdicts[columnIndex] = holderVerdict.columnSources[term];
                if (columnIndex !== dealColumnIndex) return;
                resolvedSources[term] = holderVerdict.columnSources[term];
                Object.assign(refusedTerms, holderVerdict.refusedTerms);
                Object.assign(pairRejectedTerms, holderVerdict.pairRejectedTerms);
            });
        });

        const dealTermMonths = getSelectedTermMonths(updatedState, offerType);
        const isDealTermShared = isNumber(dealTermMonths) && isTermDuplicated(allTermMonths, dealTermMonths as number);
        const dealColumnFallsBack =
            recordsRefusals &&
            aspIsUserProgramQuotes !== true &&
            isNumber(dealTermMonths) &&
            refusedTerms[dealTermMonths as number] === true &&
            (!isDealTermShared || termsJudgedByColumn.includes(dealTermMonths as number)) &&
            hasHeldLeasePair(updatedState, offerType, dealTermMonths as number);
        if (dealColumnFallsBack) {
            resolvedSources[dealTermMonths as number] = PaymentGridSource.UserProgramQuotes;
            delete refusedTerms[dealTermMonths as number];
            if (isDealTermShared) columnVerdicts[dealColumnIndex as number] = PaymentGridSource.UserProgramQuotes;
        }

        store.dispatch(
            setPaymentGridColumnSources(offerType, resolvedSources, {
                // A whole-grid verdict is the single writer for its build, so it replaces: merging let an
                // `Unavailable` from a superseded build survive, and only `RESET_PAYMENT_GRID` removes an entry. A pass
                // scoped to one column still merges.
                replace: !termsToEvaluate,
                ...(recordsRefusals && { refusedTerms, pairRejectedTerms }),
                ...(termsJudgedByColumn.length && { keepColumnVerdictsFor: termsJudgedByColumn })
            })
        );

        recordOriginalColumnSources(
            store,
            updatedState,
            offerType,
            resolvedSources,
            previousSources,
            (_termMonths, verdict) => verdict !== PaymentGridSource.Unavailable
        );

        decideDuplicateColumnsFromOwnQuotes(
            store,
            updatedState,
            offerType,
            pricedDownPayments,
            withoutTerms(resolvedSources, termsJudgedByColumn)
        );
        if (Object.keys(columnVerdicts).length) store.dispatch(setPaymentGridDecidedColumns(offerType, columnVerdicts));

        if (recordsRefusals) {
            clearRejectedColumnFigures(store, updatedState, offerType, refusedTerms);
        }

        releaseStaleFallbackRates(
            store,
            offerType,
            allTermMonths,
            (termMonths) => isColumnFullyPriced(termMonths) || (recordsRefusals && refusedTerms[termMonths] === true),
            dealColumnFallsBack ? [dealTermMonths as number] : []
        );

        if (dealColumnFallsBack) {
            priceLeaseDealColumnOnHeldPair(
                store,
                updatedState,
                offerType,
                dealTermMonths as number,
                pricedDownPayments,
                dealColumnIndex as number
            );
        }
        return;
    }

    /**
     * Finance's deal column follows the deal's endpoint, so on user-program-quotes it was already asked on that engine:
     * it is user-priced where its cells came back and unpriceable where they did not, never a candidate for a
     * program-quotes fallback, and never to be relabelled program-backed off a cache entry the call never used.
     *
     * Its siblings are judged below exactly as they are on a program-quotes deal - since US2118720 they were asked on
     * program-quotes too. That is what replaces the blanket rewrite this used to do for every Auto-Select-off deal,
     * which marked every column user-priced and so could not tell a fallen-back column from one never asked at all.
     */
    const dealTermMonths = getSelectedTermMonths(updatedState, offerType);
    const dealColumnOnUserProgramQuotes = isGridUserProgramQuotes(updatedState, offerType, getIsUserProgramQuotes);
    const followsDealEndpoint = (termMonths: number): boolean =>
        dealColumnOnUserProgramQuotes &&
        isNumber(dealTermMonths) &&
        termMonths === dealTermMonths &&
        previousSources[termMonths] !== PaymentGridSource.ProgramQuotes;

    const isExplicitLenderGrid = isExplicitLenderProgramGridActive(updatedState, offerType);
    const dealHasProgram = isExplicitLenderGrid && hasDealLenderProgram(updatedState, offerType);

    const hasProgramForTerm = (termMonths: number): boolean => {
        if (dealHasProgram) return true;
        if (isValidProgram(getPaymentProgramInfoByTerm(updatedState, offerType, termMonths))) return true;
        if (termMonths !== dealTermMonths) return false;
        const dealProgram = getSelectedTerm(updatedState, offerType).lenderProgram;
        return !isUserPricedProgram(dealProgram) && isValidProgram(dealProgram);
    };

    const isSharedTerm = (termMonths: number | null): boolean =>
        isNumber(termMonths) && isTermDuplicated(allTermMonths, termMonths as number);
    const termsJudgedByColumn = [...new Set(termMonthsList.filter(isSharedTerm))] as number[];

    const resolutions = resolveColumnSources({
        termMonthsList: termMonthsList.filter((termMonths) => !isSharedTerm(termMonths)),
        downPaymentList,
        hasProgramForTerm,
        hasQuoteForCell: (termMonths, downPayment) => hasCellQuote(updatedState, offerType, termMonths, downPayment)
    });

    /** Judged from what was recorded, never re-derived: a user-program-quotes response carries a synthesised user program, so re-deriving would let the fallback fire forever. */
    const hasAlreadyFallenBack = (termMonths: number) =>
        previousSources[termMonths] === PaymentGridSource.UserProgramQuotes ||
        previousSources[termMonths] === PaymentGridSource.Unavailable;

    const fallbackTerms = upqFallbackDisabled
        ? []
        : getFallbackTerms(resolutions).filter(
              (termMonths) => !hasAlreadyFallenBack(termMonths) && !followsDealEndpoint(termMonths)
          );

    const financeVerdict = (
        follows: boolean,
        isPriced: boolean,
        hasFallenBack: boolean,
        resolved: PaymentGridSource
    ): PaymentGridSource => {
        const settled = isPriced ? PaymentGridSource.UserProgramQuotes : PaymentGridSource.Unavailable;
        if (follows) return settled;
        if (upqFallbackDisabled) {
            return resolved === PaymentGridSource.ProgramQuotes ? PaymentGridSource.ProgramQuotes : PaymentGridSource.Unavailable;
        }
        // Sticky: a fallen-back column never reverts to program-backed, which is what terminates the pass.
        return hasFallenBack ? settled : resolved;
    };

    const columnSources = resolutions.reduce<Record<number, PaymentGridSource>>((sources, resolution) => {
        const termMonths = resolution.termMonths;
        if (!isNumber(termMonths)) return sources;
        const term = termMonths as number;
        sources[term] = financeVerdict(
            followsDealEndpoint(term),
            isColumnFullyPriced(term),
            hasAlreadyFallenBack(term),
            resolution.source
        );
        return sources;
    }, {});

    const isFallenBackVerdict = (verdict: PaymentGridSource | undefined) =>
        verdict === PaymentGridSource.UserProgramQuotes || verdict === PaymentGridSource.Unavailable;
    const holderVerdicts: Record<number, PaymentGridSource> = {};
    const holderFallbacks: { term: number; columnIndex: number }[] = [];
    const failedColumns = getPaymentGridErroredColumns(updatedState, offerType);
    const isHolderAnswered = (term: number, columnIndex: number) =>
        isHolderFullyPriced(term, columnIndex) || failedColumns.includes(columnIndex);
    const isTermAnswered = (term: number) =>
        allTermMonths.some((gridTerm, columnIndex) => gridTerm === term && isHolderAnswered(term, columnIndex));
    allTermMonths.forEach((termMonths, columnIndex) => {
        if (!isNumber(termMonths) || !termsJudgedByColumn.includes(termMonths as number)) return;
        const term = termMonths as number;
        if (!isHolderAnswered(term, columnIndex) && isTermAnswered(term)) return;
        const hasFallenBack = isFallenBackVerdict(getColumnVerdict(updatedState, offerType, columnIndex, term));
        const follows = columnIndex === dealColumnIndex && followsDealEndpoint(term);
        const [resolution] = resolveColumnSources({
            termMonthsList: [term],
            downPaymentList,
            hasProgramForTerm,
            hasQuoteForCell: (holderTerm, downPayment) =>
                hasCellQuote(updatedState, offerType, holderTerm, downPayment, columnIndex)
        });

        holderVerdicts[columnIndex] = financeVerdict(
            follows,
            isHolderFullyPriced(term, columnIndex),
            hasFallenBack,
            resolution.source
        );
        if (!upqFallbackDisabled && !follows && !hasFallenBack && resolution.source === PaymentGridSource.UserProgramQuotes) {
            holderFallbacks.push({ term, columnIndex });
        }
        if (columnIndex === dealColumnIndex) columnSources[term] = holderVerdicts[columnIndex];
    });
    const dealHolderFallbackTerms = holderFallbacks
        .filter(({ columnIndex }) => columnIndex === dealColumnIndex)
        .map(({ term }) => term);
    const termsFallingBack = [...fallbackTerms, ...dealHolderFallbackTerms];

    store.dispatch(
        setPaymentGridColumnSources(
            offerType,
            columnSources,
            termsJudgedByColumn.length ? { keepColumnVerdictsFor: termsJudgedByColumn } : undefined
        )
    );

    const attemptedSources = termsFallingBack.reduce<Record<number, PaymentGridSource>>(
        (sources, termMonths) => {
            if (sources[termMonths] == null) sources[termMonths] = PaymentGridSource.ProgramQuotes;
            return sources;
        },
        { ...previousSources }
    );

    recordOriginalColumnSources(
        store,
        updatedState,
        offerType,
        columnSources,
        attemptedSources,
        (termMonths, verdict) =>
            verdict === PaymentGridSource.ProgramQuotes ||
            (verdict === PaymentGridSource.UserProgramQuotes && isColumnFullyPriced(termMonths))
    );

    decideDuplicateColumnsFromOwnQuotes(
        store,
        updatedState,
        offerType,
        pricedDownPayments,
        withoutTerms(columnSources, termsJudgedByColumn)
    );
    if (Object.keys(holderVerdicts).length) store.dispatch(setPaymentGridDecidedColumns(offerType, holderVerdicts));

    // The whole grid, not the scoped `termMonthsList`: a hold for a column this pass is not judging is stale too.
    // Skipped while the cash-down axis is empty, where `every` reads every column as priced and would drop live holds.
    if (pricedDownPayments.length) {
        releaseStaleFallbackRates(store, offerType, allTermMonths, isColumnFullyPriced, termsFallingBack);
    }

    if (!fallbackTerms.length && !holderFallbacks.length) return;

    // Before the rows: the forced call is built from state, so the promotion has to land first.
    restoreFallbackRatesForTerms(store, offerType, termsFallingBack);

    // One user-program-quotes call per row over its fallback terms, engine forced. The row covering the selected cell goes as the deal's own payment, since the fallback re-prices that cell too.
    const selectedTermMonths = getSelectedTermMonths(updatedState, offerType);
    const dealDownPayment = getDealRowDownPayment(updatedState, offerType);
    const isSelectedColumnFallingBack =
        (isNumber(selectedTermMonths) && fallbackTerms.includes(selectedTermMonths as number)) ||
        holderFallbacks.some(({ columnIndex }) => columnIndex === dealColumnIndex);

    const fallbackColumnCalls: ColumnScopedCall[] = holderFallbacks.map(({ term, columnIndex }) => {
        const ownOverride = getOwnColumnOverride(updatedState, offerType, columnIndex, term);
        const dealsSellRate =
            columnIndex === dealColumnIndex ? getSellRateOverrideByOfferTypeAndTerm(store.getState(), offerType, term) : undefined;
        const sellRate = ownOverride?.sellRate ?? dealsSellRate;
        return {
            columnIndex,
            term,
            sellRate: sellRate ?? activeCellSellRate(updatedState, offerType, true),
            ignoreTermOverride: sellRate == null
        };
    });
    const fallbackSelectedColumn = dealColumnIndex;

    const selectedColumnFallbackRate =
        getOwnColumnOverride(updatedState, offerType, fallbackSelectedColumn as number, selectedTermMonths as number)?.sellRate ??
        getSellRateOverrideByOfferTypeAndTerm(updatedState, offerType, selectedTermMonths as number, true, true);

    fallbackColumnCalls.forEach((columnCall) => {
        if (columnCall.sellRate != null || !isNumber(selectedColumnFallbackRate)) return;
        columnCall.sellRate = selectedColumnFallbackRate as number;
    });

    gridRowsFor(updatedState, offerType)
        .filter(({ downPayment }) => pricedDownPayments.includes(downPayment))
        .forEach(({ rowIndex, downPayment }) => {
            const isDealRow = downPayment === dealDownPayment;
            const dispatchFallbackRow = (
                terms: number[],
                isMain: boolean,
                columnIndices?: number[],
                columnSellRate?: number,
                ignoreTermOverride?: boolean,
                columnResidualInfo?: GenericObject
            ) => {
                const fallbackRow = updatePaymentGridRow(offerType, terms, downPayment, {
                    forceUseGenericCalcs: true,
                    gridColumnIndices: columnIndices ?? coveredColumnIndices(updatedState, offerType, terms),
                    gridRowIndices: [rowIndex],
                    ...columnScopedMeta(
                        updatedState,
                        offerType,
                        terms,
                        columnIndices,
                        columnSellRate,
                        columnResidualInfo,
                        ignoreTermOverride
                    )
                });
                store.dispatch(addPaymentGridMeta(tagOfferType(fallbackRow, offerType), { isMainPayment: isMain }));
            };

            fallbackColumnCalls.forEach(({ term, columnIndex, sellRate, residualInfo, ignoreTermOverride }) =>
                dispatchFallbackRow(
                    [term],
                    isSelectedColumnFallingBack && isDealRow && columnIndex === fallbackSelectedColumn,
                    [columnIndex],
                    sellRate,
                    ignoreTermOverride,
                    residualInfo
                )
            );

            if (fallbackTerms.length) {
                const isTermBatchMain =
                    isSelectedColumnFallingBack &&
                    isDealRow &&
                    !fallbackColumnCalls.some(({ columnIndex }) => columnIndex === fallbackSelectedColumn);
                dispatchFallbackRow(fallbackTerms, isTermBatchMain);
            }
        });
};

/**
 * Whether a grid response still belongs to the build now on screen. A second enable, an ASP re-tick and a close all
 * leave an older build's discovery response able to fan out a whole second row round. A missing id on either side means
 * "no build identity to compare", which is every pre-redesign path and the Finance grid.
 */
const isCurrentGridBuild = (state: State, action: PaymentGridMiddlewareAction, offerType: OfferType): boolean => {
    if (isPaymentGridAwaitingVehicleSwitch(state, offerType)) return false;

    if (!isLeaseRedesign(state, offerType)) return true;

    // The grid is gone: nothing this response could drive still exists, and letting it through re-created
    // `columnSources` for a closed grid, which is what stopped the next open being a fresh build.
    if (!getPaymentGridToggle(state, offerType)) return false;

    // Only a call belonging to a build is checked. A per-edit fan-out carries a round id but no build id, and judging it
    // against the build would leave every column it does not touch spinning.
    const responseBuildId = action.meta?.originalAction?.meta?.paymentGrid?.buildId;
    const currentBuildId = getPaymentGridBuild(state, offerType)?.id;
    if (!responseBuildId || !currentBuildId) return true;

    return responseBuildId === currentBuildId;
};

/**
 * US2041815: stamps every cell the response actually priced with the time it landed.
 *
 * The term ids come off the response's own `terms` node rather than being rebuilt from the request, so
 * a partially-fulfilled response only stamps the cells it really returned. Cells that failed keep
 * whatever timestamp they had, which is what lets `oldestAt` stay honest.
 */
const stampPricedCells = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    state: State,
    offerType: OfferType
) => {
    if (action.type !== types.PAYMENT_RECEIVED) return;

    // Behind the same toggle as the rest of grid persistence: with it off nothing reads these timestamps,
    // so writing them would only put an extra dispatch on the payment path for no one's benefit. Read the
    // way this file already reads `F298789_ASP_PG_redesign`, which is declared in the same toggle group.
    // Cast because it is an app-config toggle that offer-redux's own toggle union does not declare, and
    // widening that union means another dr-platform-shared release for a string this package only reads.
    if (!getFeatureToggle(state, PAYMENT_GRID_BAGGAGE_COMPRESSION_TOGGLE as any)) return;

    /**
     * Flex buy is stamped as one grid, not cell by cell: a flex buy response carries the whole grid and
     * the reducer replaces it wholesale, so every cell is always exactly as old as the last response.
     * Stamping only when the grid is actually present keeps a main-payment response, which carries none,
     * from re-dating a grid it did not reprice.
     */
    if (action.meta?.originalAction?.meta?.paymentGrid?.isFlexBuyPayment) {
        if ((action.payload as GenericObject)?.finance?.flexBuyPaymentGridData?.length) {
            store.dispatch(setFlexBuyPaymentGridPricedAt(Date.now()));
        }
        return;
    }

    const terms = (action.payload as GenericObject)?.terms;
    const termIds: string[] = terms?.[RESPONSE_TERM_IDS_KEY[offerType]] ?? [];
    if (!termIds.length) return;

    const pricedAt = Date.now();
    const cellPricedAt = termIds.reduce<Record<string, number>>((acc, termId) => {
        // An entry with no payment is not a quote, so it does not reset that cell's clock.
        if (!isEmpty(terms?.byId?.[termId])) acc[termId] = pricedAt;
        return acc;
    }, {});

    if (Object.keys(cellPricedAt).length) {
        store.dispatch(setPaymentGridCellPricedAt(offerType, cellPricedAt));
    }
};

const handleGridActions = (
    store: PaymentGridMiddlewareStore,
    action: PaymentGridMiddlewareAction,
    updatedState: State,
    offerType: OfferType,
    upqFallbackDisabled: boolean
) => {
    const resolvedOfferType = resolveGridOfferType(action, offerType);
    const terms = action.meta?.originalAction?.meta?.[PAYMENT_REQUEST_OVERRIDE_NODE]?.[resolvedOfferType]?.termList;
    const originalAction = action.meta?.originalAction;

    // A superseded response drives nothing: not a row round, not a verdict. The reducers already discard its
    // data (`isStalePaymentResponse`), but this middleware acted on it regardless. Scoped to the Lease redesign
    // so the Finance fallback pass, which is verified against today's behaviour, is untouched.
    if (isLeaseRedesign(updatedState, resolvedOfferType) && action.meta?.isStalePaymentResponse) return;
    if (!isCurrentGridBuild(updatedState, action, resolvedOfferType)) return;

    // A failed discovery means 'no program', so the chain continues to the fallback. Gated: only the redesign has a pass to carry it forward to.
    const isDiscoverySettled =
        action.type === types.PAYMENT_RECEIVED ||
        (action.type === types.PAYMENT_FAILURE && isAspPgRedesignEnabled(updatedState, resolvedOfferType));

    if (isDiscoverySettled && originalAction.type === types.LOAD_PAYMENT_GRID_LENDER_PROGRAMS) {
        if (isAspPgRedesignEnabled(updatedState, resolvedOfferType)) {
            const unprogrammedTerms = ((terms as number[]) ?? []).filter(
                (termMonths) =>
                    isNumber(termMonths) &&
                    !(
                        action.type === types.PAYMENT_RECEIVED &&
                        hasProgramInDiscoveryResponse(action.payload, resolvedOfferType, termMonths)
                    )
            );
            if (unprogrammedTerms.length) {
                store.dispatch(clearPaymentGridTermPrograms(resolvedOfferType, unprogrammedTerms));
            }
        }

        // Carry the resolved offer type into the rows load so the chain stays on the same offer type
        // across the async PAYMENT_RECEIVED boundary (lease populate while the deal is on finance).
        // DE744178: also recover preInitializedPaymentGrid and sourceApplication from the originating
        // action so the SV finance skip-payment guard in loadRegularPaymentGridData can fire. Both were
        // attached upstream (preInit in initializePaymentGridData; sourceApplication by calculatePayments).
        const preInit = originalAction.meta?.preInitializedPaymentGrid;
        const sourceApplication = originalAction.meta?.sourceApplication;
        const rowsLoadAction = loadPaymentGridData(preInit, resolvedOfferType);
        if (sourceApplication) {
            // resolvedOfferType is always populated here, so loadPaymentGridData guarantees meta exists.
            // Cast through `unknown` because loadPaymentGridData's inferred meta shape does not include
            // sourceApplication; that field is layered on downstream by calculatePayments-aware code.
            (rowsLoadAction as { meta?: Record<string, unknown> }).meta = {
                ...rowsLoadAction.meta,
                sourceApplication
            };
        }
        store.dispatch(rowsLoadAction);
        return;
    }

    if (isDiscoverySettled && originalAction.type === types.LOAD_PAYMENT_GRID_TERM_LENDER_PROGRAM) {
        const [term] = terms;
        const { key } = originalAction.payload;
        // Carry this discovery's verdict forward rather than the merge-only program cache.
        const hasDiscoveredProgram =
            action.type === types.PAYMENT_RECEIVED && hasProgramInDiscoveryResponse(action.payload, resolvedOfferType, term);
        // Carry the resolved offer type so the column reload stays on the same offer type across the
        // async PAYMENT_RECEIVED boundary (lease single-term edit while the deal is on finance).
        store.dispatch(
            loadPaymentGridTermData(key, term, resolvedOfferType, {
                hasDiscoveredProgram,
                ...(originalAction.meta?.gridColumnIndex != null && { gridColumnIndex: originalAction.meta.gridColumnIndex })
            })
        );
        return;
    }

    /**
     * A7: stamped here, below both discovery branches, so only a settled row or cell payment dates a cell.
     *
     * Program discovery is dispatched against a real grid row, so its `PAYMENT_RECEIVED` reaches this
     * function too. Stamping before the branches above dated every returned term off a discovery record
     * that carries no `monthlyPayment` — and if the row payment that followed then failed, the cell was
     * stored with a brand-new `at` and a `po` holding no payment, which `oldestAt` counted and the
     * consumer read as current. Above the flex buy branch, because that is where a flex buy grid lands.
     */
    stampPricedCells(store, action, updatedState, resolvedOfferType);

    if (action.type === types.PAYMENT_RECEIVED && originalAction.type === types.UPDATE_FLEX_BUY_TERMS_GRID) {
        const { flexBuyPaymentGridData } = action.payload[offerType] || {};
        store.dispatch(updateFlexBuyPaymentGridData(flexBuyPaymentGridData));
        return;
    }

    // The redesign replaces the reselect with the column's own fallback. Keyed on the offer type the response is FOR, so an Alt Deal Structure lease grid is not stranded.
    const skipReselectForFallback = isAspPgRedesignEnabled(updatedState, resolvedOfferType);

    if (!skipReselectForFallback && shouldProcessGridReselectAction(action)) {
        const item = getNewSelectedCell(updatedState);
        if (item === null) return;

        if (item) {
            store.dispatch(updateDownPaymentImmediate(item.cashDownVal));
            store.dispatch(selectedTerm(item.id));
        }
        return;
    }

    // A row response is the only point a column is known to be short. A failure and an abort both count as settled - `hasCellQuote` cannot tell a hard failure from no quote, and an abort would otherwise hold the render gate.
    const isSplitPart = Boolean(originalAction?.meta?.isGridRowSplitPart);
    const isGridRowSettled =
        action.type === types.PAYMENT_RECEIVED || action.type === types.PAYMENT_FAILURE || action.type === types.PAYMENT_ABORTED;
    if (isGridRowSettled && isGridRowPayment(originalAction?.type)) {
        if (action.type !== types.PAYMENT_ABORTED && isAspPgRedesignEnabled(updatedState, resolvedOfferType)) {
            recordColumnCells(store, action, resolvedOfferType, originalAction);
        }
        if (action.type === types.PAYMENT_RECEIVED) releaseLeftTermsOncePriced(store, action, resolvedOfferType);

        // Scoped to the edited column, as a split half is: unscoped, the pass fires fallbacks for columns the user never touched.
        const isTargetedColumnEdit = originalAction?.type === types.UPDATED_SELL_RATE_OVERRIDE;

        /**
         * A whole-grid verdict belongs to a BUILD; an EDIT revises only the columns it asked about. Inferred from
         * `originalAction.type`, a term or per-column residual edit took the unscoped branch and - since that dispatches
         * with `replace` - rewrote every column's verdict from stale facts. The build id is the fact being asked about,
         * and is already `isCurrentGridBuild`'s vocabulary.
         *
         * A Lease build is judged WHOLE even when its rows split by engine: the pass returns early while any row is
         * loading. Finance never carries a build id, and the explicit branch keeps that from mattering.
         */
        const isBuildRound = Boolean(originalAction?.meta?.paymentGrid?.buildId);
        /**
         * The ROUND's terms, not the settling call's: a split row is two calls sharing a round id, and the half that
         * settled last judged only its own `termList`, so the first half's columns kept a stale verdict. Finance splits
         * its rows too since US2118720, so it reads this as well; `terms` remains the fallback for a call that carries
         * none.
         */
        const roundTerms: number[] | undefined = originalAction?.meta?.paymentGrid?.roundTerms;
        const termsToEvaluate = isLeaseRedesign(updatedState, resolvedOfferType)
            ? isBuildRound
                ? undefined
                : roundTerms ?? terms
            : isSplitPart || isTargetedColumnEdit
            ? roundTerms ?? terms
            : undefined;

        const callColumns: number[] | undefined =
            originalAction?.meta?.paymentGrid?.roundColumns ?? originalAction?.meta?.gridColumnIndices;
        const rowRoundColumns = ((originalAction?.meta?.gridRowIndices as number[] | undefined) ?? []).flatMap(
            (rowIndex) => (getRowsStatuses(updatedState, resolvedOfferType) as Record<number, any>)?.[rowIndex]?.roundColumns ?? []
        );
        const roundColumns: number[] | undefined = callColumns
            ? [...new Set([...callColumns, ...rowRoundColumns])].sort((left, right) => left - right)
            : undefined;

        handleGridFallbackPass(
            store,
            isAspPgRedesignEnabled(updatedState, resolvedOfferType) ? store.getState() : updatedState,
            resolvedOfferType,
            upqFallbackDisabled,
            termsToEvaluate,
            originalAction?.meta?.paymentGrid?.isUserProgramQuotes,
            action.type !== types.PAYMENT_ABORTED,
            termsToEvaluate ? roundColumns : undefined
        );
        settleActiveColumnAfterRound(store, resolvedOfferType, upqFallbackDisabled);
    }
};

const canHoldGridForVehicleSwitch = (state: State, offerType: OfferType, sourceApplication?: string): boolean =>
    sourceApplication !== ApplicationCodes.SP &&
    getPaymentGridToggle(state, offerType) &&
    !isFlexBuyPayment(state) &&
    isNumber(getSelectedTermMonths(state, offerType));

const rebuildGridAfterVehicleSwitch = (
    store: PaymentGridMiddlewareStore,
    next: PaymentGridMiddlewareNext,
    action: PaymentGridMiddlewareAction,
    sourceApplication?: string
) => {
    const { offerType } = action.payload;
    if (!isPaymentGridAwaitingVehicleSwitch(store.getState(), offerType)) return;

    next(action);

    const updatedState = store.getState();
    if (canHoldGridForVehicleSwitch(updatedState, offerType, sourceApplication)) {
        initializePaymentGridData(store, action, updatedState, offerType);
    }
};

const dropGridOverridesForMileageChange = (
    store: PaymentGridMiddlewareStore,
    state: State,
    action: PaymentGridMiddlewareAction,
    offerType: OfferType
): void => {
    if (!isAspPaymentGridRedesignEnabled(state) || !changesAnnualMiles(action)) return;

    const { keepsColumn, keepsTerm } = getMileageChangeResidualKeeper(state, offerType);
    const dropsRates = isUpqResidualKeepOnMileageChangeEnabled(state);
    const gridData = getOfferDetails(state, offerType)?.paymentGridData as GridPaymentGridData | undefined;

    Object.entries(gridData?.columnOverrides ?? {}).forEach(([columnIndex, columnOverride]) => {
        if (keepsColumn(Number(columnIndex))) return;
        const dropsResidual = columnOverride.residualInfo != null;
        const dropsSellRate = dropsRates && columnOverride.sellRate != null;
        if (dropsResidual || dropsSellRate) {
            store.dispatch(
                setPaymentGridColumnOverride(offerType, Number(columnIndex), {
                    ...(dropsResidual && { residualInfo: undefined }),
                    ...(dropsSellRate && { sellRate: undefined, rateType: undefined })
                })
            );
        }
    });
    Object.entries(gridData?.gridWrittenTerms ?? {}).forEach(([termMonths, halves]) => {
        if (keepsTerm(Number(termMonths))) return;
        if (halves.residual) {
            store.dispatch(setPaymentGridWrittenTerm(offerType, Number(termMonths), 'residual', { clear: true }));
        }
        if (dropsRates && halves.rate) {
            store.dispatch(setPaymentGridWrittenTerm(offerType, Number(termMonths), 'rate', { clear: true }));
        }
    });
    if (!dropsRates) return;
    Object.keys(gridData?.pendingSellRates ?? {}).forEach((columnIndex) => {
        if (!keepsColumn(Number(columnIndex))) {
            store.dispatch(setPaymentGridPendingSellRate(offerType, Number(columnIndex), undefined));
        }
    });
};

const gridMiddleware: PaymentGridMiddleware = (middlewareConfig) => (store) => (next) => (action) => {
    const state = store.getState();
    let offerType = getCurrentOfferType(state);
    const upqFallbackDisabled = isUpqFallbackDisabled(middlewareConfig, state);

    if (shouldHandleCalculationEngineChange(state, action, offerType)) {
        paymentActionsSkipList.push(types.UPDATE_CURRENT_CALCULATION_ENDPOINT);
        action.meta.middleware = false;
    } else {
        paymentActionsSkipList = paymentActionsSkipList.filter((item) => item !== types.UPDATE_CURRENT_CALCULATION_ENDPOINT);
    }

    const { sourceApplication } = middlewareConfig || {};

    if (action.type === types.HOLD_PAYMENT_GRID_FOR_VEHICLE_SWITCH) {
        if (canHoldGridForVehicleSwitch(state, action.payload.offerType, sourceApplication)) next(action);
        return;
    }

    if (action.type === types.REBUILD_PAYMENT_GRID_AFTER_VEHICLE_SWITCH) {
        rebuildGridAfterVehicleSwitch(store, next, action, sourceApplication);
        return;
    }

    // Early return for cash offers
    if (offerType === CASH) return next(action);

    // Early return for payment grid actions
    const isPaymentGridAction = action?.meta?.paymentGrid?.gridAction || false;
    if (isPaymentGridAction) return next(action);

    // Handle offer type updates
    if (action.type === types.UPDATED_OFFER_TYPE) {
        offerType = action.payload;
    }

    // US2062271: a Quick Quote without a vehicle cannot use the Lease/Balloon payment grid — enabling it
    // forces program-quotes, which Payment Services rejects with no VIN/chromeStyleId/ALG/OEM code. Swallow
    // the enabling action so the toggle never flips on, no endpoint switch happens, and no lender-program
    // call is fired. Both reducers for these actions only write payment-grid state, so dropping the action
    // simply leaves the grid off. Normal deals are unaffected (see shouldGatePaymentGridForQuote).
    if (
        isPaymentGridEnablingAction(action) &&
        shouldGatePaymentGridForQuote(state, getPaymentGridEnablingOfferType(action, offerType))
    ) {
        return;
    }

    const holdsGridForVehicleSwitch =
        action.type === types.SWITCH_VEHICLE && canHoldGridForVehicleSwitch(state, offerType, sourceApplication);

    const isForPaymentGrid =
        (isPaymentGridEnabled(state, offerType) &&
            !isPaymentGridAwaitingVehicleSwitch(state, offerType) &&
            !holdsGridForVehicleSwitch) ||
        action.meta?.skipPaymentCall;

    // Add source application to action meta
    action.meta = { ...(action.meta ?? {}), sourceApplication };

    // Before the payment fan-out below, which reads the stamp to decide each row's engine.
    const buildFollowingDeal = gridBuildFollowingDeal(state, action, offerType);
    if (buildFollowingDeal) store.dispatch(setPaymentGridBuild(offerType, buildFollowingDeal));

    // Suppressed rather than swallowed: the edit still reaches the reducers, so the cell keeps what was typed and
    // the next edit sends it. Only the payment is withheld - see shouldHoldGridEditForIncompleteColumn.
    if (shouldHoldGridEditForIncompleteColumn(state, action, offerType)) {
        action.meta = { ...action.meta, middleware: { ...(action.meta.middleware || {}), payment: false } };
        const writesTermSlot = editWritesTermSlot(
            state,
            offerType,
            action.meta?.gridColumnIndex as number | undefined,
            action.payload.months as number
        );

        if (writesTermSlot && action.type === types.UPDATED_SELL_RATE_OVERRIDE && isNumber(action.payload?.sellRate)) {
            store.dispatch(
                updateBuyRateOverride(action.payload.sellRate as number, action.payload.months as number, {
                    isSelectedTerm: action.payload.months === getSelectedTermMonths(state, offerType),
                    withPayment: false,
                    rateType: action.payload.rateType,
                    userIntent: PAYMENT_GRID_SELL_RATE_INTENT
                })
            );
        }

        if (writesTermSlot) {
            store.dispatch(
                setPaymentGridWrittenTerm(offerType, action.payload.months as number, gridWrittenHalfFor(action.type), {
                    clear: action.type === types.REMOVED_PROGRAM_RESIDUAL_OVERRIDE
                })
            );
        }
    }

    dropGridOverridesForMileageChange(store, state, action, offerType);

    // Handle payment actions for payment grid
    if (shouldProcessPaymentAction(state, action, isForPaymentGrid, sourceApplication)) {
        const shouldReturn = handlePaymentAction(store, next, action, offerType, middlewareConfig?.selectors?.invalidatesProgram);
        if (shouldReturn) return; // Regular payment grid already called next(), exit middleware
        // FlexBuy continues below to call next(action)
    }

    holdActiveCellPosition(store, state, action, offerType);

    // Handle selected term action before next()
    if (action.type === types.SELECTED_TERM) {
        releaseDealTermBeforeEditOnSelection(store, state, action, offerType);
        // Must run before next(), which is where the payload is built.
        forceEngineForSelectedCell(store, action, upqFallbackDisabled);
        handleSelectedTermAction(store, action, state, offerType);
    }

    if (
        action.type === types.UPDATE_PAYMENT_GRID_TERM &&
        isNumber(leftTermToHold(state, offerType, getPaymentGridTerms(state), action.payload.key, action.payload.val))
    ) {
        action.meta = { ...action.meta, holdsLeftTerm: true };
    }

    // Use original action
    const dealSafeTerms =
        action.type === types.PAYMENT_RECEIVED ? withoutNonActiveCellRecords(state, offerType, action) : undefined;
    next(dealSafeTerms ? { ...action, payload: { ...action.payload, terms: dealSafeTerms } } : action);

    const updatedState = store.getState();
    offerType = getCurrentOfferType(updatedState);

    // Handle selected term action after next()
    if (action.type === types.SELECTED_TERM) {
        handleSelectedTermActionAfterNext(store, state, updatedState, offerType);
        // Last, so this selection's own payload and its deal-level rate sync are built from the endpoint the deal
        // was on when the user clicked. The adoption governs what happens NEXT, not this call.
        adoptSelectedCellEngine(store, action, updatedState, offerType, upqFallbackDisabled);
    }

    // Handle payment grid toggle actions
    if (isPaymentGridToggleAction(action.type)) {
        handlePaymentGridToggleAction(
            store,
            action,
            updatedState,
            offerType,
            upqFallbackDisabled,
            middlewareConfig?.selectors?.getExplicitLenderLock
        );
        return;
    }

    if (action.type === types.SWITCH_VEHICLE) {
        if (holdsGridForVehicleSwitch) {
            store.dispatch(resetPaymentGrid(offerType, true));
            store.dispatch(holdPaymentGridForVehicleSwitch(offerType));
        }
        return;
    }

    if (shouldHandleCalculationEngineChange(state, action, offerType)) {
        handleCalculationEngineChange(store, action, updatedState, offerType, middlewareConfig?.selectors?.getExplicitLenderLock);
        return;
    }

    if (shouldRealignColumnSourcesForAspOff(state, action, offerType)) {
        realignColumnSourcesForAspOff(store, offerType);
        return;
    }

    // Handle initialize payment grid from template
    if (action.type === types.INITIALIZE_PAYMENT_GRID_FROM_TEMPLATE) {
        handleInitializePaymentGridFromTemplate(store, action, updatedState, middlewareConfig?.selectors?.getExplicitLenderLock);
        return;
    }

    // Handle load payment grid data
    if (action.type === types.LOAD_PAYMENT_GRID_DATA) {
        handleLoadPaymentGridData(store, action, updatedState, state, offerType, upqFallbackDisabled);
        return;
    }

    // Handle update payment grid term
    if (action.type === types.UPDATE_PAYMENT_GRID_TERM) {
        handleUpdatePaymentGridTerm(store, action, updatedState, state, offerType);
        return;
    }

    // Handle load payment grid term data
    if (action.type === types.LOAD_PAYMENT_GRID_TERM_DATA) {
        handleLoadPaymentGridTermData(store, action, updatedState, state, offerType);
        return;
    }

    // Handle update payment grid cash down
    if (action.type === types.UPDATE_PAYMENT_GRID_CASH_DOWN) {
        handleUpdatePaymentGridCashDown(store, action, updatedState, state, offerType);
        return;
    }

    // Handle grid actions
    const isGridAction = action.meta?.originalAction?.meta?.paymentGrid?.gridAction || false;
    if (isGridAction) {
        handleGridActions(store, action, updatedState, offerType, upqFallbackDisabled);
        return;
    }

    restampActiveCellFromDealPayment(store, action, updatedState, offerType);
};

export const paymentGridMiddleware: PaymentGridMiddleware = (middlewareConfig) => (store) => {
    const withStore = gridMiddleware(middlewareConfig)(store);
    return (next) => {
        const handle = withStore(next);
        return (action) => {
            observeMainDealRequest(store, action);
            const result = handle(action);
            settleRowsHeldForMainDeal(store, action);
            return result;
        };
    };
};
